import { Component, OnInit, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { environment } from 'src/environments/environment';
import { SparePartsService } from 'src/app/services/spareParts/spare-parts.service';
import { HttpClient } from '@angular/common/http';
import { OrdersService } from 'src/app/services/orders/orders.service';
import { PackageListsService, PackageListOutModel, PackageListItemOutModel, UniquePackageModel, AvailableSparePartModel, EmpaquetadoParaCargadoModel, AddSparePartToPackageResponse, RemoveSparePartFromPackageResponse, DeleteEmptyPackageResponse, GeneratePackageLabelRequest } from 'src/app/services/packageLists/package-lists.service';
import { PendingPackagesService } from 'src/app/services/pending-packages/pending-packages.service';
import { OrderOutModel } from 'src/app/models/order-out-model/order-out-model';
import { ConfigurationService, PackageStatesConfig } from 'src/app/services/configuration/configuration.service';
import { SparePartModel } from 'src/app/models/spare-part-model/spare-part-model';
import { ToastService } from 'src/app/services/toast/toast.service';
import { LabelsService, SparePartLabelInfo } from 'src/app/services/labels/labels.service';
import { LabelPrintAuthorizationService } from 'src/app/services/labels/label-print-authorization.service';
import { WorkshopsService } from 'src/app/services/workshops/workshops.service';
import { WorkshopModel } from 'src/app/models/workshop-model/workshop-model';
import { isSparePartPackaged, getSparePartsBlockedForPackagingConfirmation, formatPackagingConfirmationBlockMessage, isPackagingConfirmationBlocked, normalizeSparePartForPackagingConfirmation, getSparePartsBlockedByNeedReview, getSparePartsBlockedByIsDamaged, isNeedReviewBlocking, isDamagedBlocking } from 'src/app/utils/spare-part-packaging.util';
import { getPackageBlockedByAnuladoMessage, packageHasAnuladoSparePart } from 'src/app/utils/spare-part-truck-loading.util';

interface PackageItem {
  sparePartId: number;
  sparePartName: string;
  productCode: string;
  packageNumber: string;
  scannedAt: Date;
  orderNumber: string;
  metadata?: {
    remito?: string;
    notes?: string;
  };
}

interface SparePartSearchResult extends EmpaquetadoParaCargadoModel {
  // Extiende EmpaquetadoParaCargadoModel para incluir todos los campos necesarios
}

interface TruckLoadingDraft {
  transportType: 'camion' | 'camioneta';
  scannedItemsForLoading: any[];
  empaquetadosForLoading: any[];
  currentScanningCode: string;
  savedAt: string;
}

@Component({
  selector: 'app-package-management',
  templateUrl: './package-management.component.html',
  styleUrls: ['./package-management.component.css']
})
export class PackageManagementComponent implements OnInit {
  private readonly truckLoadingDraftKey = 'package-management-truck-loading-draft-v1';

  @ViewChild('scanningInput', { static: false }) scanningInput!: ElementRef<HTMLInputElement>;

  currentView: 'menu' | 'empaquetado' | 'enviado' | 'cargado' | 'historial' | 'edit-list' | 'manage-existing' = 'menu';

  // Formularios
  packageNameForm: FormGroup;
  scanForm: FormGroup;
  remitoForm: FormGroup;

  // Estados y datos
  currentPackageList: PackageListOutModel | null = null;
  scannedItems: PackageItem[] = [];
  availableItems: SparePartSearchResult[] = [];

  // Configuración de impresión
  printerType: 'paper' | 'thermal' = 'thermal';
  thermalPrinterWidth: '58mm' | '80mm' | '100mm' = '100mm';

  // Modal de reimpresión
  showReprintModal = false;
  reprintPackageData: UniquePackageModel | null = null;
  reprintPrinterType: 'paper' | 'thermal' = 'thermal';
  reprintThermalPrinterWidth: '58mm' | '80mm' | '100mm' = '100mm';
  checkedItems: PackageItem[] = [];
  isScanning = false;
  lastScannedCode = '';

  // Para cargado al camión
  transportType: 'camion' | 'camioneta' = 'camion';

  // Estados de paquete desde configuración
  packageStates: PackageStatesConfig | null = null;

  // Estado de carga general
  loading = false;

  // Para historial y edición
  historicalLists: PackageListOutModel[] = [];
  currentEditingList: PackageListOutModel | null = null;
  showSparePartHistoryModal = false;
  selectedOrderForEdit: number | null = null;

  // Para gestión de paquetes existentes
  existingPackages: PackageListOutModel[] = [];
  uniquePackages: UniquePackageModel[] = [];
  selectedPackageForManagement: PackageListOutModel | null = null;
  selectedPackageForView: PackageListOutModel | null = null; // Para vista de solo lectura
  showPackageDetailsModal = false; // Nueva propiedad para el modal
  availableSparePartsForOrder: any[] = []; // Repuestos disponibles de la orden para agregar al paquete

  // Para talleres
  workshops: WorkshopModel[] = [];
  enviadoSentFrom = '';
  enviadoSentTo = '';
  searchFilters = {
    type: '',
    status: '',
    name: '',
    startDate: '',
    endDate: ''
  };

  // Paginación para paquetes existentes
  currentPage = 1;
  pageSize = 10;
  totalPages = 0;
  totalCount = 0;

  // Modal para agregar repuesto por código
  showAddByCodeModal = false;
  sparePartCodeToAdd = '';
  foundSparePartToAdd: SparePartSearchResult | null = null;

  // Para gestión de paquetes existentes
  showAddSparePartToPackageModal = false;
  showRemoveSparePartModal = false;
  showDeletePackageModal = false;
  currentPackageForAddSparePart: string | null = null;

  // Para mostrar confirmación de carga completada
  showExportConfirmationModal = false;
  exportedItemsList: any[] = [];

  // Loader para eliminación de paquetes
  deletingEmptyPackage = false;

  // Para escaneo de paquetes pendientes
  pendingPackageToScan = '';
  scanningPendingPackage = false;
  scannedPendingPackageInfo: any = null;
  pendingPackageMessage = '';
  pendingPackageMessageType: 'success' | 'error' = 'success';
  remitoNumberForPendingPackage = '';
  transportTypeForPendingPackage: 'camion' | 'camioneta' = 'camion';
  availableSparePartsForPackage: AvailableSparePartModel[] = [];
  selectedSparePartToAdd: AvailableSparePartModel | null = null;

  // Para modal de confirmación de empaquetado
  showPackagingConfirmationModal = false;
  packageSparePartsToConfirm: any[] = [];
  packageToConfirmInfo: any = null;
  loadingPackageDetails = false;
  readonly availablePackagers: string[] = [
    'Carlos', 'Hiler', 'Maxi', 'Leonardo',
    'Empaquetador 1', 'Empaquetador 2', 'Empaquetador 3'
  ];
  selectedPackager: string | null = null;
  selectedSparePartToRemove: any = null;
  selectedPackageToDelete: string = '';
  currentPackageForManagement: string = '';

  // Para modales de confirmación y alertas elegantes
  showConfirmModal = false;
  showAlertModal = false;
  showInputModal = false;
  modalTitle = '';
  modalMessage = '';
  modalType: 'success' | 'error' | 'warning' | 'info' = 'info';
  private pendingAlertQueue: Array<{ title: string; message: string; alertType: 'success' | 'error' | 'warning' | 'info' }> = [];
  confirmCallback: (() => void) | null = null;
  inputCallback: ((value: string) => void) | null = null;
  inputPlaceholder = '';
  inputValue = '';

  // Para el flujo de creación de lista
  pendingListType: 'empaquetado' | 'enviado' | 'cargado' | null = null;
  pendingRemitoNumber: string | null = null;

  // Para rastrear si el modal es para cambio de transporte
  isTransportChangeModal = false;

  // Para el flujo de creación de lista de tipo cargado
  showTransportSelectionModal = false;
  pendingPackageName = '';

  // Para bloquear el cambio de tipo de transporte una vez creada la lista
  transportTypeLocked = false;

  // Para el flujo de carga al camión
  scannedItemsForLoading: any[] = [];
  empaquetadosForLoading: any[] = [];
  showMainLoadingModal = false;
  /** Pestaña activa del modal de carga en móvil */
  loadingModalMobileTab: 'pending' | 'scanned' = 'pending';
  /** Mensaje de error/aviso al escanear paquete/orden en el modal de carga (visible dentro del modal) */
  loadingModalScanError: string | null = null;

  // Historial de cargamentos
  historialCargamentos: any[] = [];
  loadingHistorial = false;
  selectedPackageForHistory: any = null;
  showPackageHistoryModal = false;
  showVehicleSelectionModal = false;
  showCargadoPreview = false;
  currentScanningCode = '';
  isScanningIndividual = false;
  loadingEmpaquetados = false;
  paquetesEnCamion: any[] = [];
  loadingPaquetesEnCamion = false;

  // Para el buscador en la vista de carga
  searchTermCargado = '';

  // Para debug y seguimiento de actualizaciones
  lastUpdateTime = '';
  updateCounter = 0;

  // Para el modal de asignación de paquetes
  showPackageAssignmentModalFlag = false;
  selectedOrderForAssignment: OrderOutModel | null = null;
  packagesByStatus: { [key: string]: any[] } = {};
  filteredPackagesByStatus: { [key: string]: any[] } = {};
  selectedPackageType: string = '';

  // Para expansión de paquetes
  expandedPackages: Set<string> = new Set();

  private apiUrlSparePart = environment.apiEndpoint + '/SparePart';



  constructor(
    private fb: FormBuilder,
    private sparePartService: SparePartsService,
    private http: HttpClient,
    private packageListsService: PackageListsService,
    private configurationService: ConfigurationService,
    private ordersService: OrdersService,
    private pendingPackagesService: PendingPackagesService,
    private cdr: ChangeDetectorRef,
    private toastService: ToastService,
    private labelsService: LabelsService,
    private labelPrintAuth: LabelPrintAuthorizationService,
    private workshopsService: WorkshopsService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.packageNameForm = this.fb.group({
      packageName: ['', [Validators.required, Validators.minLength(3)]]
    });

    this.scanForm = this.fb.group({
      scannedCode: ['', Validators.required]
    });

    this.remitoForm = this.fb.group({
      remitoNumber: ['', Validators.required],
      notes: ['']
    });
  }

  ngOnInit() {
    // Leer queryParams para establecer la vista correcta
    this.route.queryParams.subscribe(params => {
      const filter = params['filter'];
      if (filter) {
        switch (filter) {
          case 'empaquetado':
            this.startEmpaquetado();
            break;
          case 'cargado':
            this.startCargado();
            break;
          case 'enviado':
            this.startEnviado();
            break;
          case 'historial':
            this.showHistorial();
            break;
          default:
            this.showMenu();
            break;
        }
      } else {
        // Si no hay filter, mostrar el menú
        this.showMenu();
      }
    });

    // Cargar listas guardadas si las hay
    this.loadSavedLists();

    // Cargar talleres
    this.loadWorkshops();

    // Cargar estados de paquete desde el backend
    this.configurationService.getPackageStates().subscribe({
      next: (states) => {
        this.packageStates = states;
      },
      error: (error) => {
        console.error('Error cargando estados de paquete:', error);
      }
    });

    // Cargar historial de cargamentos
    this.loadHistorialCargamentos();
  }

  // Método para inicializar la carga cuando se cambia de vista
  private initializeViewData() {
    switch (this.currentView) {
      case 'empaquetado':
        this.loadUniquePackages('empaquetado');
        break;
      case 'enviado':
        this.loadUniquePackages('enviado');
        break;
      case 'cargado':
        this.loadPaquetesEnCamion();
        break;
      case 'manage-existing':
        this.loadExistingPackages();
        break;
      default:
        break;
    }
  }

  // === NAVEGACIÓN ===
  showMenu() {
    this.currentView = 'menu';
    this.currentPackageList = null;
    this.currentEditingList = null;
    this.pendingListType = null;
    this.clearLists();
  }

  startEmpaquetado() {
    this.currentView = 'empaquetado';
    this.clearLists();
    this.initializeViewData();

    // Forzar recarga para asegurar que los paquetes cargados al camión no aparezcan
    this.loadUniquePackages('empaquetado');

    // Cargar también la lista de paquetes existentes filtrados por tipo 'empaquetado'
    this.loadExistingPackagesFiltered('empaquetado');
  }

  startEnviado() {
    this.currentView = 'enviado';
    this.clearLists();
    this.initializeViewData();
    this.loadUniquePackages('enviado', this.getEnviadoSentFromFormatted(), this.getEnviadoSentToFormatted());

    // Cargar paquetes enviados existentes
    this.loadExistingPackagesFiltered('enviado');
  }



  showHistorial() {
    this.currentView = 'historial';
  }

  showManageExisting() {
    // Redirigir al nuevo componente de gestión de paquetes existentes
    this.router.navigate(['/admin/packages-management']);
  }

  // Cargar listas existentes para un paso específico
  loadExistingListsForStep(stepType: 'empaquetado' | 'enviado' | 'cargado') {
    this.packageListsService.getAllLists({
      type: stepType,
      status: 'active',
      page: 1,
      pageSize: 50
    }).subscribe({
      next: (response) => {
        this.existingPackages = response.lists || [];
        if (this.existingPackages.length === 0) {
          this.showAlert('Información', `No hay listas activas de tipo "${stepType}". Puede escanear números de paquete para crear nuevas listas automáticamente.`, 'info');
        }
      },
      error: (error) => {
        console.error(`Error cargando listas de ${stepType}:`, error);
        this.showAlert('Error', `Error al cargar las listas de ${stepType}`, 'error');
      }
    });
  }

  // === GESTIÓN DE HISTORIAL ===
  loadHistoricalLists() {
    this.packageListsService.getAllLists({
      page: 1,
      pageSize: 50
    }).subscribe({
      next: (response) => {
        this.historicalLists = response.lists || [];
      },
      error: (error) => {
        console.error('Error cargando historial:', error);
        this.showAlert('Error', 'Error al cargar el historial de listas', 'error');
      }
    });
  }

  // === GESTIÓN DE PAQUETES EXISTENTES ===
  loadExistingPackages() {
    const filters: Record<string, string | number> = {
      page: this.currentPage,
      pageSize: this.pageSize
    };

    // Aplicar filtros si están definidos
    if (this.searchFilters.type) filters.type = this.searchFilters.type;
    if (this.searchFilters.status) filters.status = this.searchFilters.status;
    if (this.searchFilters.name) filters.name = this.searchFilters.name;
    if (this.searchFilters.startDate) filters.startDate = this.searchFilters.startDate;
    if (this.searchFilters.endDate) filters.endDate = this.searchFilters.endDate;

    this.packageListsService.getAllLists(filters).subscribe({
      next: (response) => {
        this.existingPackages = response.lists || [];
        this.totalCount = response.totalCount;
        this.totalPages = response.totalPages;
      },
      error: (error) => {
        console.error('Error cargando paquetes existentes:', error);
        this.showAlert('Error', 'Error al cargar los paquetes existentes', 'error');
      }
    });
  }

  // Cargar paquetes existentes filtrados por tipo específico
  loadExistingPackagesFiltered(packageType: string) {
    const filters: Record<string, string | number> = {
      page: this.currentPage,
      pageSize: this.pageSize,
      type: packageType
    };

    this.packageListsService.getAllLists(filters).subscribe({
      next: (response) => {
        this.existingPackages = response.lists || [];
        this.totalCount = response.totalCount;
        this.totalPages = response.totalPages;
      },
      error: (error) => {
        console.error(`Error cargando paquetes ${packageType} existentes:`, error);
        this.showAlert('Error', `Error al cargar los paquetes ${packageType} existentes`, 'error');
      }
    });
  }

  // === GESTIÓN DE PAQUETES ÚNICOS ===
  loadUniquePackages(type?: string, sentFrom?: string, sentTo?: string) {
    this.loading = true;
    this.packageListsService.getUniquePackages(type, sentFrom, sentTo).subscribe({
      next: (packages) => {
        this.uniquePackages = packages;
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        console.error('❌ Error cargando paquetes únicos:', error);
        this.showAlert('Error', 'Error al cargar los paquetes únicos', 'error');
      }
    });
  }

  // Recargar paquetes únicos según la vista actual
  reloadCurrentViewPackages() {
    switch (this.currentView) {
      case 'empaquetado':
        this.loadUniquePackages('empaquetado');
        this.loadExistingPackagesFiltered('empaquetado'); // Recargar paquetes empaquetados existentes
        break;
      case 'cargado':
        this.loadUniquePackages('cargado');
        this.loadExistingPackagesFiltered('cargado'); // Recargar paquetes cargados existentes
        break;
      case 'enviado':
        this.loadUniquePackages('enviado', this.getEnviadoSentFromFormatted(), this.getEnviadoSentToFormatted());
        this.loadExistingPackagesFiltered('enviado'); // Recargar paquetes enviados existentes
        break;
      case 'manage-existing':
        this.loadExistingPackages(); // Recargar todos los paquetes existentes
        break;
      default:
        break;
    }
  }

  applyEnviadoDateTimeFilter(): void {
    this.loadUniquePackages('enviado', this.getEnviadoSentFromFormatted(), this.getEnviadoSentToFormatted());
  }

  clearEnviadoDateTimeFilter(): void {
    this.enviadoSentFrom = '';
    this.enviadoSentTo = '';
    this.loadUniquePackages('enviado');
  }

  private getEnviadoSentFromFormatted(): string | undefined {
    if (!this.enviadoSentFrom) return undefined;
    return this.formatSentFilterDateTime(this.enviadoSentFrom);
  }

  private getEnviadoSentToFormatted(): string | undefined {
    if (!this.enviadoSentTo) return undefined;
    return this.formatSentFilterDateTime(this.enviadoSentTo);
  }

  private formatSentFilterDateTime(value: string): string | undefined {
    const [datePart, timePart] = value.split('T');
    if (!datePart) return undefined;

    const [year, month, day] = datePart.split('-');
    if (!year || !month || !day) return undefined;

    const normalizedTime = timePart ? timePart.slice(0, 5) : '00:00';
    return `${day}/${month}/${year} ${normalizedTime}`;
  }

  // Enfoque agresivo: polling + inserción optimista para asegurar visibilidad del paquete
  private ensurePackageVisible(packageNumber: string, type: 'empaquetado' | 'cargado' | 'enviado', attempts: number = 6) {
    const exists = this.uniquePackages?.some(p => p.packageNumber === packageNumber);
    if (exists) {
      return;
    }

    // Inserción optimista para feedback inmediato
    const optimistic = {
      packageNumber,
      sparePartsCount: 0,
      totalAmount: 0,
      orderNumber: '',
      createdAt: new Date(),
      listType: type,
      packageListId: 0,
      packageListName: '(pendiente de sincronización)',
      transportType: 'camion'
    } as any;

    this.uniquePackages = [optimistic, ...(this.uniquePackages || [])];

    // Polling rápido para reemplazar por datos reales
    let tries = 0;
    const interval = setInterval(() => {
      tries++;
      this.packageListsService.getUniquePackages(type).subscribe({
        next: (packages) => {
          const found = packages.find(p => p.packageNumber === packageNumber);
          if (found) {
            // Reemplazar el optimista por el real
            this.uniquePackages = [found, ...packages.filter(p => p.packageNumber !== packageNumber)];
            clearInterval(interval);
          } else if (tries >= attempts) {
            clearInterval(interval);
          }
        },
        error: () => {
          if (tries >= attempts) {
            clearInterval(interval);
          }
        }
      });
    }, 500);
  }

  // Forzar recarga de paquetes (método adicional para sincronización)
  forceReloadPackages() {
    // Limpiar el array actual para forzar la recarga
    this.uniquePackages = [];
    this.loading = true;

    // Recargar según la vista actual
    this.reloadCurrentViewPackages();

    // También sincronizar todas las vistas para mantener consistencia
    this.syncAllViews();
  }

  // Filtrar paquetes existentes
  applyFilters() {
    this.currentPage = 1; // Reiniciar a la primera página
    this.loadExistingPackages();
  }

  // Limpiar filtros
  clearFilters() {
    this.searchFilters = {
      type: '',
      status: '',
      name: '',
      startDate: '',
      endDate: ''
    };
    this.currentPage = 1;
    this.loadExistingPackages();
  }

  // Paginación
  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadExistingPackages();
    }
  }

  // Seleccionar paquete para gestión detallada
  selectPackageForManagement(packageList: PackageListOutModel) {
    // Cargar los detalles completos del paquete incluyendo sus items
    this.packageListsService.getPackageList(packageList.packageListId).subscribe({
      next: (detailedPackage) => {
        this.selectedPackageForManagement = detailedPackage;

        // Cargar repuestos disponibles de la orden para agregar al paquete
        this.loadAvailableSparePartsForOrder(detailedPackage);
      },
      error: (error) => {
        console.error('Error cargando detalles del paquete:', error);
        this.showAlert('Error', 'Error al cargar los detalles del paquete', 'error');
        // Fallback al paquete original si falla la carga detallada
        this.selectedPackageForManagement = packageList;
      }
    });
  }

  // Cargar repuestos disponibles de la orden para agregar al paquete
  loadAvailableSparePartsForOrder(packageData: PackageListOutModel) {
    if (!packageData || !packageData.items || packageData.items.length === 0) {
      this.availableSparePartsForOrder = [];
      return;
    }

    // Obtener el primer item para determinar la orden
    const firstItem = packageData.items[0];
    if (!firstItem || !firstItem.orderNumber) {
      this.availableSparePartsForOrder = [];
      return;
    }

    // Obtener todos los repuestos de la orden
    this.ordersService.getAllFilteredOrders(firstItem.orderNumber).subscribe({
      next: (orders) => {
        const order = orders.find(o => o.orderNumber === firstItem.orderNumber);
        if (order && order.spareParts && order.spareParts.length > 0) {
          // Obtener el número de exportación del primer repuesto de la orden
          const packageExportNumber = order.spareParts[0].exportNumber;

          // Obtener IDs de repuestos ya en el paquete
          const existingSparePartIds = packageData.items.map(item => item.sparePartId);

          // Filtrar repuestos que:
          // 1. No están en el paquete actual
          // 2. Comparten el mismo número de exportación
          // 3. Pertenecen a la misma orden
          this.availableSparePartsForOrder = order.spareParts.filter(sp => {
            const isNotInPackage = !existingSparePartIds.includes(sp.sparePartId);
            const hasSameExportNumber = !packageExportNumber || (sp.exportNumber === packageExportNumber);

            return isNotInPackage && hasSameExportNumber;
          });
        } else {
          this.availableSparePartsForOrder = [];
        }
      },
      error: (error) => {
        console.error('Error cargando repuestos de la orden:', error);
        this.availableSparePartsForOrder = [];
      }
    });
  }

  // Agregar repuesto disponible de la orden al paquete
  addAvailableSparePartToPackage(sparePart: any): void {
    if (!this.selectedPackageForManagement) {
      this.showAlert('Error', 'No hay paquete seleccionado', 'error');
      return;
    }

    // Usar la función interna existente para agregar el repuesto
    this.addSparePartToPackageInternal(
      this.selectedPackageForManagement.name,
      sparePart.sparePartId
    );

    // Remover el repuesto de la lista de disponibles
    this.availableSparePartsForOrder = this.availableSparePartsForOrder.filter(
      sp => sp.sparePartId !== sparePart.sparePartId
    );
  }

  // Volver a la lista de paquetes existentes
  backToExistingPackages() {
    this.selectedPackageForManagement = null;
    this.availableSparePartsForOrder = []; // Limpiar lista de repuestos disponibles
  }

  // Eliminar paquete existente
  deleteExistingPackage(packageList: PackageListOutModel) {
    this.modalTitle = 'Confirmar Eliminación';
    this.modalMessage = `¿Está seguro que desea eliminar la lista "${packageList.name}"? Esta acción eliminará todos los items asociados y no se puede deshacer.`;
    this.confirmCallback = () => {
      this.packageListsService.deleteList(packageList.packageListId).subscribe({
        next: () => {
          this.showAlert('Éxito', `Lista "${packageList.name}" eliminada exitosamente`, 'success');
          this.loadExistingPackages(); // Recargar la lista
        },
        error: (error) => {
          console.error('Error eliminando lista:', error);
          this.showAlert('Error', 'Error al eliminar la lista', 'error');
        }
      });
    };
    this.showConfirmModal = true;
  }

  // Completar paquete existente
  completeExistingPackage(packageList: PackageListOutModel) {
    if (packageList.status !== 'active') {
      this.showAlert('Información', 'Solo se pueden completar listas con estado "activo"', 'info');
      return;
    }

    this.modalTitle = 'Completar Lista';
    this.modalMessage = `¿Está seguro que desea completar la lista "${packageList.name}" con ${packageList.totalItems} items?`;
    this.confirmCallback = () => {
      this.packageListsService.completeList({
        packageListId: packageList.packageListId
      }).subscribe({
        next: () => {
          this.showAlert('Éxito', `Lista "${packageList.name}" completada exitosamente`, 'success');
          this.loadExistingPackages(); // Recargar la lista
        },
        error: (error) => {
          console.error('Error completando lista:', error);
          this.showAlert('Error', 'Error al completar la lista', 'error');
        }
      });
    };
    this.showConfirmModal = true;
  }

  // Eliminar item específico de un paquete
  deleteItemFromExistingPackage(packageList: PackageListOutModel, item: PackageListItemOutModel) {
    this.modalTitle = 'Confirmar Eliminación';
    this.modalMessage = `¿Está seguro que desea eliminar "${item.sparePartName}" de la lista "${packageList.name}"?`;
    this.confirmCallback = () => {
      this.packageListsService.deleteItemFromList(packageList.packageListId, item.packageListItemId).subscribe({
        next: () => {
          this.showAlert('Éxito', `Item "${item.sparePartName}" eliminado exitosamente`, 'success');

          // Actualizar la lista local
          if (this.selectedPackageForManagement && this.selectedPackageForManagement.packageListId === packageList.packageListId) {
            this.selectedPackageForManagement.items = this.selectedPackageForManagement.items.filter(i => i.packageListItemId !== item.packageListItemId);
            this.selectedPackageForManagement.totalItems--;
          }

          // Recargar la lista general
          this.loadExistingPackages();
        },
        error: (error) => {
          console.error('Error eliminando item:', error);
          this.showAlert('Error', 'Error al eliminar el item', 'error');
        }
      });
    };
    this.showConfirmModal = true;
  }

  // Imprimir etiquetas de todo el paquete
  printPackageLabels(packageList: PackageListOutModel) {
    if (!packageList.items || packageList.items.length === 0) {
      this.showAlert('Información', 'Este paquete no tiene items para imprimir', 'info');
      return;
    }

    // Agrupar items por número de orden para obtener la información completa
    const orderNumbers = [...new Set(packageList.items.map(item => item.orderNumber).filter(on => on && on !== 'N/A'))];

    if (orderNumbers.length === 0) {
      this.showAlert('Error', 'No se encontraron números de orden válidos para los items del paquete', 'error');
      return;
    }

    // Por ahora vamos a usar el primer número de orden encontrado
    // En el futuro se puede mejorar para manejar múltiples órdenes
    const mainOrderNumber = orderNumbers[0];

    // Buscar la orden completa usando el número como término de búsqueda
    this.ordersService.getAllFilteredOrders(mainOrderNumber).subscribe({
      next: (orders) => {
        const order = orders.find(o => o.orderNumber === mainOrderNumber);
        if (order) {
          if (this.isSendYesOrNoNo(this.getOrderSendYesOrNo(order))) {
            this.showAlert(
              'Error',
              `No se puede imprimir etiquetas del paquete: la orden ${mainOrderNumber} tiene ENVIAR = NO.`,
              'error'
            );
            return;
          }

          // Filtrar solo los repuestos que están en este paquete
          const packageSparePartIds = packageList.items.map(item => item.sparePartId);
          const packageSpareParts = order.spareParts.filter(sp => packageSparePartIds.includes(sp.sparePartId));

          if (packageSpareParts.length === 0) {
            this.showAlert('Error', 'No se encontraron repuestos válidos en la orden para este paquete', 'error');
            return;
          }

          // Obtener los números de paquete únicos
          const packageNumbers = [...new Set(packageList.items.map(item => item.packageNumber).filter(pn => pn && pn.trim() !== ''))];

          if (packageNumbers.length === 0) {
            this.showAlert('Error', 'No se encontraron números de paquete válidos', 'error');
            return;
          }

          // Crear etiquetas reales para todos los repuestos del paquete
          const labelsForPrint: SparePartLabelInfo[] = packageSpareParts.map((sp, index) => ({
            sparePartId: sp.sparePartId,
            sparePartName: sp.sparePartName,
            sparePartNameEs: sp.sparePartNameEs,
            productCode: sp.productCode,
            packageNumber: packageNumbers[0],
            orderNumber: order.orderNumber,
            siniestro: order.siniestro || 'N/A',
            clienteUY: order.clienteUY || 'AUTOCOMERCIAL',
            positionInPackage: index + 1,
            totalInPackage: packageSpareParts.length,
            workshopName: order.workshopName,
            workshopAddress: order.workshopAddress,
            workshopPhone: order.workshopPhone,
            workshopCity: order.workshopCity,
            workshopDepartment: order.workshopDepartment,
            clientName: order.clientName,
            clientPhone: order.clientPhone,
            deliveryAddress: order.deliveryAddress,
            providerPhone: this.labelsService.getCompanyWhatsApp(order.clienteUY)
          }));

          this.labelPrintAuth.printAuthorizedSparePartLabels(order, labelsForPrint, {
            useSharedNumber: true,
            includePackageLabel: true
          }).subscribe({
            error: (error) => {
              this.showAlert('Error', error?.message || 'No se pudo completar la impresión.', 'error');
            }
          });
        } else {
          this.showAlert('Error', 'No se encontró la orden asociada a este paquete', 'error');
        }
      },
      error: (error) => {
        console.error('Error cargando orden:', error);
        this.showAlert('Error', 'Error al cargar la información de la orden', 'error');
      }
    });
  }

  // Generar etiquetas para el paquete (basado en order-history logic)
  private generateLabelsForPackage(order: OrderOutModel, selectedParts: { sparePartId: number, sparePartName: string, sparePartNameEs?: string, productCode: string }[], packageNumbers: string[]) {
    const useSharedNumber = packageNumbers.length === 1;
    const labelsHtml = this.generateLabelsHtml(order, selectedParts, packageNumbers, useSharedNumber);

    // Crear una nueva ventana para imprimir
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Etiquetas del Paquete ${packageNumbers.join(', ')}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .label { 
              border: 2px solid #000; 
              padding: 6px; 
              margin: 10px 0; 
              page-break-inside: avoid; 
              width: 264px;
              height: 189px;
              display: flex;
              flex-direction: column;
              background-color: white;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
              box-sizing: border-box;
              overflow: hidden;
            }
            .label-header {
              display: flex;
              align-items: center;
              justify-content: space-between;
              margin-bottom: 2px;
              height: 24px;
            }
            .label-logo { 
              max-height: 20px;
              max-width: 80px;
              object-fit: contain;
            }
            .contact-info {
              font-size: 7px;
              text-align: right;
              line-height: 1.0;
            }
            .order-info {
              font-size: 9px;
              margin-bottom: 3px;
            }
            .client-info {
              font-size: 8px;
              line-height: 1.1;
              margin-bottom: 3px;
              background: #f9f9f9;
              padding: 2px;
              border-radius: 2px;
            }
            .client-info div {
              margin-bottom: 1px;
            }
            .client-header {
              display: flex;
              align-items: center;
              gap: 3px;
              margin-bottom: 2px;
              padding-bottom: 2px;
              border-bottom: 1px solid #ddd;
            }
            .client-logo {
              max-height: 20px;
              max-width: 60px;
              object-fit: contain;
              margin-right: 5px;
            }
            .client-company {
              font-weight: bold;
              font-size: 9px;
              color: #333;
              text-transform: uppercase;
            }
            .part-info { 
              font-size: 9px; 
              font-weight: bold; 
              margin-bottom: 3px; 
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 2px 8px;
            }
            .package-info { 
              font-size: 9px; 
              background: #f0f0f0; 
              padding: 3px; 
              border-radius: 2px; 
            }
            @media print {
              body { margin: 0; }
              .label { margin: 10px 0; }
            }
          </style>
        </head>
        <body>
          ${labelsHtml}
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();

      // Esperar un momento antes de imprimir para que se cargue el contenido
      setTimeout(() => {
        printWindow.print();
      }, 500);
    }
  }

  // Generar HTML de etiquetas (versión simplificada de order-history)
  private generateLabelsHtml(order: OrderOutModel, selectedParts: { sparePartId: number, sparePartName: string, sparePartNameEs?: string, productCode: string }[], packageNumbers: string[], useSharedNumber: boolean): string {
    let html = '';

    selectedParts.forEach((part, index) => {
      const packageNumber = useSharedNumber ? packageNumbers[0] : packageNumbers[index];
      const totalInPackage = selectedParts.length;
      const positionInPackage = index + 1;
      const logoSrc = this.getCompanyLogoPath(order?.clienteUY);

      html += `
        <div class="label">
          <div class="label-header">
            <img class="label-logo" src="${logoSrc}" alt="Logo" onerror="this.style.display='none'">
            ${this.getContactInfoHtml()}
          </div>
          
          <div class="order-info">
            <strong>Orden:</strong> ${order.orderNumber || 'N/A'}<br>
            <strong>Cliente:</strong> ${order.clientName || 'N/A'}
          </div>

          <div class="client-info">
          </div>

          <div class="part-info">
            <div><strong>Código:</strong> ${part.productCode}</div>
          </div>
          
          <div class="package-info">
            <div><strong>Nº Paquete:</strong> ${packageNumber}</div>
            <div><strong>Número de Pieza:</strong> ${positionInPackage}/${totalInPackage}</div>
          </div>
        </div>
      `;
    });

    return html;
  }

  private getCompanyLogoPath(company: string | undefined): string {
    // Usar el servicio de labels para obtener el logo correcto
    return this.labelsService.getLogoForCompany(company);
  }

  private getContactInfoHtml(): string {
    return `
      <div class="contact-info">
        <div><strong>Nombre:</strong> Mateo Carniero</div>
        <div><strong>Teléfono:</strong> +598 95 500 745</div>
      </div>
    `;
  }

  // === MÉTODOS AUXILIARES ===
  loadSavedLists() {
    // Implementación básica
  }

  clearLists() {
    this.scannedItems = [];
    this.availableItems = [];
    this.checkedItems = [];
    this.uniquePackages = [];
    this.loading = false;
  }

  requestPackageName(type: 'empaquetado' | 'enviado' | 'cargado') {
    const typeNames = {
      'empaquetado': 'Empaquetado',
      'enviado': 'Enviado al Taller',
      'cargado': 'Cargado'
    };

    if (type === 'cargado') {
      // Para listas de tipo cargado, primero pedir el nombre
      this.modalTitle = `Crear Lista de ${typeNames[type]}`;
      this.modalMessage = `Ingrese un nombre para la lista de ${typeNames[type].toLowerCase()}`;
      this.inputPlaceholder = `Ej: Lista ${typeNames[type]} ${new Date().toLocaleDateString()}`;
      this.inputValue = '';

      this.inputCallback = (packageName: string) => {
        this.requestTransportType(packageName);
      };
    } else {
      // Para otros tipos, pedir directamente el nombre
      this.modalTitle = `Crear Lista de ${typeNames[type]}`;
      this.modalMessage = `Ingrese un nombre para la lista de ${typeNames[type].toLowerCase()}`;
      this.inputPlaceholder = `Ej: Lista ${typeNames[type]} ${new Date().toLocaleDateString()}`;
      this.inputValue = '';

      this.inputCallback = (packageName: string) => {
        this.createNewPackageList(packageName, type);
      };
    }

    this.showInputModal = true;
  }

  requestTransportType(packageName: string) {
    this.modalTitle = 'Seleccionar Tipo de Transporte';
    this.modalMessage = `Seleccione el tipo de transporte para la lista "${packageName}". Esta decisión no se podrá cambiar después.`;
    this.inputPlaceholder = '';
    this.inputValue = '';

    // Cambiar el modal para mostrar opciones de transporte
    this.showTransportSelectionModal = true;
    this.pendingPackageName = packageName;
  }

  selectTransportType(selectedType: 'camion' | 'camioneta') {
    this.transportType = selectedType;
    this.createNewPackageList(this.pendingPackageName, 'cargado');
    this.closeTransportSelectionModal();
  }

  closeTransportSelectionModal() {
    this.showTransportSelectionModal = false;
    this.pendingPackageName = '';
    this.pendingListType = null;
  }

  createNewPackageList(name: string, type: 'empaquetado' | 'enviado' | 'cargado') {
    const transportType = type === 'cargado' ? this.transportType : undefined;

    // Primero validar que el nombre sea único
    this.packageListsService.isUniqueName(name).subscribe({
      next: (isUnique) => {
        if (!isUnique) {
          this.showAlert('Error', `Ya existe una lista activa con el nombre "${name}". Por favor, elija otro nombre.`, 'error');
          this.pendingListType = null;
          return;
        }

        // Si el nombre es único, crear la lista
        this.packageListsService.createListForComponent(name, type, transportType).subscribe({
          next: (response) => {
            this.currentPackageList = response;
            this.currentEditingList = response; // Establecer también currentEditingList
            this.currentView = type;
            this.clearLists(); // Limpiar todas las listas
            this.pendingListType = null;

            // Si es tipo cargado, establecer el tipo de transporte y deshabilitar cambios
            if (type === 'cargado') {
              this.transportType = transportType as 'camion' | 'camioneta';
              // Marcar que el tipo de transporte no se puede cambiar
              this.transportTypeLocked = true;
            }

            this.showAlert('Éxito', `Lista "${name}" creada exitosamente${type === 'cargado' ? ` para ${transportType === 'camion' ? 'Camión' : 'Camioneta'}` : ''}`, 'success');

            // Si es tipo cargado, cargar repuestos automáticamente
            if (type === 'cargado') {
              this.loadEmpaquetadosParaCargado();
            }
          },
          error: (error) => {
            console.error('Error creando lista:', error);
            if (error.error && typeof error.error === 'string' && error.error.includes('Ya existe una lista activa')) {
              this.showAlert('Error', error.error, 'error');
            } else {
              this.showAlert('Error', 'Error al crear la lista de paquetes', 'error');
            }
            this.pendingListType = null;
          }
        });
      },
      error: (error) => {
        console.error('Error validando nombre único:', error);
        this.showAlert('Error', 'Error al validar el nombre de la lista', 'error');
        this.pendingListType = null;
      }
    });
  }

  loadEmpaquetadosParaCargado() {
    if (!this.currentPackageList) return;

    // Primero obtener todas las listas activas para filtrar repuestos
    this.packageListsService.getAllLists({
      status: 'active',
      page: 1,
      pageSize: 100
    }).subscribe({
      next: (activeListsResponse) => {
        // Obtener todos los sparePartIds que ya están en listas activas
        const sparePartIdsInActiveLists = new Set<number>();
        activeListsResponse.lists.forEach(list => {
          if (list.packageListId !== this.currentPackageList!.packageListId) {
            list.items.forEach(item => {
              sparePartIdsInActiveLists.add(item.sparePartId);
            });
          }
        });

        // Ahora obtener los repuestos empaquetados disponibles
        this.packageListsService.getEmpaquetadosParaCargado().subscribe({
          next: (empaquetados) => {
            if (empaquetados.length === 0) {
              this.showAlert('Información', 'No hay repuestos empaquetados disponibles para cargar automáticamente', 'info');
              return;
            }

            // Filtrar repuestos que NO están en otras listas activas
            const availableSpareParts = empaquetados.filter(sparePart =>
              !sparePartIdsInActiveLists.has(sparePart.sparePartId)
            );

            if (availableSpareParts.length === 0) {
              this.showAlert('Información', 'Todos los repuestos empaquetados ya están en otras listas activas', 'info');
              return;
            }

            // Agregar cada repuesto disponible a la lista provisional "A Cargar"
            availableSpareParts.forEach((sparePart) => {
              this.availableItems.push({
                sparePartId: sparePart.sparePartId,
                sparePartName: sparePart.sparePartNameEs || sparePart.sparePartName,
                productCode: sparePart.productCode,
                packageNumber: sparePart.packageNumber || '',
                productStatus: sparePart.productStatus,
                orderNumber: sparePart.orderNumber || 'N/A'
              });
            });

            this.showAlert('Carga Automática', `Se cargaron ${availableSpareParts.length} repuestos empaquetados disponibles a la lista "A Cargar"`, 'success');
          },
          error: (error) => {
            console.error('Error cargando repuestos empaquetados:', error);
            this.showAlert('Error', 'Error al cargar repuestos empaquetados automáticamente', 'error');
          }
        });
      },
      error: (error) => {
        console.error('Error obteniendo listas activas:', error);
        this.showAlert('Error', 'Error al verificar listas activas', 'error');
      }
    });
  }

  // === MÉTODOS PARA GESTIÓN DE LISTA PROVISIONAL ===

  // Manejar cambio de tipo de transporte
  onTransportTypeChange() {
    // Verificar si el tipo de transporte está bloqueado
    if (this.transportTypeLocked) {
      this.showAlert('Información', 'El tipo de transporte no se puede cambiar una vez creada la lista.', 'info');
      // Revertir el cambio
      this.transportType = this.transportType === 'camion' ? 'camioneta' : 'camion';
      return;
    }

    if (this.transportType === 'camioneta') {
      // Cambio a camioneta: limpiar lista "A Cargar" y mostrar input manual
      this.availableItems = [];
      this.showAlert('Información', 'Cambiado a Camioneta. Ahora puede agregar cualquier tipo de repuesto manualmente.', 'info');
    } else {
      // Cambio a camión: verificar si hay items chequeados
      if (this.checkedItems.length > 0) {
        this.modalTitle = 'Confirmar Cambio';
        this.modalMessage = `Al cambiar a Camión, se eliminarán ${this.checkedItems.length} repuestos de la lista de chequeados y se actualizará su estado a NO-ENVIADO. ¿Desea continuar?`;
        this.isTransportChangeModal = true;
        this.confirmCallback = () => {
          this.removeCheckedItemsFromDatabase();
        };
        this.showConfirmModal = true;
      } else {
        // No hay items chequeados, solo recargar
        this.loadEmpaquetadosParaCargado();
        this.showAlert('Información', 'Cambiado a Camión. Se han cargado repuestos empaquetados disponibles.', 'info');
      }
    }
  }

  // Método para eliminar repuestos chequeados de la base de datos al cambiar a camión
  removeCheckedItemsFromDatabase() {
    if (!this.currentPackageList || this.checkedItems.length === 0) {
      this.loadEmpaquetadosParaCargado();
      this.showAlert('Información', 'Cambiado a Camión. Se han cargado repuestos empaquetados disponibles.', 'info');
      return;
    }

    let completedDeletions = 0;
    const totalItems = this.checkedItems.length;
    // Guardar los IDs de los repuestos chequeados antes de limpiar la lista
    const checkedSparePartIds = this.checkedItems.map(item => item.sparePartId);

    this.checkedItems.forEach((item) => {
      // Buscar el item en la lista actual para obtener su ID de la base de datos
      const listItem = this.currentPackageList!.items.find(i => i.sparePartId === item.sparePartId);

      if (listItem) {
        // Eliminar de la base de datos
        this.packageListsService.deleteItemFromList(this.currentPackageList!.packageListId, listItem.packageListItemId).subscribe({
          next: () => {
            // Actualizar shippingStatus a NO-ENVIADO
            this.sparePartService.updateSparePartShippingStatus(item.sparePartId, 'NO-ENVIADO').subscribe({
              next: (response) => {
                completedDeletions++;

                // Si todos los items han sido procesados
                if (completedDeletions === totalItems) {
                  // Limpiar la lista local de chequeados
                  this.checkedItems = [];
                  // Eliminar también del modelo de la lista actual usando los IDs originales
                  this.currentPackageList!.items = this.currentPackageList!.items.filter(i =>
                    !checkedSparePartIds.includes(i.sparePartId)
                  );
                  // Recargar repuestos disponibles
                  this.loadEmpaquetadosParaCargado();
                  this.showAlert('Éxito', `Se eliminaron ${totalItems} repuestos de la base de datos y se actualizó su estado a NO-ENVIADO. Cambiado a Camión.`, 'success');
                }
              },
              error: (error) => {
                console.error('Error actualizando shipping status:', error);
                completedDeletions++;
                if (completedDeletions === totalItems) {
                  this.checkedItems = [];
                  this.currentPackageList!.items = this.currentPackageList!.items.filter(i =>
                    !checkedSparePartIds.includes(i.sparePartId)
                  );
                  this.loadEmpaquetadosParaCargado();
                  this.showAlert('Advertencia', 'Cambio completado con algunos errores al actualizar estados.', 'warning');
                }
              }
            });
          },
          error: (error) => {
            console.error('Error eliminando repuesto de la base de datos:', error);
            completedDeletions++;
            if (completedDeletions === totalItems) {
              this.checkedItems = [];
              this.currentPackageList!.items = this.currentPackageList!.items.filter(i =>
                !checkedSparePartIds.includes(i.sparePartId)
              );
              this.loadEmpaquetadosParaCargado();
              this.showAlert('Advertencia', 'Cambio completado con algunos errores al eliminar repuestos.', 'warning');
            }
          }
        });
      } else {
        // Si no se encuentra en la base de datos, solo contar como completado
        completedDeletions++;
        if (completedDeletions === totalItems) {
          this.checkedItems = [];
          this.currentPackageList!.items = this.currentPackageList!.items.filter(i =>
            !checkedSparePartIds.includes(i.sparePartId)
          );
          this.loadEmpaquetadosParaCargado();
          this.showAlert('Información', 'Cambiado a Camión. Se han cargado repuestos empaquetados disponibles.', 'info');
        }
      }
    });
  }

  // Método para cancelar el cambio de transporte
  cancelTransportChange() {
    // Revertir el cambio de radio button a camioneta
    this.transportType = 'camioneta';
    this.closeConfirmModal();
  }

  closeConfirmModal() {
    // Si es un modal de cambio de transporte y se cancela, revertir el radio button
    if (this.isTransportChangeModal) {
      this.transportType = 'camioneta';
      this.isTransportChangeModal = false;
    }

    this.showConfirmModal = false;
    this.confirmCallback = null;
  }

  // Agregar repuesto de la lista provisional a la lista principal
  addToCheckedList(item: SparePartSearchResult) {
    // Verificar si ya está en la lista de chequeados
    const existingItem = this.checkedItems.find(checked => checked.sparePartId === item.sparePartId);
    if (existingItem) {
      this.showAlert('Advertencia', 'Este repuesto ya está en la lista de chequeados', 'warning');
      return;
    }

    // Crear el item para la lista de chequeados
    const packageItem: PackageItem = {
      sparePartId: item.sparePartId,
      sparePartName: item.sparePartName,
      productCode: item.productCode,
      packageNumber: item.packageNumber,
      scannedAt: new Date(),
      orderNumber: item.orderNumber
    };

    // Guardar en la base de datos
    this.packageListsService.addItemForComponent(this.currentPackageList!.packageListId, {
      sparePartId: item.sparePartId,
      sparePartName: item.sparePartName,
      sparePartNameEs: item.sparePartNameEs,  // Incluir nombre en español
      sparePartNamePt: item.sparePartNamePt,  // Incluir nombre en portugués
      productCode: item.productCode,
      packageNumber: item.packageNumber,
      orderNumber: item.orderNumber,
      remitoNumber: undefined
    }).subscribe({
      next: (savedItem) => {
        // Actualizar shipping status a ENVIADO
        this.sparePartService.updateSparePartShippingStatus(item.sparePartId, 'ENVIADO').subscribe({
          next: () => {
          },
          error: (error) => {
            console.error('Error actualizando shipping status:', error);
          }
        });

        // Agregar a la lista local de chequeados
        this.checkedItems.push({
          ...packageItem,
          scannedAt: new Date(savedItem.scannedAt)
        });

        // Agregar también al modelo de la lista actual con el ID de la base de datos
        this.currentPackageList!.items.push({
          packageListItemId: savedItem.packageListItemId,
          packageListId: savedItem.packageListId,
          sparePartId: savedItem.sparePartId,
          sparePartName: savedItem.sparePartName,
          productCode: savedItem.productCode,
          packageNumber: savedItem.packageNumber,
          orderNumber: savedItem.orderNumber,
          scannedAt: savedItem.scannedAt,
          itemStatus: savedItem.itemStatus,
          remitoNumber: savedItem.remitoNumber,
          position: savedItem.position
        });

        // Remover de la lista provisional
        this.availableItems = this.availableItems.filter(available => available.sparePartId !== item.sparePartId);

        this.showAlert('Éxito', `Repuesto "${item.sparePartName}" agregado a la lista de chequeados`, 'success');
      },
      error: (error) => {
        if (error.isDuplicate) {
          this.showAlert('Repuesto Duplicado', error.message, 'warning');
        } else {
          console.error('Error guardando repuesto en la base de datos:', error);
          this.showAlert('Error', 'Error al guardar el repuesto en la base de datos', 'error');
        }
      }
    });
  }

  // Remover repuesto de la lista provisional
  removeFromAvailableList(item: SparePartSearchResult) {
    this.availableItems = this.availableItems.filter(available => available.sparePartId !== item.sparePartId);
    this.showAlert('Información', `Repuesto "${item.sparePartName}" removido de la lista provisional`, 'info');
  }

  // Buscar repuesto en la lista provisional por código
  findInAvailableList(code: string): SparePartSearchResult | null {
    return this.availableItems.find(item => item.productCode === code) || null;
  }

  // === MÉTODOS DE MODALES ===
  showAlert(title: string, message: string, alertType: 'success' | 'error' | 'warning' | 'info' = 'info') {
    if (this.showAlertModal) {
      this.pendingAlertQueue.push({ title, message, alertType });
      return;
    }
    this.openAlertModal(title, message, alertType);
  }

  private openAlertModal(title: string, message: string, alertType: 'success' | 'error' | 'warning' | 'info'): void {
    this.modalTitle = title;
    this.modalMessage = message;
    this.modalType = alertType;
    this.showAlertModal = true;
  }

  closeAlertModal() {
    this.showAlertModal = false;
    const nextAlert = this.pendingAlertQueue.shift();
    if (nextAlert) {
      setTimeout(() => {
        this.openAlertModal(nextAlert.title, nextAlert.message, nextAlert.alertType);
      }, 0);
    }
  }

  closeInputModal() {
    this.showInputModal = false;
    this.inputCallback = null;
    this.inputValue = '';
    this.pendingListType = null;
  }

  closeAllModals() {
    this.showInputModal = false;
    this.showConfirmModal = false;
    this.showAlertModal = false;
    this.showMainLoadingModal = false;
    this.showVehicleSelectionModal = false;
    this.inputCallback = null;
    this.inputValue = '';
    this.pendingListType = null;
    this.pendingRemitoNumber = null;
    this.pendingAlertQueue = [];
  }

  confirmAction() {
    if (this.confirmCallback) {
      this.confirmCallback();
    }
    this.isTransportChangeModal = false;
    this.closeConfirmModal();
  }

  submitInput() {
    if (this.inputValue.trim() && this.inputCallback) {
      this.inputCallback(this.inputValue.trim());
      this.closeInputModal();
    }
  }

  // === MÉTODOS DE ESCANEO ===

  /**
   * Normaliza el código escaneado para corregir problemas de configuración de teclado
   * Reemplaza comillas simples (') por guiones (-) para compatibilidad con escáneres
   * configurados en portugués/español que interpretan incorrectamente el guion
   */
  private normalizeScannedCode(code: string): string {
    if (!code) return code;
    // Reemplazar comilla simple por guion (problema común con teclados en portugués/español)
    return code.replace(/'/g, '-');
  }

  /**
   * Normaliza en tiempo real el formulario reactivo de escaneo
   * Se ejecuta cada vez que el usuario escribe en el input
   */
  onScannedCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const normalized = this.normalizeScannedCode(input.value);
    if (normalized !== input.value) {
      this.scanForm.patchValue({ scannedCode: normalized }, { emitEvent: false });
    }
  }

  /**
   * Normaliza en tiempo real el campo pendingPackageToScan
   * Se ejecuta cada vez que cambia el modelo
   */
  onPendingPackageToScanChange(): void {
    if (this.pendingPackageToScan) {
      const normalized = this.normalizeScannedCode(this.pendingPackageToScan);
      if (normalized !== this.pendingPackageToScan) {
        this.pendingPackageToScan = normalized;
      }
    }
  }

  /**
   * Normaliza en tiempo real el campo currentScanningCode
   * Se ejecuta cada vez que cambia el modelo
   */
  onCurrentScanningCodeChange(): void {
    this.loadingModalScanError = null;
    if (this.currentScanningCode) {
      const normalized = this.normalizeScannedCode(this.currentScanningCode);
      if (normalized !== this.currentScanningCode) {
        this.currentScanningCode = normalized;
      }
    }
  }

  onScanSubmit() {
    const scannedCode = this.scanForm.get('scannedCode')?.value;

    if (!scannedCode) {
      return;
    }

    // En las vistas "cargado" y "enviado", todo se procesa como número de paquete
    if (this.currentView === 'cargado') {
      this.validateAndScanPackageForCargado(scannedCode);
    } else if (this.currentView === 'enviado') {
      this.validateAndScanPackageForEnvio(scannedCode);
    } else {
      // Solo para la vista "empaquetado", detectar si es número de paquete o código de repuesto individual
      const isPackage = this.isPackageNumber(scannedCode);

      if (isPackage) {
        // Si no hay listas existentes, crear una nueva automáticamente
        if (this.existingPackages.length === 0) {
          this.scanPackageForNewList(scannedCode);
        } else {
          this.scanPackage(scannedCode);
        }
      } else {
        this.scanSparePart(scannedCode);
      }
    }
    this.scanForm.reset();
  }

  // Validar y escanear paquete para envío al taller
  validateAndScanPackageForEnvio(packageNumber: string) {
    // Normalizar el código escaneado (reemplazar comillas simples por guiones)
    packageNumber = this.normalizeScannedCode(packageNumber);

    this.isScanning = true;

    // Procesar envío sin solicitar remito al ingresar el paquete
    if (this.existingPackages.length === 0) {
      this.scanPackageForNewList(packageNumber);
    } else {
      this.scanPackage(packageNumber);
    }
  }

  // Validar y escanear paquete para carga al camión
  validateAndScanPackageForCargado(packageNumber: string) {
    // Normalizar el código escaneado (reemplazar comillas simples por guiones)
    packageNumber = this.normalizeScannedCode(packageNumber);

    this.isScanning = true;

    // Validar que el paquete existe y pertenece a la lista "Empaquetado"
    this.packageListsService.validatePackageForCargado(packageNumber).subscribe({
      next: (validation) => {
        if (validation.isValid) {
          // SIEMPRE crear una nueva lista única para cada paquete en "Cargado al Camión"
          this.scanPackageForNewList(packageNumber);
        } else {
          this.showAlert('Error de Validación', validation.message, 'error');
        }
        this.isScanning = false;
      },
      error: (error) => {
        console.error('Error validando paquete:', error);
        this.showAlert('Error', 'Error al validar el paquete. Verifique que el paquete existe y pertenece a la lista "Empaquetado".', 'error');
        this.isScanning = false;
      }
    });
  }

  // Escanear paquete para crear nueva lista automáticamente
  scanPackageForNewList(packageNumber: string, remitoNumber?: string) {
    // Normalizar el código escaneado (reemplazar comillas simples por guiones)
    packageNumber = this.normalizeScannedCode(packageNumber);

    if (!this.currentView) {
      console.error('No hay currentView definido');
      return;
    }

    const listType = this.currentView as 'empaquetado' | 'enviado' | 'cargado';

    this.isScanning = true;

    // Crear una nueva lista automáticamente con nombre descriptivo
    let listName: string;
    if (listType === 'cargado') {
      listName = `Paquete ${packageNumber} - Cargado al Camión - ${new Date().toLocaleDateString()}`;
    } else if (listType === 'enviado') {
      const remitoText = remitoNumber?.trim() ? ` - Remito ${remitoNumber.trim()}` : '';
      listName = `Paquete ${packageNumber} - Enviado al Taller${remitoText} - ${new Date().toLocaleDateString()}`;
    } else {
      listName = `Paquete ${packageNumber} - ${new Date().toLocaleDateString()}`;
    }
    const transportType = listType === 'cargado' ? this.transportType : undefined;

    this.packageListsService.createListForComponent(listName, listType, transportType).subscribe({
      next: (response) => {
        this.currentPackageList = response;
        this.currentEditingList = response;

        // Ahora escanear el paquete en la nueva lista
        this.packageListsService.scanPackage(response.packageListId, packageNumber).subscribe({
          next: (scanResponse) => {
            this.isScanning = false;

            if (scanResponse.success) {
              this.showAlert('Éxito', `Lista creada y paquete ${packageNumber} agregado exitosamente. ${scanResponse.itemsAdded} repuestos agregados.`, 'success');

              // Si es una lista de tipo "cargado", actualizar el estado de los repuestos
              if (listType === 'cargado') {
                this.updateSparePartsStatusForCargado(packageNumber);
              }
              // Si es una lista de tipo "enviado", actualizar el remito Y el shippingStatus a ENVIADO
              if (listType === 'enviado' && remitoNumber) {
                this.updateSparePartsRemitoOnly(packageNumber, remitoNumber);
              }

              // Verificar estados finales después de la actualización
              setTimeout(() => {
                this.packageListsService.getSparePartsByPackage(packageNumber).subscribe({
                  next: (finalSpareParts) => {
                  },
                  error: (error) => console.error('❌ Error verificando estados finales:', error)
                });
              }, 2000);

              // Recargar las listas de paquetes únicos con un pequeño delay para asegurar sincronización
              setTimeout(() => {
                this.reloadCurrentViewPackages();
                // Forzar una segunda recarga después de un delay adicional para asegurar que los datos estén sincronizados
                setTimeout(() => {
                  this.forceReloadPackages();
                }, 1000);
              }, 500);
            } else {
              this.showAlert('Error', scanResponse.message || 'Error al agregar el paquete', 'error');
            }
          },
          error: (error) => {
            this.isScanning = false;
            console.error('Error escaneando paquete:', error);
            this.showAlert('Error', 'Error al escanear el paquete en la nueva lista', 'error');
          }
        });
      },
      error: (error) => {
        this.isScanning = false;
        console.error('Error creando lista:', error);
        this.showAlert('Error', 'Error al crear la nueva lista', 'error');
      }
    });
  }

  // Solicitar número de remito para un paquete específico
  requestRemitoNumberForPackage(packageNumber: string) {
    this.modalTitle = 'Número de Remito';
    this.modalMessage = `Ingrese el número de remito para el paquete ${packageNumber}:`;
    this.inputPlaceholder = 'Ej: REM-001';
    this.inputValue = '';

    // Forzar actualización del input
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);

    this.inputCallback = (remitoNumber: string) => {
      if (!remitoNumber || remitoNumber.trim() === '') {
        this.showAlert('Error', 'Debe ingresar un número de remito válido', 'error');
        return;
      }
      // Continuar con el proceso de escaneo usando el remito
      this.scanPackageForNewList(packageNumber, remitoNumber.trim());
    };

    this.showInputModal = true;
  }

  // Solicitar número de remito para actualizar un paquete ya escaneado
  requestRemitoToUpdatePackage(packageNumber: string) {
    this.modalTitle = 'Actualizar Número de Remito';
    this.modalMessage = `Ingrese el número de remito para actualizar el paquete ${packageNumber}:`;
    this.inputPlaceholder = 'Ej: REM-001';
    this.inputValue = '';

    // Forzar actualización del input
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);

    this.inputCallback = (remitoNumber: string) => {
      if (!remitoNumber || remitoNumber.trim() === '') {
        this.showAlert('Error', 'Debe ingresar un número de remito válido', 'error');
        return;
      }
      // Actualizar solo el remito del paquete ya escaneado
      this.updateSparePartsRemitoOnly(packageNumber, remitoNumber.trim());
      this.showAlert('Éxito', `Remito actualizado correctamente para el paquete ${packageNumber}`, 'success');
    };

    this.showInputModal = true;
  }

  // Detectar si el código escaneado es un número de paquete
  private isPackageNumber(code: string): boolean {
    // Detectar patrones comunes de números de paquete
    const packagePatterns = [
      /^PKG-\d+$/,     // PKG-0001
      /^PAQ-\d+$/,     // PAQ-0001  
      /^PACK-\d+$/,    // PACK-0001
      /^P\d{4,}$/,     // P0001
      /^E\d{7}-PAC\d{4}$/, // E2024001-PAC0001
      /^E\d{4}-\d{3}-PAC\d{4}$/ // E2024-001-PAC0001 (formato alternativo)
    ];

    return packagePatterns.some(pattern => pattern.test(code.toUpperCase()));
  }

  // Escanear paquete completo
  scanPackage(packageNumber: string) {
    // Si no hay una lista activa, pero hay listas existentes, usar la primera
    if (!this.currentPackageList && this.existingPackages.length > 0) {
      this.currentPackageList = this.existingPackages[0];
    }

    if (!this.currentPackageList) {
      console.error('No hay lista activa para agregar el paquete');
      this.showAlert('Error', 'No hay una lista activa para agregar el paquete', 'error');
      return;
    }

    this.isScanning = true;

    // Llamar al backend para agregar el paquete completo
    this.packageListsService.scanPackage(this.currentPackageList.packageListId, packageNumber).subscribe({
      next: (response) => {
        this.isScanning = false;

        if (response.success) {
          this.showAlert('Éxito', `Paquete ${packageNumber} agregado exitosamente a "${this.currentPackageList?.name}". ${response.itemsAdded} repuestos agregados.`, 'success');

          // Si es una lista de tipo "cargado", actualizar el estado de los repuestos
          if (this.currentView === 'cargado') {
            this.updateSparePartsStatusForCargado(packageNumber);
          }

          if (this.currentView === 'enviado') {
            this.updateSparePartsStatusForEnvio(packageNumber);
          }

          // Recargar vistas para reflejar el paquete agregado también en paquetes únicos
          if (this.currentView) {
            this.loadExistingListsForStep(this.currentView as 'empaquetado' | 'enviado' | 'cargado');
            this.reloadCurrentViewPackages();
            // Segundo intento de sincronización por seguridad
            setTimeout(() => this.forceReloadPackages(), 750);
            // Enfoque agresivo: forzar visibilidad del paquete
            this.ensurePackageVisible(packageNumber, 'empaquetado');
          }
        } else {
          this.showAlert('Error', response.message || 'Error al agregar el paquete', 'error');
        }
      },
      error: (error) => {
        this.isScanning = false;
        console.error('Error escaneando paquete:', error);

        let errorMessage = 'Error al escanear el paquete';
        let isWarning = false;

        // Manejar casos específicos de error
        if (error.error && error.error.message) {
          errorMessage = error.error.message;

          // Si el paquete ya está en la lista, mostrar como advertencia en lugar de error
          if (errorMessage.includes('ya está en esta lista') || errorMessage.includes('ya estÃ¡ en esta lista')) {
            errorMessage = `El paquete ${packageNumber} ya está en la lista "${this.currentPackageList?.name}".`;
            isWarning = true;
          }
        } else if (error.error && typeof error.error === 'string') {
          errorMessage = error.error;
          if (errorMessage.includes('ya está en esta lista') || errorMessage.includes('ya estÃ¡ en esta lista')) {
            errorMessage = `El paquete ${packageNumber} ya está en la lista "${this.currentPackageList?.name}".`;
            isWarning = true;
          }
        }

        this.showAlert(isWarning ? 'Información' : 'Error', errorMessage, isWarning ? 'warning' : 'error');
      }
    });
  }

  scanSparePart(code: string) {
    // Normalizar el código escaneado (reemplazar comillas simples por guiones)
    code = this.normalizeScannedCode(code);

    // Si estamos en la vista "cargado", buscar primero en la lista provisional
    if (this.currentView === 'cargado') {
      const availableItem = this.findInAvailableList(code);
      if (availableItem) {
        this.addToCheckedList(availableItem);
        return;
      }
    }

    // Buscar el repuesto por código en la base de datos
    this.sparePartService.findSparePartByCode(code).subscribe({
      next: (response) => {
        if (response && response.length > 0) {
          const sparePart = response[0];

          // Si estamos en la vista "cargado", verificar si ya está en otra lista
          if (this.currentView === 'cargado') {
            // Si no hay una lista activa, crear una automáticamente
            if (!this.currentPackageList) {
              const listName = `Lista Cargado - ${new Date().toLocaleDateString()}`;
              this.packageListsService.createListForComponent(listName, 'cargado', this.transportType).subscribe({
                next: (newList) => {
                  this.currentPackageList = newList;
                  this.currentEditingList = newList;
                  // Ahora procesar el repuesto con la nueva lista
                  this.processSparePartForCargado(sparePart);
                },
                error: (error) => {
                  console.error('Error creando lista:', error);
                  this.showAlert('Error', 'Error al crear la lista de carga', 'error');
                }
              });
              return;
            }

            // Si hay una lista activa, procesar normalmente
            this.processSparePartForCargado(sparePart);
          } else {
            // Para otras vistas, usar el método original
            this.addSparePartToList(sparePart);
          }
        } else {
          this.showAlert('Error', 'No se encontró el repuesto con ese código', 'error');
        }
      },
      error: (error) => {
        console.error('Error buscando repuesto:', error);
        this.showAlert('Error', 'Error al buscar el repuesto', 'error');
      }
    });
  }

  // Método auxiliar para procesar repuestos en la vista de cargado
  private processSparePartForCargado(sparePart: any) {
    const packageNumber = (sparePart.packageNumber || sparePart.PackageNumber || '').trim();
    if (!packageNumber) {
      this.showAlert('Error', 'El repuesto no tiene número de paquete asociado', 'error');
      return;
    }

    this.packageListsService.getSparePartsByPackage(packageNumber).subscribe({
      next: (packageSpareParts) => {
        if (packageHasAnuladoSparePart(packageSpareParts)) {
          this.showAlert('Error', getPackageBlockedByAnuladoMessage(packageNumber), 'error');
          return;
        }

        this.continueProcessSparePartForCargado(sparePart);
      },
      error: (error) => {
        console.error('Error verificando repuestos anulados del paquete:', error);
        this.showAlert('Error', 'No se pudo validar el paquete antes de cargarlo', 'error');
      }
    });
  }

  private continueProcessSparePartForCargado(sparePart: any) {
    // Si es modo camioneta, agregar directamente a chequeados
    if (this.transportType === 'camioneta') {
      const newAvailableItem: SparePartSearchResult = {
        sparePartId: sparePart.sparePartId,
        sparePartName: sparePart.sparePartName,
        productCode: sparePart.productCode,
        packageNumber: sparePart.packageNumber || '',
        productStatus: sparePart.productStatus,
        orderNumber: sparePart.orderNumber || 'N/A'
      };

      // Verificar si ya está en la lista de chequeados
      const existingInChecked = this.checkedItems.find(item => item.sparePartId === sparePart.sparePartId);
      if (existingInChecked) {
        this.showAlert('Advertencia', 'Este repuesto ya está en la lista de chequeados', 'warning');
        return;
      }

      // Agregar directamente a la lista de chequeados
      this.addToCheckedList(newAvailableItem);
      return;
    }

    // Verificar si el repuesto ya está en otra lista activa
    this.packageListsService.getAllLists({
      status: 'active',
      page: 1,
      pageSize: 100
    }).subscribe({
      next: (activeListsResponse) => {
        const isInOtherActiveList = activeListsResponse.lists.some(list =>
          list.packageListId !== this.currentPackageList?.packageListId &&
          list.items.some(item => item.sparePartId === sparePart.sparePartId)
        );

        if (isInOtherActiveList) {
          this.showAlert('Error', 'Este repuesto ya está en otra lista activa y no puede ser agregado', 'error');
          return;
        }

        const newAvailableItem: SparePartSearchResult = {
          sparePartId: sparePart.sparePartId,
          sparePartName: sparePart.sparePartName,
          productCode: sparePart.productCode,
          packageNumber: sparePart.packageNumber || '',
          productStatus: sparePart.productStatus,
          orderNumber: sparePart.orderNumber || 'N/A'
        };

        // Verificar si ya está en la lista provisional
        const existingInAvailable = this.availableItems.find(item => item.sparePartId === sparePart.sparePartId);
        if (existingInAvailable) {
          this.showAlert('Advertencia', 'Este repuesto ya está en la lista "A Cargar"', 'warning');
          return;
        }

        this.availableItems.push(newAvailableItem);
        this.showAlert('Éxito', `Repuesto "${sparePart.sparePartName}" agregado a la lista "A Cargar"`, 'success');
      },
      error: (error) => {
        console.error('Error verificando listas activas:', error);
        this.showAlert('Error', 'Error al verificar si el repuesto está en otra lista', 'error');
      }
    });
  }

  addSparePartToList(sparePart: SparePartModel) {
    if (!this.currentPackageList) {
      this.showAlert('Error', 'No hay una lista activa', 'error');
      return;
    }

    // Verificar si el repuesto ya está en la lista local
    const existingItem = this.scannedItems.find(item => item.sparePartId === sparePart.sparePartId);
    if (existingItem) {
      this.showAlert('Advertencia', 'Este repuesto ya está en la lista', 'warning');
      return;
    }

    // Crear el item para la lista
    const packageItem: PackageItem = {
      sparePartId: sparePart.sparePartId,
      sparePartName: sparePart.sparePartName,
      productCode: sparePart.productCode,
      packageNumber: sparePart.packageNumber || '',
      scannedAt: new Date(),
      orderNumber: sparePart.orderNumber || 'N/A'
    };

    // Guardar en la base de datos
    this.packageListsService.addItemForComponent(this.currentPackageList.packageListId, {
      sparePartId: sparePart.sparePartId,
      sparePartName: sparePart.sparePartName,
      sparePartNameEs: sparePart.sparePartNameEs,  // Incluir nombre en español
      sparePartNamePt: sparePart.sparePartNamePt,  // Incluir nombre en portugués
      productCode: sparePart.productCode,
      packageNumber: sparePart.packageNumber || '',
      orderNumber: sparePart.orderNumber || 'N/A',
      remitoNumber: undefined // Se agregará después si es necesario
    }).subscribe({
      next: (savedItem) => {
        // Agregar a la lista local con los datos guardados
        this.scannedItems.push({
          ...packageItem,
          scannedAt: new Date(savedItem.scannedAt)
        });

        // Si es tipo 'enviado', solicitar remito
        if (this.currentView === 'enviado') {
          this.requestRemitoNumber(packageItem);
        }
      },
      error: (error) => {
        // Manejar específicamente el error de duplicado
        if (error.isDuplicate) {
          this.showAlert('Repuesto Duplicado', error.message, 'warning');
        } else {
          console.error('Error guardando repuesto en la base de datos:', error);
          this.showAlert('Error', 'Error al guardar el repuesto en la base de datos', 'error');
        }
      }
    });
  }

  requestRemitoNumber(item: PackageItem) {
    this.modalTitle = 'Número de Remito';
    this.modalMessage = `Ingrese el número de remito para "${item.sparePartName}"`;
    this.inputPlaceholder = 'Ej: REM-2024-001';
    this.inputValue = '';

    this.inputCallback = (remitoNumber: string) => {
      item.metadata = { remito: remitoNumber };

      // Actualizar el remito en la base de datos
      if (this.currentPackageList) {
        // Buscar el item en la lista para obtener su ID
        const listItem = this.currentPackageList.items.find(i => i.sparePartId === item.sparePartId);
        if (listItem) {
          // Aquí podrías implementar la actualización del remito en la base de datos
          // Por ahora solo actualizamos localmente
        }
      }
    };

    this.showInputModal = true;
  }

  // === MÉTODOS DE EDICIÓN ===
  editList(list: PackageListOutModel) {
    this.currentEditingList = list;
    this.clearLists();

    // Cargar items de la lista
    if (list.type === 'cargado') {
      // Para listas de tipo "cargado", usar la vista "cargado"
      this.currentView = 'cargado';
      this.currentPackageList = list;

      // Establecer el transportType correcto si está disponible
      if (list.transportType) {
        this.transportType = list.transportType as 'camion' | 'camioneta';
        // Bloquear el cambio de transporte para listas existentes
        this.transportTypeLocked = true;
      }

      // Cargar items en la lista de chequeados
      this.checkedItems = list.items.map(item => ({
        sparePartId: item.sparePartId,
        sparePartName: item.sparePartName,
        productCode: item.productCode,
        packageNumber: item.packageNumber || '',
        scannedAt: new Date(item.scannedAt),
        orderNumber: item.orderNumber || 'N/A',
        metadata: item.remitoNumber ? { remito: item.remitoNumber } : undefined
      }));

      // Si la lista está activa, cargar repuestos disponibles para "A Cargar"
      if (list.status === 'active') {
        this.loadEmpaquetadosParaCargado();
      }
    } else {
      // Para otros tipos, usar la vista de edición normal
      this.currentView = 'edit-list';

      // Cargar items en scannedItems
      this.scannedItems = list.items.map(item => ({
        sparePartId: item.sparePartId,
        sparePartName: item.sparePartName,
        productCode: item.productCode,
        packageNumber: item.packageNumber || '',
        scannedAt: new Date(item.scannedAt),
        orderNumber: item.orderNumber || 'N/A',
        metadata: item.remitoNumber ? { remito: item.remitoNumber } : undefined
      }));
    }
  }

  deleteList(list: PackageListOutModel) {
    this.modalTitle = 'Confirmar Desarmado de Paquete';
    this.modalMessage = `¿Está seguro que desea desarmar el paquete "${list.name}"? Esta acción liberará todos los repuestos del paquete y permitirá reutilizarlos.`;
    this.confirmCallback = () => {
      // Implementar desarmado del paquete
      this.packageListsService.deleteList(list.packageListId).subscribe({
        next: () => {
          this.showAlert('Éxito', 'Paquete desarmado exitosamente. Los repuestos han sido liberados.', 'success');
          this.loadPackagesByStatus(); // Recargar listas
          this.closeConfirmModal();
        },
        error: (error) => {
          console.error('Error al desarmar paquete:', error);
          this.showAlert('Error', 'Error al desarmar el paquete', 'error');
          this.closeConfirmModal();
        }
      });
    };
    this.showConfirmModal = true;
  }

  // === MÉTODOS DE MODALES ADICIONALES ===
  showAddSparePartByCodeModal() {
    this.showAddByCodeModal = true;
    this.sparePartCodeToAdd = '';
    this.foundSparePartToAdd = null;
  }

  closeAddByCodeModal() {
    this.showAddByCodeModal = false;
    this.sparePartCodeToAdd = '';
    this.foundSparePartToAdd = null;
  }

  addSparePartByCode() {
    // Implementar búsqueda y agregado por código
  }

  closeAddSparePartModal() {
    this.showAddByCodeModal = false;
    this.selectedOrderForEdit = null;
  }

  closeSparePartHistoryModal() {
    this.showSparePartHistoryModal = false;
  }

  // === MÉTODOS DE GESTIÓN DE ITEMS ===
  loadItemsForCurrentList() {
    if (!this.currentPackageList) return;

    this.packageListsService.getPackageList(this.currentPackageList.packageListId).subscribe({
      next: (list) => {
        this.scannedItems = list.items.map(item => ({
          sparePartId: item.sparePartId,
          sparePartName: item.sparePartName,
          productCode: item.productCode,
          packageNumber: item.packageNumber || '',
          scannedAt: new Date(item.scannedAt),
          orderNumber: item.orderNumber || 'N/A',
          metadata: item.remitoNumber ? { remito: item.remitoNumber } : undefined
        }));
      },
      error: (error) => {
        console.error('Error recargando items de la lista:', error);
        this.showAlert('Error', 'Error al recargar los items de la lista', 'error');
      }
    });
  }

  completeList() {
    if (!this.currentPackageList) return;

    let itemCount = 0;
    if (this.currentView === 'cargado') {
      itemCount = this.checkedItems.length;
    } else {
      itemCount = this.scannedItems.length;
    }

    this.modalTitle = 'Completar Lista';
    this.modalMessage = `¿Está seguro que desea completar la lista "${this.currentPackageList.name}" con ${itemCount} items?`;
    this.confirmCallback = () => {
      // Implementar completado de lista
      this.showAlert('Éxito', 'Lista completada exitosamente', 'success');
    };
    this.showConfirmModal = true;
  }

  // Método específico para completar lista de cargado
  completeCargadoList() {
    if (!this.currentPackageList || this.currentView !== 'cargado') return;

    this.modalTitle = 'Completar Carga';
    this.modalMessage = `¿Está seguro que desea completar la carga con ${this.checkedItems.length} items chequeados?`;
    this.confirmCallback = () => {
      // Aquí se implementaría la lógica para completar la lista de cargado
      this.showAlert('Éxito', 'Carga completada exitosamente', 'success');
    };
    this.showConfirmModal = true;
  }

  markAsReturned(item: PackageItem) {
    // Implementar marcado como devuelto
  }

  deleteItemFromList(item: PackageItem) {
    this.modalTitle = 'Confirmar Eliminación';
    this.modalMessage = `¿Está seguro que desea eliminar "${item.sparePartName}" de la lista?`;
    this.confirmCallback = () => {
      // Implementar eliminación de item
    };
    this.showConfirmModal = true;
  }

  isReturnedItem(item: PackageItem): boolean {
    // Implementar lógica para verificar si item está devuelto
    return false;
  }

  returnToAvailableList(item: PackageItem) {
    if (!this.currentPackageList) {
      console.error('currentPackageList es null');
      this.showAlert('Error', 'No hay lista de paquetes activa', 'error');
      return;
    }

    // Para la vista 'cargado', buscar el item en checkedItems
    const checkedItem = this.checkedItems.find(i => i.sparePartId === item.sparePartId);

    if (!checkedItem) {
      console.error('No se encontró el item en checkedItems');
      this.showAlert('Error', 'No se encontró el ítem en la lista de chequeados', 'error');
      return;
    }

    // Buscar el item en la lista actual para obtener su ID de la base de datos
    const listItem = this.currentPackageList.items.find(i => i.sparePartId === item.sparePartId);

    if (!listItem) {
      console.error('No se encontró el item en currentPackageList.items');
      this.showAlert('Error', 'No se encontró el ítem en la base de datos', 'error');
      return;
    }

    // Eliminar de la base de datos
    this.packageListsService.deleteItemFromList(this.currentPackageList.packageListId, listItem.packageListItemId).subscribe({
      next: () => {
        // Actualizar shippingStatus a NO-ENVIADO
        this.sparePartService.updateSparePartShippingStatus(item.sparePartId, 'NO-ENVIADO').subscribe({
          next: () => {
            // Mover el ítem de checkedItems a availableItems
            this.checkedItems = this.checkedItems.filter(checked => checked.sparePartId !== item.sparePartId);
            this.availableItems.push({
              sparePartId: item.sparePartId,
              sparePartName: item.sparePartName,
              productCode: item.productCode,
              packageNumber: item.packageNumber,
              productStatus: 'empaquetado', // o el status que corresponda
              orderNumber: item.orderNumber
            });

            // Eliminar también del modelo de la lista actual
            this.currentPackageList!.items = this.currentPackageList!.items.filter(i => i.sparePartId !== item.sparePartId);
            this.showAlert('Éxito', `Repuesto "${item.sparePartName}" devuelto a "A Cargar" y marcado como NO-ENVIADO`, 'success');
          },
          error: (error) => {
            console.error('Error actualizando shippingStatus:', error);
            this.showAlert('Error', 'No se pudo actualizar el shipping status', 'error');
          }
        });
      },
      error: (error) => {
        console.error('Error eliminando item de la lista:', error);
        this.showAlert('Error', 'No se pudo eliminar el ítem de la lista', 'error');
      }
    });
  }

  /**
   * Ver detalles de un paquete (solo lectura)
   */
  viewPackageDetails(packageData: PackageListOutModel): void {
    this.selectedPackageForView = packageData;
    this.loadItemsForPackageView(packageData.packageListId);
    this.showPackageDetailsModal = true;
  }

  /**
   * Cerrar modal de detalles de paquete
   */
  closePackageDetailsModal(): void {
    this.showPackageDetailsModal = false;
    this.selectedPackageForView = null;
  }

  /**
   * Cargar items para vista de solo lectura
   */
  loadItemsForPackageView(packageListId: number): void {
    this.packageListsService.getPackageList(packageListId).subscribe({
      next: (response) => {
        if (this.selectedPackageForView) {
          this.selectedPackageForView.items = response.items || [];
        }
      },
      error: (error) => {
        console.error('Error cargando detalles del paquete:', error);
        this.showAlert('Error', 'Error al cargar los detalles del paquete', 'error');
      }
    });
  }

  // =============================================================================
  // MÉTODOS PARA GESTIÓN DE PAQUETES EXISTENTES
  // =============================================================================

  /**
   * Abrir modal para agregar repuesto a un paquete
   */
  openAddSparePartToPackageModal(packageNumber: string): void {
    this.currentPackageForManagement = packageNumber;
    this.loadAvailableSparePartsForPackage(packageNumber);
    this.showAddSparePartToPackageModal = true;
  }

  /**
   * Cargar repuestos disponibles para agregar a un paquete
   */
  loadAvailableSparePartsForPackage(packageNumber: string): void {
    this.packageListsService.getAvailableSparePartsForPackage(packageNumber).subscribe({
      next: (spareParts) => {
        this.availableSparePartsForPackage = spareParts;
      },
      error: (error) => {
        console.error('Error cargando repuestos disponibles:', error);
        this.showAlert('Error', 'Error al cargar repuestos disponibles', 'error');
      }
    });
  }


  /**
   * Agregar repuesto a un paquete por código o ID
   */
  addSparePartToPackageByCode(packageNumber: string, inputValue: string): void {
    if (!inputValue || inputValue.trim() === '') {
      this.showAlert('Error', 'Por favor ingrese un ID o código de repuesto válido', 'error');
      return;
    }

    const trimmedValue = inputValue.trim();

    // Si es un número, usar directamente
    if (!isNaN(+trimmedValue)) {
      return;
    }

    // Si es un código alfanumérico, buscar el repuesto por código
    this.findSparePartByCode(trimmedValue, packageNumber);
  }

  /**
   * Buscar repuesto por código y agregarlo al paquete
   */
  private findSparePartByCode(code: string, packageNumber: string): void {
    this.loading = true;
    // Buscar repuesto por código en la base de datos
    this.sparePartService.findSparePartByCode(code).subscribe({
      next: (spareParts) => {
        this.loading = false;

        if (spareParts && spareParts.length > 0) {
          const sparePart = spareParts[0]; // Tomar el primer resultado

          if (sparePart.sparePartId) {
            // Convertir a número si es necesario
            const sparePartId = typeof sparePart.sparePartId === 'string' ? parseInt(sparePart.sparePartId) : sparePart.sparePartId;
            this.addSparePartToPackageInternal(packageNumber, sparePartId);
          } else {
            this.showAlert('Error', 'El repuesto encontrado no tiene un ID válido', 'error');
          }
        } else {
          this.showAlert('Error', `No se encontró un repuesto con el código "${code}"`, 'error');
        }
      },
      error: (error) => {
        this.loading = false;
        console.error('❌ Error al buscar repuesto por código:', error);
        this.showAlert('Error', 'Error al buscar el repuesto. Intente con el ID numérico.', 'error');
      }
    });
  }

  /**
   * Cerrar modal de agregar repuesto
   */
  closeAddSparePartToPackageModal(): void {
    this.showAddSparePartToPackageModal = false;
    this.availableSparePartsForPackage = [];
    this.selectedSparePartToAdd = null;
    this.currentPackageForManagement = '';
    this.currentPackageForAddSparePart = null;
  }

  /**
   * Abrir modal para remover repuesto de un paquete
   */
  openRemoveSparePartModal(sparePart: any): void {
    this.selectedSparePartToRemove = sparePart;
    this.showRemoveSparePartModal = true;
  }

  /**
   * Remover repuesto de un paquete
   */
  removeSparePartFromPackage(): void {
    if (!this.selectedSparePartToRemove) return;

    this.packageListsService.removeSparePartFromPackage(this.selectedSparePartToRemove.sparePartId).subscribe({
      next: (response) => {
        if (response.success) {
          this.showAlert('Éxito', response.message, 'success');

          // NO preguntar por eliminar el paquete - mantener registro para evitar reutilización
          if (response.shouldDeletePackage) {
            // Solo mostrar mensaje informativo de que el paquete fue marcado como vacío
            this.showAlert('Información', 'El paquete quedó vacío y fue marcado como no reutilizable', 'info');
          }

          // NO filtrar localmente el array - esto puede causar inconsistencias
          // En su lugar, recargar completamente desde el servidor

          // Limpiar el caché local primero para forzar una recarga completa
          if (this.selectedPackageForView) {
            this.selectedPackageForView.items = [];
          }

          // Esperar un momento y luego recargar desde el servidor
          setTimeout(() => {
            if (this.selectedPackageForView?.packageListId) {
              this.loadItemsForPackageView(this.selectedPackageForView.packageListId);
            }
          }, 100);

          // Recargar la lista de repuestos disponibles si estamos en el modal de agregar
          if (this.currentPackageForManagement) {
            this.loadAvailableSparePartsForPackage(this.currentPackageForManagement);
          }

          // Recargar la vista principal de paquetes después de eliminar un repuesto
          this.reloadCurrentViewPackages();

          // Si el paquete quedó vacío, cerrar el modal de detalles
          if (response.shouldDeletePackage) {
            this.closePackageDetailsModal();
          }

          // Si se corrigió una inconsistencia, mostrar mensaje informativo
          if (response.message?.includes('Se ha corregido la inconsistencia')) {
            this.showAlert('Información', response.message, 'info');
          }
        } else {
          this.showAlert('Error', response.message, 'error');
        }

        // Ocultar automáticamente los mensajes de alerta después de 3 segundos
        setTimeout(() => {
          this.closeAlertModal();
        }, 3000);
      },
      error: (error) => {
        console.error('Error removiendo repuesto del paquete:', error);

        // Verificar si es una inconsistencia corregida
        if (error.error?.message?.includes('Se ha corregido la inconsistencia')) {
          this.showAlert('Información', error.error.message, 'info');

          // Refrescar la vista para que desaparezca el repuesto inconsistente
          this.reloadCurrentViewPackages();

          // Si estamos en el modal de detalles, actualizarlo también
          if (this.selectedPackageForView?.packageListId) {
            this.loadItemsForPackageView(this.selectedPackageForView.packageListId);
          }
        } else {
          this.showAlert('Error', error.error?.message || 'Error al remover repuesto del paquete', 'error');
        }

        // Ocultar automáticamente los mensajes de alerta después de 3 segundos
        setTimeout(() => {
          this.closeAlertModal();
        }, 3000);

        this.closeRemoveSparePartModal();
      }
    });
  }

  /**
   * Cerrar modal de remover repuesto
   */
  closeRemoveSparePartModal(): void {
    this.showRemoveSparePartModal = false;
    this.selectedSparePartToRemove = null;
  }

  /**
   * Limpiar inconsistencias en asignaciones de paquetes
   */
  cleanInconsistencies(): void {
    if (confirm('¿Está seguro de que desea limpiar las inconsistencias en las asignaciones de paquetes? Esta operación puede tardar unos momentos.')) {
      this.packageListsService.cleanInconsistencies().subscribe({
        next: (response) => {
          if (response.success) {
            this.showAlert('Éxito', response.message, 'success');
            // Recargar la vista actual después de limpiar inconsistencias
            this.reloadCurrentViewPackages();
          } else {
            this.showAlert('Error', response.message, 'error');
          }
        },
        error: (error) => {
          console.error('Error limpiando inconsistencias:', error);
          this.showAlert('Error', 'Error al limpiar inconsistencias', 'error');
        }
      });
    }
  }

  /**
   * Eliminar paquete vacío
   */
  deleteEmptyPackage(): void {
    if (!this.selectedPackageToDelete) return;

    // Mostrar loader
    this.deletingEmptyPackage = true;

    this.packageListsService.deleteEmptyPackage(this.selectedPackageToDelete).subscribe({
      next: (response) => {
        if (response.success) {
          this.showAlert('Éxito', response.message, 'success');
          this.closeDeletePackageModal();
          this.closePackageDetailsModal();

          // Actualizar inmediatamente la lista de paquetes únicos
          if (this.uniquePackages) {
            this.uniquePackages = this.uniquePackages.filter(
              pkg => pkg.packageNumber !== this.selectedPackageToDelete
            );
          }

          // También recargar desde el servidor para asegurar sincronización
          this.reloadCurrentViewPackages();
        } else {
          this.showAlert('Error', response.message, 'error');
        }

        // Ocultar loader
        this.deletingEmptyPackage = false;
      },
      error: (error) => {
        console.error('Error eliminando paquete vacío:', error);
        this.showAlert('Error', 'Error al eliminar paquete vacío', 'error');

        // Ocultar loader
        this.deletingEmptyPackage = false;
      }
    });
  }

  /**
   * Cerrar modal de eliminar paquete
   */
  closeDeletePackageModal(): void {
    this.showDeletePackageModal = false;
    this.selectedPackageToDelete = '';
  }

  // TrackBy function para optimizar el rendimiento del ngFor
  trackByItemId(index: number, item: any): string {
    // Usar solo el sparePartId y el índice para crear un ID único y simple
    return `${item.sparePartId || 'no-id'}-${index}`;
  }

  trackBySparePartId(index: number, item: any): any {
    return item.sparePartId || `item-${index}`;
  }

  /**
   * Carga los talleres desde el backend
   */
  loadWorkshops(): void {
    this.workshopsService.getWorkshops().subscribe({
      next: (workshops) => {
        this.workshops = workshops;
      },
      error: (error) => {
        console.error('🔧 ❌ Error cargando talleres desde BD:', error);
        console.error('🔧 Usando fallback hardcodeado SIN datos completos');
        // Fallback a mapeo hardcodeado si falla la carga (DATOS INCOMPLETOS)
        this.workshops = [
          { workshopId: 1, name: 'Taller Central', city: 'Montevideo', company: 'AUTOCOMERCIAL', address: 'Av. 18 de Julio 1234', phoneNumber: '+598 2901 2345', department: 'Montevideo' } as any,
          { workshopId: 2, name: 'AutoService Norte', city: 'Las Piedras', company: 'AUTOCOMERCIAL', address: 'Ruta 8 Km 12', phoneNumber: '+598 2602 3456', department: 'Canelones' } as any,
          { workshopId: 3, name: 'Mecanica Especializada', city: 'Montevideo', company: 'AUTOCOMERCIAL', address: 'Bulevar Artigas 567', phoneNumber: '+598 2400 4567', department: 'Montevideo' } as any,
          { workshopId: 4, name: 'Taller Express', city: 'Montevideo', company: 'AUTOCOMERCIAL', address: 'Av. Italia 890', phoneNumber: '+598 2487 5678', department: 'Montevideo' } as any,
          { workshopId: 5, name: 'Garage Premium', city: 'Montevideo', company: 'AUTOCOMERCIAL', address: 'Constituyente 1234', phoneNumber: '+598 2203 6789', department: 'Montevideo' } as any
        ];
      }
    });
  }

  /**
   * Obtiene el nombre del taller basado en el workshopId
   */
  getWorkshopName(workshopId: number | null | undefined): string {
    if (!workshopId) {
      return 'Sin asignar';
    }

    const workshop = this.workshops.find(w => w.workshopId === workshopId);
    return workshop ? workshop.name : `Taller ID: ${workshopId}`;
  }

  /**
   * Obtiene la clase CSS para el badge del taller
   */
  getWorkshopBadgeClass(workshopId: number | null | undefined): string {
    if (!workshopId) {
      return 'bg-gray-100 text-gray-600';
    }

    const badgeMap: { [key: number]: string } = {
      1: 'bg-blue-100 text-blue-800',
      2: 'bg-green-100 text-green-800',
      3: 'bg-purple-100 text-purple-800',
      4: 'bg-yellow-100 text-yellow-800',
      5: 'bg-red-100 text-red-800'
    };

    return badgeMap[workshopId] || 'bg-gray-100 text-gray-600';
  }

  /**
   * Generar título del paquete para la vista de cargado al camión
   */
  generateCargadoPackageTitle(packageData: PackageListOutModel): string {
    // Obtener el número de orden del primer item del paquete
    const orderNumber = packageData.items && packageData.items.length > 0
      ? packageData.items[0].orderNumber
      : 'N/A';

    // Obtener el número de paquete del primer item
    const packageNumber = packageData.items && packageData.items.length > 0
      ? packageData.items[0].packageNumber
      : 'N/A';

    // Obtener la fecha de creación del paquete
    const createdAt = packageData.createdAt
      ? new Date(packageData.createdAt).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
      : 'N/A';

    return `Paquete ${orderNumber} - ${packageNumber} - ${createdAt}`;
  }

  generateEnviadoPackageTitle(packageData: PackageListOutModel): string {
    // Obtener el número de orden del primer item del paquete
    const orderNumber = packageData.items && packageData.items.length > 0
      ? packageData.items[0].orderNumber
      : 'N/A';

    // Obtener el número de paquete del primer item
    const packageNumber = packageData.items && packageData.items.length > 0
      ? packageData.items[0].packageNumber
      : 'N/A';

    // Obtener la fecha de creación del paquete
    const createdAt = packageData.createdAt
      ? new Date(packageData.createdAt).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
      : 'N/A';

    return `Paquete ${orderNumber} - ${packageNumber} - ${createdAt}`;
  }

  // Generar título para paquete único de cargado
  generateUniqueCargadoPackageTitle(packageData: UniquePackageModel): string {
    const orderNumber = packageData.orderNumber || 'N/A';
    const packageNumber = packageData.packageNumber || 'N/A';
    const loadDate = new Date(packageData.createdAt);

    return `Paquete ${orderNumber} - ${packageNumber} - ${loadDate.toLocaleDateString()}`;
  }

  // Ver detalles de paquete único
  viewUniquePackageDetails(packageData: UniquePackageModel) {
    // Si no hay packageListId, usar endpoint directo por packageNumber
    if (!packageData.packageListId) {
      this.packageListsService.getSparePartsByPackage(packageData.packageNumber).subscribe({
        next: (spareParts) => {
          // Crear items directamente sin PackageList
          const detailedItems: any[] = [];
          spareParts.forEach((sparePart, index) => {
            detailedItems.push({
              packageListItemId: index + 1,
              packageListId: null,
              sparePartId: sparePart.sparePartId,
              sparePartName: sparePart.sparePartName || 'Sin nombre',
              sparePartNameEs: sparePart.sparePartNameEs || '',
              sparePartNamePt: sparePart.sparePartNamePt || '',
              productCode: sparePart.productCode || 'Sin código',
              amount: sparePart.amount || 1,
              unitPrice: sparePart.unitPrice || 0,
              packageNumber: packageData.packageNumber,
              orderNumber: sparePart.orderNumber || 'Sin orden',
              exportNumber: sparePart.exportNumber || '',
              position: index + 1,
              itemStatus: 'completed',
              scannedAt: new Date().toISOString(),
              remitoNumber: null,
              workshopId: sparePart.workshopId || null
            });
          });

          // Crear un paquete temporal
          const tempPackage: any = {
            packageListId: null,
            name: `Paquete ${packageData.packageNumber}`,
            type: packageData.listType || 'empaquetado',
            status: 'active',
            createdAt: packageData.createdAt || new Date().toISOString(),
            items: detailedItems,
            totalItems: detailedItems.length
          };

          this.selectedPackageForView = tempPackage;
          this.showPackageDetailsModal = true;
        },
        error: (error) => {
          console.error('Error cargando repuestos del paquete:', error);
          this.showAlert('Error', 'Error al cargar los repuestos del paquete', 'error');
        }
      });
      return;
    }

    // Cargar los detalles del paquete desde la lista (cuando hay packageListId)
    this.packageListsService.getPackageList(packageData.packageListId).subscribe({
      next: (packageList) => {
        // Obtener los repuestos detallados del paquete específico
        this.packageListsService.getSparePartsByPackage(packageData.packageNumber).subscribe({
          next: (spareParts) => {
            // Crear items directamente sin mapeo complejo
            const detailedItems: any[] = [];
            spareParts.forEach((sparePart, index) => {
              const newItem = {
                packageListItemId: index + 1,
                packageListId: packageList.packageListId,
                sparePartId: sparePart.sparePartId,
                sparePartName: sparePart.sparePartName || 'Sin nombre',
                sparePartNameEs: sparePart.sparePartNameEs || '',
                sparePartNamePt: sparePart.sparePartNamePt || '',
                productCode: sparePart.productCode || 'Sin código',
                amount: sparePart.amount || 1,
                unitPrice: sparePart.unitPrice || 0,
                packageNumber: packageData.packageNumber,
                orderNumber: sparePart.orderNumber || 'Sin orden',
                exportNumber: sparePart.exportNumber || '',
                position: index + 1,
                itemStatus: 'completed',
                scannedAt: new Date().toISOString(),
                remitoNumber: null,
                workshopId: sparePart.workshopId || null
              };
              detailedItems.push(newItem);
            });

            // Crear un paquete temporal con los repuestos detallados
            const tempPackage: any = {
              ...packageList,
              items: detailedItems,
              totalItems: detailedItems.length
            };

            this.selectedPackageForView = tempPackage;

            // Force change detection by creating a new reference
            setTimeout(() => {
              this.selectedPackageForView = {
                ...tempPackage,
                items: [...tempPackage.items]
              };
              this.showPackageDetailsModal = true;
            }, 0);
          },
          error: (error) => {
            console.error('Error cargando repuestos del paquete:', error);
            this.showAlert('Error', 'Error al cargar los repuestos del paquete', 'error');
          }
        });
      },
      error: (error) => {
        console.error('Error cargando detalles del paquete:', error);
        this.showAlert('Error', 'Error al cargar los detalles del paquete', 'error');
      }
    });
  }



  /**
   * Actualizar el estado de los repuestos cuando se cargan al camión
   */
  private updateSparePartsStatusForCargado(packageNumber: string): void {
    // Obtener los repuestos del paquete y actualizar su estado
    this.packageListsService.getSparePartsByPackage(packageNumber).subscribe({
      next: (spareParts) => {
        // Para cada repuesto, obtener el clienteUY de su orden y actualizar el estado
        spareParts.forEach(sparePart => {
          if (sparePart.orderNumber) {
            this.ordersService.getOrderByOrderNumber(sparePart.orderNumber).subscribe({
              next: (order) => {
                if (order && order.clienteUY) {
                  const newStatus = `EXPORTADO`; // Al cargar al camión debe ser EXPORTADO
                  this.sparePartService.updateSparePartStatus(sparePart.sparePartId, newStatus).subscribe({
                    next: () => {
                      // NO actualizar shippingStatus aquí - solo se actualiza cuando se envía físicamente
                    },
                    error: (error) => {
                      console.error(`Error actualizando estado del repuesto ${sparePart.sparePartId}:`, error);
                    }
                  });
                } else {
                  console.error(`Orden ${sparePart.orderNumber} no tiene clienteUY definido`);
                }
              },
              error: (error) => {
                console.error(`Error obteniendo orden para repuesto ${sparePart.sparePartId}:`, error);
              }
            });
          }
        });
      },
      error: (error) => {
        console.error('Error obteniendo repuestos del paquete:', error);
      }
    });
  }

  /**
   * Actualizar el remito Y el shippingStatus de los repuestos cuando se envían al taller
   * Método para actualizar remito y cambiar shippingStatus a "ENVIADO"
   * VERSIÓN CORREGIDA: Usa forkJoin para esperar a que TODAS las actualizaciones terminen
   */
  private updateSparePartsRemitoOnly(packageNumber: string, remitoNumber: string): void {
    // Obtener los repuestos del paquete
    this.packageListsService.getSparePartsByPackage(packageNumber).subscribe({
      next: async (spareParts) => {
        if (spareParts.length === 0) {
          console.error('❌ ERROR: No se encontraron repuestos para el paquete', packageNumber);
          this.showAlert('Error', 'No se encontraron repuestos para el paquete', 'error');
          return;
        }

        // Contadores para tracking
        let successCount = 0;
        let errorCount = 0;
        const errors: string[] = [];

        // PROCESAR SECUENCIALMENTE cada repuesto usando async/await
        for (const sparePart of spareParts) {
          try {
            // 1. Actualizar el remito
            try {
              const updateResult = await this.sparePartService.updateSparePartShippingReceipt(sparePart.sparePartId, remitoNumber.trim()).toPromise();
            } catch (error) {
              console.error(`❌ ERROR al actualizar remito para repuesto ${sparePart.sparePartId}:`, error);
              throw error;
            }

            // CRÍTICO: Esperar un momento para asegurar que la base de datos se actualizó
            await new Promise(resolve => setTimeout(resolve, 300));

            // 2. Actualizar el shippingStatus a "ENVIADO"
            await this.sparePartService.updateSparePartShippingStatus(sparePart.sparePartId, 'ENVIADO').toPromise();

            // CRÍTICO: Verificar que el remito se guardó correctamente recargándolo múltiples veces
            for (let attempt = 1; attempt <= 3; attempt++) {
              await new Promise(resolve => setTimeout(resolve, 500 * attempt));
              this.packageListsService.getSparePartsByPackage(packageNumber).subscribe({
                next: (verifiedSpareParts) => {
                  const verifiedSparePart = verifiedSpareParts.find(sp => sp.sparePartId === sparePart.sparePartId);
                  if (verifiedSparePart) {
                    const remitoVerificado = verifiedSparePart.shippingReceipt || (verifiedSparePart as any).ShippingReceipt || '';
                    if (remitoVerificado !== remitoNumber.trim()) {
                      console.error(`❌ ERROR CRÍTICO (intento ${attempt}): El remito no coincide! Esperado: "${remitoNumber.trim()}", Obtenido: "${remitoVerificado}"`);
                    } else {
                    }
                  } else {
                  }
                },
                error: (err) => console.error(`Error verificando remito (intento ${attempt}):`, err)
              });
            }

            successCount++;
          } catch (error: any) {
            console.error(`❌ Error actualizando repuesto ${sparePart.sparePartId}:`, error);
            errorCount++;
            const errorMsg = error.error || error.message || 'Error desconocido';
            errors.push(`Repuesto ${sparePart.sparePartId} (${sparePart.productCode || 'sin código'}): ${errorMsg}`);
          }
        }

        // Mostrar resultado final
        this.showFinalResult(successCount, errorCount, spareParts.length, errors);

        // IMPORTANTE: Sincronizar vistas DESPUÉS de que TODAS las actualizaciones terminaron
        if (successCount > 0) {
          setTimeout(() => {
            this.syncAllViews();
            this.closeAllModals();

            // Forzar recarga de la vista actual
            if (this.currentView === 'enviado') {
              this.loadUniquePackages('enviado');
            }
          }, 500);
        }
      },
      error: (error) => {
        console.error('Error obteniendo repuestos del paquete:', error);
        this.showAlert('Error', 'Error al obtener los repuestos del paquete', 'error');
      }
    });
  }

  private showFinalResult(successCount: number, errorCount: number, total: number, errors: string[]): void {
    if (errorCount === 0) {
      this.showAlert(
        'Éxito',
        `Todos los repuestos (${successCount}/${total}) fueron enviados al taller correctamente`,
        'success'
      );
    } else if (successCount === 0) {
      this.showAlert(
        'Error',
        `No se pudo enviar ningún repuesto al taller.\n\nErrores:\n${errors.slice(0, 3).join('\n')}${errors.length > 3 ? `\n... y ${errors.length - 3} más` : ''}`,
        'error'
      );
    } else {
      this.showAlert(
        'Parcialmente Completado',
        `${successCount} de ${total} repuestos enviados correctamente.\n${errorCount} fallaron.\n\nErrores:\n${errors.slice(0, 3).join('\n')}${errors.length > 3 ? `\n... y ${errors.length - 3} más` : ''}`,
        'warning'
      );
    }
  }

  /**
   * Actualizar el estado de los repuestos cuando se envían al taller
   * Actualiza AMBOS estados: ProductStatus y ShippingStatus
   */
  private updateSparePartsStatusForEnvio(packageNumber: string, remitoNumber?: string): void {
    // Obtener los repuestos del paquete y actualizar su estado
    this.packageListsService.getSparePartsByPackage(packageNumber).subscribe({
      next: (spareParts) => {
        if (spareParts.length === 0) {
          console.error('❌ ERROR: No se encontraron repuestos para el paquete', packageNumber);
          return;
        }

        // Para cada repuesto, actualizar SOLO ShippingStatus (NO cambiar ProductStatus)
        spareParts.forEach(sparePart => {
          const newShippingStatus = 'ENVIADO';

          // Actualizar SOLO ShippingStatus (NO cambiar ProductStatus a EXPORTADO)
          this.sparePartService.updateSparePartShippingStatus(sparePart.sparePartId, newShippingStatus).subscribe({
            next: (shippingResponse) => {
              // Después de actualizar ShippingStatus, actualizar el remito si se proporcionó
              if (remitoNumber && remitoNumber.trim() !== '') {
                this.sparePartService.updateSparePartShippingReceipt(sparePart.sparePartId, remitoNumber.trim()).subscribe({
                  next: () => {
                  },
                  error: (error) => {
                    console.error(`❌ Error actualizando remito para repuesto ${sparePart.sparePartId}:`, error);
                  }
                });
              } else {
              }
            },
            error: (error) => {
              console.error(`❌ Error actualizando ShippingStatus para repuesto ${sparePart.sparePartId}:`, error);
            }
          });
        });
      },
      error: (error) => {
        console.error('Error obteniendo repuestos del paquete:', error);
      }
    });
  }

  // === MODAL DE ASIGNACIÓN DE PAQUETES ===

  /**
   * Mostrar modal de asignación de paquetes
   */
  showPackageAssignmentModal(): void {
    this.showPackageAssignmentModalFlag = true;
    this.loadPackagesByStatus();
  }



  /**
   * Seleccionar paquete para asignación de repuestos
   */
  selectOrderForAssignment(packageData: any): void {
    // Si es un paquete, necesitamos obtener la orden asociada
    const orderNumber = packageData.orderNumber;
    if (orderNumber) {
      this.ordersService.getOrderByOrderNumber(orderNumber).subscribe({
        next: (order) => {
          this.selectedOrderForAssignment = order;
          this.showSparePartSelectionModal(order);
        },
        error: (error) => {
          console.error('Error obteniendo orden para paquete:', error);
          this.showAlert('Error', 'Error al obtener la información de la orden', 'error');
        }
      });
    }
  }



  /**
   * Filtrar paquetes por tipo seleccionado
   */
  filterPackagesByType(): void {
    if (!this.selectedPackageType) {
      // Si no hay filtro, mostrar todos
      this.filteredPackagesByStatus = { ...this.packagesByStatus };
    } else {
      // Si hay filtro, mostrar solo el tipo seleccionado
      this.filteredPackagesByStatus = {
        [this.selectedPackageType]: this.packagesByStatus[this.selectedPackageType] || []
      };
    }

  }

  /**
   * Alternar expansión de un paquete
   */
  togglePackageExpansion(packageData: any): void {
    const packageKey = `${packageData.packageNumber}-${packageData.listType}`;

    if (this.expandedPackages.has(packageKey)) {
      // Contraer paquete
      this.expandedPackages.delete(packageKey);
      packageData.expandedItems = null;
    } else {
      // Expandir paquete
      this.expandedPackages.add(packageKey);
      this.loadPackageItems(packageData);
    }
  }

  /**
   * Verificar si un paquete está expandido
   */
  isPackageExpanded(packageData: any): boolean {
    const packageKey = `${packageData.packageNumber}-${packageData.listType}`;
    return this.expandedPackages.has(packageKey);
  }

  /**
   * Cargar los items de un paquete para mostrarlos expandidos
   */
  private loadPackageItems(packageData: any): void {
    // Cargar los detalles del paquete desde la lista
    this.packageListsService.getPackageList(packageData.packageListId).subscribe({
      next: (packageList) => {
        // Filtrar solo los items de este paquete específico
        const filteredItems = packageList.items.filter(item =>
          item.packageNumber === packageData.packageNumber
        );

        // Asignar los items al paquete para mostrarlos expandidos
        packageData.expandedItems = filteredItems;
      },
      error: (error) => {
        console.error('Error cargando items del paquete:', error);
        packageData.expandedItems = [];
        this.showAlert('Error', 'Error al cargar los repuestos del paquete', 'error');
      }
    });
  }

  /**
   * Cerrar modal de asignación de paquetes
   */
  closePackageAssignmentModal(): void {
    this.showPackageAssignmentModalFlag = false;
    this.selectedOrderForAssignment = null;
  }

  /**
   * Mostrar modal de selección de repuestos para gestión de paquetes existentes
   */
  showSparePartSelectionModal(order: OrderOutModel): void {
    // Verificar si ya existen paquetes para esta orden
    const existingPackages = this.getExistingPackagesForOrder(order);

    // Crear un modal personalizado para gestionar repuestos en paquetes existentes
    const modalHtml = `
      <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" id="sparePartSelectionModal" onclick="closeSparePartSelectionModal()">
        <div class="bg-white rounded-lg shadow-lg w-11/12 max-w-7xl max-h-[90vh] overflow-y-auto" onclick="event.stopPropagation()">
          <div class="p-4 border-b flex justify-between items-center">
            <h2 class="text-xl font-bold">Gestionar Repuestos en Paquetes - Orden ${order.orderNumber}</h2>
            <button onclick="closeSparePartSelectionModal()" class="text-gray-500 hover:text-gray-700">
              <i class="fas fa-times"></i>
            </button>
          </div>
          
          <div class="p-4">
            <div class="mb-4 bg-blue-50 p-3 rounded-lg">
              <p class="text-sm text-blue-800">
                <i class="fas fa-info-circle mr-2"></i>
                <strong>Instrucciones:</strong> Aquí puedes gestionar los repuestos de esta orden en paquetes existentes. 
                Solo puedes agregar repuestos que pertenezcan a esta misma orden y que no estén asignados a otros paquetes.
              </p>
            </div>
            
            <!-- Lista de repuestos con opciones de gestión -->
            <div class="mb-6">
              <h3 class="font-semibold mb-3">Repuestos de la Orden</h3>
              <div class="grid grid-cols-1 gap-3">
                ${(order.spareParts || []).map((part, index) => {
      const existingPackage = existingPackages.find(pkg =>
        pkg.spareParts && pkg.spareParts.some(sp => sp.sparePartId === part.sparePartId)
      );
      const isAlreadyAssigned = existingPackage !== undefined;
      const isPackaged = isSparePartPackaged(part);
      const canBeAssigned = !isAlreadyAssigned && !isPackaged;

      return `
                    <div class="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 ${isPackaged ? 'bg-gray-100 border-gray-300' : isAlreadyAssigned ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}" data-spare-part-id="${part.sparePartId}">
                      <div class="flex-1">
                        <div class="font-medium">${part.sparePartName}</div>
                        <div class="text-sm text-gray-500">Código: ${part.productCode} | Cantidad: ${part.amount} | Estado: ${part.productStatus}</div>
                        ${isPackaged ? `<div class="text-xs text-green-700 font-medium">✓ Empaquetado${part.packageNumber ? ` — Paquete ${part.packageNumber}` : ''} (solo informativo)</div>` : ''}
                        ${!isPackaged && isAlreadyAssigned ? `<div class="text-xs text-green-600 font-medium">✓ Ya asignado a ${existingPackage.packageNumber}</div>` : ''}
                      </div>
                      <div class="flex items-center space-x-3">
                        ${canBeAssigned ? `
                          <button onclick="addSparePartToPackage('${part.sparePartId}', '${part.sparePartName}', '${part.productCode}')" 
                                  class="px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 text-sm">
                            <i class="fas fa-plus mr-1"></i>Agregar
                          </button>
                        ` : !isPackaged ? `
                          <button onclick="removeSparePartFromPackage('${part.sparePartId}')" 
                                  class="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 text-sm">
                            <i class="fas fa-minus mr-1"></i>Remover
                          </button>
                        ` : ''}
                      </div>
                    </div>
                  `;
    }).join('')}
              </div>
            </div>
          </div>
          
          <div class="p-4 border-t flex justify-end space-x-2">
            <button onclick="closeSparePartSelectionModal()" class="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    `;

    // Agregar el modal al DOM
    document.body.insertAdjacentHTML('beforeend', modalHtml);

    // Variables globales para el modal
    let currentOrder = order;

    // Agregar funciones JavaScript al window
    (window as any).closeSparePartSelectionModal = () => {
      const modal = document.getElementById('sparePartSelectionModal');
      if (modal) {
        modal.remove();
      }
    };

    (window as any).addSparePartToPackage = (sparePartId: string, sparePartName: string, productCode: string) => {
      // Aquí implementaremos la lógica para agregar el repuesto al paquete
      // TODO: Implementar lógica de agregar repuesto
    };

    (window as any).removeSparePartFromPackage = (sparePartId: string) => {
      // Aquí implementaremos la lógica para remover el repuesto del paquete
      // TODO: Implementar lógica de remover repuesto
    };
  }

  /**
   * Cargar paquetes separados por estado
   */
  loadPackagesByStatus(): void {
    this.packageListsService.getUniquePackages().subscribe({
      next: (packages) => {
        // Agrupar paquetes por estado
        this.packagesByStatus = {
          'empaquetado': packages.filter(pkg => pkg.listType === 'empaquetado'),
          'cargado': packages.filter(pkg => pkg.listType === 'cargado'),
          'enviado': packages.filter(pkg => pkg.listType === 'enviado')
        };

        // Inicializar filtros con todos los paquetes
        this.filteredPackagesByStatus = { ...this.packagesByStatus };
      },
      error: (error) => {
        console.error('Error cargando paquetes por estado:', error);
        this.showAlert('Error', 'Error al cargar los paquetes', 'error');
      }
    });
  }

  /**
   * Obtener paquetes existentes para una orden
   */
  private getExistingPackagesForOrder(order: OrderOutModel): any[] {
    // Obtener todos los paquetes que contienen repuestos de esta orden
    const allPackages: any[] = [];

    Object.values(this.packagesByStatus).forEach(packages => {
      packages.forEach(pkg => {
        // Verificar si el paquete pertenece a esta orden
        if (pkg.orderNumber === order.orderNumber) {
          // Agregar información de repuestos al paquete
          const packageWithSpareParts = {
            ...pkg,
            spareParts: [] // Inicializar array vacío
          };

          // Cargar los repuestos del paquete
          this.packageListsService.getSparePartsByPackage(pkg.packageNumber).subscribe({
            next: (spareParts) => {
              packageWithSpareParts.spareParts = spareParts;
            },
            error: (error) => {
              console.error('Error cargando repuestos del paquete:', error);
              packageWithSpareParts.spareParts = [];
            }
          });

          allPackages.push(packageWithSpareParts);
        }
      });
    });

    return allPackages;
  }

  /**
   * Escanear paquete pendiente
   */
  async scanPendingPackage(): Promise<void> {
    if (!this.pendingPackageToScan?.trim()) {
      this.showPendingPackageMessage('Por favor ingresa un número de paquete', 'error');
      return;
    }

    this.scanningPendingPackage = true;
    this.scannedPendingPackageInfo = null;
    this.clearPendingPackageMessage();

    try {
      // Verificar si el paquete existe y está disponible
      const response = await this.pendingPackagesService.checkPackageNumber(this.pendingPackageToScan.trim()).toPromise();

      if (response.isDeleted) {
        this.showPendingPackageMessage(
          `El paquete ${this.pendingPackageToScan} fue eliminado y no puede ser reutilizado. Razón: ${response.reason}`,
          'error'
        );
        return;
      }

      if (!response.exists) {
        this.showPendingPackageMessage(
          `El paquete ${this.pendingPackageToScan} no existe`,
          'error'
        );
        return;
      }

      if (response.status !== 'CREADO') {
        this.showPendingPackageMessage(
          `El paquete ${this.pendingPackageToScan} ya no está disponible para escanear (Estado: ${response.status})`,
          'error'
        );
        return;
      }

      // Obtener información del paquete pendiente
      const pendingPackagesResponse = await this.pendingPackagesService.searchPendingPackages({
        packageNumber: this.pendingPackageToScan.trim(),
        page: 1,
        pageSize: 1
      }).toPromise();

      if (pendingPackagesResponse.pendingPackages.length > 0) {
        const packageInfo = pendingPackagesResponse.pendingPackages[0];
        this.scannedPendingPackageInfo = {
          packageNumber: packageInfo.packageNumber,
          orderNumber: packageInfo.orderNumber,
          sparePartsCount: packageInfo.sparePartCount
        };
        this.showPendingPackageMessage(
          `Paquete ${this.pendingPackageToScan} encontrado. ${packageInfo.sparePartCount} repuestos disponibles. Mostrando lista para confirmación...`,
          'success'
        );

        // Obtener lista de repuestos del paquete para mostrar en modal de confirmación
        await this.loadPackageSparePartsForConfirmation(packageInfo.packageNumber);
      } else {
        this.showPendingPackageMessage(
          `No se pudo obtener información del paquete ${this.pendingPackageToScan}`,
          'error'
        );
      }
    } catch (error) {
      console.error('Error al escanear paquete pendiente:', error);
      this.showPendingPackageMessage(
        `Error al escanear paquete: ${error.error?.message || error.message || 'Error desconocido'}`,
        'error'
      );
    } finally {
      this.scanningPendingPackage = false;
    }
  }

  /**
   * Cargar lista de repuestos del paquete para mostrar en modal de confirmación
   */
  async loadPackageSparePartsForConfirmation(packageNumber: string): Promise<void> {
    this.loadingPackageDetails = true;

    try {
      // Obtener lista de repuestos del paquete
      const spareParts = await this.packageListsService.getSparePartsByPackage(packageNumber).toPromise();

      // Guardar información del paquete y repuestos para el modal
      this.packageToConfirmInfo = this.scannedPendingPackageInfo;
      this.packageSparePartsToConfirm = (spareParts || []).map(sp => normalizeSparePartForPackagingConfirmation(sp));
      this.alignPackageOrderNumberWithSpareParts();

      const emergentAlertGroups = await this.fetchPackagingEmergentCommentAlerts(packageNumber);
      const emergentCommentLines = this.buildEmergentCommentLines(emergentAlertGroups);

      // Primero el modal de confirmación; luego la alerta encima (z-[60] > z-50)
      this.showPackagingConfirmationModal = true;
      if (emergentCommentLines.length > 0) {
        setTimeout(() => {
          this.showEmergentCommentModal(emergentCommentLines, 'Escaneo de empaquetado');
        }, 0);
      }

    } catch (error: any) {
      console.error('Error al obtener repuestos del paquete:', error);
      let errorMessage = 'Error desconocido';

      if (error?.status === 404) {
        errorMessage = `El paquete ${packageNumber} no fue encontrado en el servidor. Verifique que el número de paquete sea correcto.`;
      } else if (error?.error?.message) {
        errorMessage = error.error.message;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      this.showPendingPackageMessage(
        `Error al obtener lista de repuestos del paquete: ${errorMessage}`,
        'error'
      );
    } finally {
      this.loadingPackageDetails = false;
    }
  }

  /**
   * Confirmar empaquetado del paquete después de revisar la lista
   */
  hasPackagingConfirmationBlock(): boolean {
    return getSparePartsBlockedForPackagingConfirmation(this.packageSparePartsToConfirm).length > 0;
  }

  isSparePartPackagingBlocked(sparePart: { needReview?: boolean | string; isDamaged?: string }): boolean {
    return isPackagingConfirmationBlocked(sparePart);
  }

  isSparePartNeedReviewBlocked(sparePart: { needReview?: boolean | string }): boolean {
    return isNeedReviewBlocking(sparePart);
  }

  isSparePartIsDamagedBlocked(sparePart: { isDamaged?: string }): boolean {
    return isDamagedBlocking(sparePart);
  }

  private getOrderSendYesOrNo(order: any): string | boolean | undefined {
    return order?.sendYesOrNo ?? order?.SendYesOrNo;
  }

  private isSendYesOrNoNo(sendYesOrNo: string | boolean | null | undefined): boolean {
    return sendYesOrNo === false || sendYesOrNo === 'false';
  }

  private collectSparePartOrderNumbers(spareParts: any[]): string[] {
    return [...new Set(
      (spareParts || [])
        .map(sparePart => String(sparePart?.orderNumber ?? sparePart?.OrderNumber ?? '').trim())
        .filter(orderNumber => orderNumber.length > 0 && orderNumber !== 'N/A')
    )];
  }

  private alignPackageOrderNumberWithSpareParts(): void {
    const liveOrderNumbers = this.collectSparePartOrderNumbers(this.packageSparePartsToConfirm);
    if (liveOrderNumbers.length !== 1 || !this.packageToConfirmInfo) {
      return;
    }

    const liveOrderNumber = liveOrderNumbers[0];
    if ((this.packageToConfirmInfo.orderNumber || '').trim() === liveOrderNumber) {
      return;
    }

    this.packageToConfirmInfo = {
      ...this.packageToConfirmInfo,
      orderNumber: liveOrderNumber
    };

    if (this.scannedPendingPackageInfo?.packageNumber === this.packageToConfirmInfo.packageNumber) {
      this.scannedPendingPackageInfo = {
        ...this.scannedPendingPackageInfo,
        orderNumber: liveOrderNumber
      };
    }
  }

  private async validateOrderSendSettingOrBlock(): Promise<boolean> {
    const liveOrderNumbers = this.collectSparePartOrderNumbers(this.packageSparePartsToConfirm);
    if (liveOrderNumbers.length > 1) {
      this.showPackagingConfirmationModal = true;
      this.showPendingPackageMessage(
        `No se puede continuar con el paquete ${this.packageToConfirmInfo?.packageNumber || ''}: los repuestos pertenecen a más de una orden (${liveOrderNumbers.join(', ')}).`,
        'error'
      );
      return false;
    }

    const orderNumber = liveOrderNumbers[0] || this.packageToConfirmInfo?.orderNumber?.trim();
    if (!orderNumber) {
      return true;
    }

    try {
      const order = await this.ordersService.getOrderByOrderNumber(orderNumber).toPromise();
      if (this.isSendYesOrNoNo(this.getOrderSendYesOrNo(order))) {
        this.showPackagingConfirmationModal = true;
        this.showPendingPackageMessage(
          `No se puede continuar con el paquete ${this.packageToConfirmInfo?.packageNumber || ''}: la orden ${orderNumber} tiene ENVIAR = NO.`,
          'error'
        );
        return false;
      }

      return true;
    } catch (error: any) {
      this.showPackagingConfirmationModal = true;
      this.showPendingPackageMessage(
        `No se pudo validar el campo ENVIAR de la orden ${orderNumber}: ${error?.error?.message || error?.message || 'Error desconocido'}`,
        'error'
      );
      return false;
    }
  }

  async confirmPackagePackaging(): Promise<void> {
    if (!this.packageToConfirmInfo) {
      return;
    }

    if (!this.selectedPackager) {
      this.showAlert('Error', 'Debes seleccionar el empaquetador antes de confirmar.', 'error');
      return;
    }

    const sendSettingAllowsPackaging = await this.validateOrderSendSettingOrBlock();
    if (!sendSettingAllowsPackaging) {
      return;
    }

    const blockedForPackaging = getSparePartsBlockedForPackagingConfirmation(this.packageSparePartsToConfirm);
    if (blockedForPackaging.length > 0) {
      this.showPackagingConfirmationModal = true;
      this.showPendingPackageMessage(
        formatPackagingConfirmationBlockMessage(blockedForPackaging),
        'error'
      );
      return;
    }

    try {
      // Revalidar con datos frescos de BD antes de confirmar
      const freshSpareParts = await this.packageListsService
        .getSparePartsByPackage(this.packageToConfirmInfo.packageNumber)
        .toPromise();
      this.packageSparePartsToConfirm = (freshSpareParts || []).map(sp => normalizeSparePartForPackagingConfirmation(sp));
      this.alignPackageOrderNumberWithSpareParts();
      const orderNumber = this.packageToConfirmInfo.orderNumber;

      const freshSendSettingAllowsPackaging = await this.validateOrderSendSettingOrBlock();
      if (!freshSendSettingAllowsPackaging) {
        return;
      }

      const freshBlocked = getSparePartsBlockedForPackagingConfirmation(this.packageSparePartsToConfirm);
      if (freshBlocked.length > 0) {
        this.showPackagingConfirmationModal = true;
        this.showPendingPackageMessage(
          formatPackagingConfirmationBlockMessage(freshBlocked),
          'error'
        );
        return;
      }

      // Cerrar modal de confirmación
      this.showPackagingConfirmationModal = false;

      // Proceder con el empaquetado normal
      await this.addPendingPackageToList();

      if (orderNumber) {
        const needsOrderPrintAlert = await this.shouldShowOrderPrintAlert(orderNumber);
        if (needsOrderPrintAlert) {
          this.showAlert('Alerta', '⚠️ Necesita Impresión de Orden', 'warning');
        }
      }

    } catch (error) {
      console.error('Error al confirmar empaquetado:', error);
      this.showPendingPackageMessage(
        `Error al confirmar empaquetado: ${error.error?.message || error.message || 'Error desconocido'}`,
        'error'
      );
    }
  }

  /**
   * Cancelar empaquetado del paquete
   */
  cancelPackagePackaging(): void {
    this.showPackagingConfirmationModal = false;
    this.packageToConfirmInfo = null;
    this.packageSparePartsToConfirm = [];
    this.selectedPackager = null;
    this.showPendingPackageMessage('Empaquetado cancelado por el usuario', 'error');
  }

  private async shouldShowOrderPrintAlert(orderNumber: string): Promise<boolean> {
    try {
      const order = await this.ordersService.getOrderByOrderNumber(orderNumber).toPromise();
      const workshopId = order?.workshopId;

      if (!workshopId) {
        return false;
      }

      let workshop = this.workshops.find(w => w.workshopId === workshopId);

      if (!workshop) {
        workshop = await this.workshopsService.getWorkshop(workshopId).toPromise();
      }

      return workshop?.alertOrder === true;
    } catch (error) {
      console.error('Error evaluando alerta de impresión de orden:', error);
      return false;
    }
  }

  /**
   * Imprimir etiqueta del paquete general usando HTML/CSS (como las etiquetas de repuestos)
   */
  async printPackageLabelsDuringPackaging(): Promise<void> {
    if (!this.packageToConfirmInfo || this.packageSparePartsToConfirm.length === 0) {
      this.showAlert('Error', 'No hay información del paquete para imprimir etiqueta', 'error');
      return;
    }

    const sendSettingAllowsPackaging = await this.validateOrderSendSettingOrBlock();
    if (!sendSettingAllowsPackaging) {
      return;
    }

    try {
      // Mostrar mensaje de carga
      this.showAlert('Info', 'Generando etiqueta del paquete...', 'info');

      // Obtener la orden completa usando el número de orden
      const orderResponse = await this.ordersService.getAllFilteredOrders(this.packageToConfirmInfo.orderNumber, undefined, undefined).toPromise();

      const order = orderResponse?.find(o => o.orderNumber === this.packageToConfirmInfo.orderNumber);

      if (!order) {
        this.showAlert('Error', 'No se pudo encontrar la orden para generar las etiquetas', 'error');
        return;
      }

      // Usar el workshopId de la orden si está disponible, sino usar el del primer repuesto
      let finalWorkshopId = order?.workshopId;

      // Si la orden no tiene workshopId, usar el del primer repuesto del paquete
      if (!finalWorkshopId && this.packageSparePartsToConfirm && this.packageSparePartsToConfirm.length > 0) {
        finalWorkshopId = this.packageSparePartsToConfirm[0].workshopId;
      }

      // FIX PROBLEMA 6 (CRÍTICO): NO asignar taller por defecto
      // Si no hay taller asignado, debe quedar como null
      // La etiqueta debe indicar "Sin taller asignado"
      // ELIMINADO el fallback que asignaba WorkshopId = 1 automáticamente
      if (finalWorkshopId === null || finalWorkshopId === undefined) {
        finalWorkshopId = null;  // Explícitamente null para claridad
      }

      // Obtener datos del taller - ESTRATEGIA INTELIGENTE
      let workshopData;

      // ESTRATEGIA CORRECTA:
      // 1. SIEMPRE intentar consultar por workshopId usando getWorkshopDataById (consulta this.workshops de la BD)
      // 2. Si getWorkshopDataById devuelve datos válidos, usarlos
      // 3. Si la orden tiene workshopName/Address desde la BD, usarlos como override

      const workshopFromService = this.getWorkshopDataById(finalWorkshopId);

      // Prioridad 1: Datos explícitos de la orden desde la BD (si existen)
      if (order?.workshopName && order?.workshopAddress && order?.workshopCity) {
        workshopData = {
          name: order.workshopName,
          address: order.workshopAddress,
          phone: order.workshopPhone || '',
          city: order.workshopCity,
          department: (order as any).workshopDepartment || ''
        };
      }
      // Prioridad 2: Datos de getWorkshopDataById (this.workshops o hardcoded)
      else if (workshopFromService && workshopFromService.name && workshopFromService.name !== 'Sin taller asignado') {
        workshopData = workshopFromService;
      }
      // Prioridad 3: Fallback final
      else {
        workshopData = {
          name: 'Sin taller asignado',
          address: '',
          phone: '',
          city: '',
          department: ''
        };
      }

      // Preparar orden con información de taller correcta
      const orderToUse = {
        ...order,
        workshopId: finalWorkshopId,
        workshopName: workshopData.name,
        workshopAddress: workshopData.address,
        workshopPhone: workshopData.phone,
        workshopCity: workshopData.city,
        workshopDepartment: workshopData.department
      };

      // Usar el método correspondiente según el tipo de impresora
      if (this.printerType === 'thermal') {
        // Usar ticketera térmica
        this.labelsService.printPackageLabelWindowThermal(orderToUse, this.packageToConfirmInfo, this.packageSparePartsToConfirm.length, this.packageSparePartsToConfirm, this.thermalPrinterWidth);
      } else {
        // Usar impresión en papel normal
        this.labelsService.printPackageLabelWindow(orderToUse, this.packageToConfirmInfo, this.packageSparePartsToConfirm.length, this.packageSparePartsToConfirm);
      }

      this.showAlert('Éxito', `Etiqueta del paquete generada exitosamente con taller: ${workshopData.name}`, 'success');

    } catch (error) {
      console.error('Error al imprimir etiqueta del paquete:', error);
      this.showAlert('Error', 'Error al generar la etiqueta del paquete', 'error');
    }
  }

  /**
   * Método para determinar el taller por defecto según la orden
   */
  private getDefaultWorkshopForOrder(order: any): { name: string; address: string; phone: string; city: string } {
    // Si hay un workshopId, usar los datos correctos del taller
    if (order?.workshopId) {
      return this.getWorkshopDataById(order.workshopId);
    }

    // CRÍTICO: Si NO hay workshopId, NO asignar ningún taller
    // Retornar datos vacíos para "Sin taller asignado"
    return this.getWorkshopDataById(null);
  }

  /**
   * Método para reimprimir la etiqueta del paquete usando la misma lógica que el botón principal
   */
  openReprintModal(packageData: UniquePackageModel): void {
    if (!packageData || !packageData.packageNumber) {
      this.showAlert('Error', 'No se puede reimprimir la etiqueta: datos del paquete inválidos', 'error');
      return;
    }

    // Configurar el modal con los datos del paquete
    this.reprintPackageData = packageData;

    // Usar la configuración actual como valores por defecto
    this.reprintPrinterType = this.printerType;
    this.reprintThermalPrinterWidth = this.thermalPrinterWidth;

    // Mostrar el modal
    this.showReprintModal = true;
  }

  async reprintPackageLabel(packageData: UniquePackageModel): Promise<void> {
    // Este método ahora solo llama al modal
    this.openReprintModal(packageData);
  }

  async confirmReprintPackageLabel(): Promise<void> {
    if (!this.reprintPackageData) {
      this.showAlert('Error', 'No hay datos del paquete para reimprimir', 'error');
      return;
    }

    try {
      // Mostrar mensaje de carga
      this.showAlert('Info', 'Reimprimiendo etiqueta del paquete...', 'info');

      // Guardar la configuración actual
      const originalPrinterType = this.printerType;
      const originalThermalWidth = this.thermalPrinterWidth;

      // Aplicar la configuración del modal
      this.printerType = this.reprintPrinterType;
      this.thermalPrinterWidth = this.reprintThermalPrinterWidth;

      // Obtener los repuestos reales del paquete desde el backend
      const sparePartsResponse = await this.packageListsService.getSparePartsByPackage(this.reprintPackageData.packageNumber).toPromise();

      // Crear un objeto temporal que simule packageToConfirmInfo
      const tempPackageInfo = {
        packageNumber: this.reprintPackageData.packageNumber,
        orderNumber: this.reprintPackageData.orderNumber
      };

      // Guardar los valores actuales
      const originalPackageInfo = this.packageToConfirmInfo;
      const originalSparePartsCount = this.packageSparePartsToConfirm?.length || 0;

      // Configurar temporalmente los valores necesarios para usar el método principal
      this.packageToConfirmInfo = tempPackageInfo;

      // Usar los repuestos reales obtenidos del backend
      this.packageSparePartsToConfirm = sparePartsResponse || [];

      // Llamar al método principal con los datos preparados
      await this.printPackageLabelsDuringPackaging();

      // Restaurar los valores originales
      this.packageToConfirmInfo = originalPackageInfo;
      this.packageSparePartsToConfirm = Array(originalSparePartsCount).fill(null);
      this.printerType = originalPrinterType;
      this.thermalPrinterWidth = originalThermalWidth;

      // Cerrar el modal
      this.closeReprintModal();

    } catch (error) {
      console.error('Error al reimprimir etiqueta del paquete:', error);
      this.showAlert('Error', 'Error al reimprimir la etiqueta del paquete', 'error');
    }
  }

  closeReprintModal(): void {
    this.showReprintModal = false;
    this.reprintPackageData = null;
  }


  /**
   * Agregar paquete pendiente a la lista de empaquetado
   */
  async addPendingPackageToList(): Promise<void> {
    // Usar la información del paquete a confirmar, o si no existe, usar scannedPendingPackageInfo
    const packageInfo = this.packageToConfirmInfo || this.scannedPendingPackageInfo;

    if (!packageInfo) {
      return;
    }

    if (!this.selectedPackager) {
      this.showPendingPackageMessage('Selecciona el empaquetador para continuar.', 'error');
      return;
    }

    try {
      const request = {
        packageNumber: packageInfo.packageNumber,
        listType: 'empaquetado',
        scannedBy: 'system',
        selectedPackager: this.selectedPackager
      };

      const response = await this.pendingPackagesService.scanPendingPackage(request).toPromise();

      if (response.success) {
        this.showPendingPackageMessage(
          `Paquete ${packageInfo.packageNumber} agregado exitosamente a la lista de empaquetado`,
          'success'
        );

        // Limpiar formulario y recargar paquetes según la vista actual
        this.clearPendingPackageForm();
        this.reloadCurrentViewPackages();

        // Limpiar información del paquete confirmado
        this.packageToConfirmInfo = null;
        this.packageSparePartsToConfirm = [];
        this.selectedPackager = null;
      } else {
        this.showPendingPackageMessage(
          `Error al agregar paquete: ${response.message}`,
          'error'
        );
      }
    } catch (error) {
      console.error('Error al agregar paquete a lista:', error);
      this.showPendingPackageMessage(
        `Error al agregar paquete: ${error.error?.message || error.message || 'Error desconocido'}`,
        'error'
      );
    }
  }


  /**
   * Mostrar mensaje para paquetes pendientes
   */
  private showPendingPackageMessage(message: string, type: 'success' | 'error'): void {
    this.pendingPackageMessage = message;
    this.pendingPackageMessageType = type;

    // Auto-limpiar mensaje después de 5 segundos
    setTimeout(() => {
      this.clearPendingPackageMessage();
    }, 5000);
  }

  private async fetchPackagingEmergentCommentAlerts(packageNumber: string): Promise<any[]> {
    try {
      const response = await this.pendingPackagesService
        .getEmergentCommentAlerts(packageNumber)
        .toPromise();
      const alertGroups = response?.emergentCommentAlerts ?? (response as any)?.EmergentCommentAlerts;
      return Array.isArray(alertGroups) ? alertGroups : [];
    } catch (alertError) {
      console.error('Error al obtener alertas de comentario emergente:', alertError);
      return [];
    }
  }

  private buildEmergentCommentLines(alertGroups: any[] | undefined): string[] {
    if (!Array.isArray(alertGroups) || alertGroups.length === 0) {
      return [];
    }

    const lines: string[] = [];
    alertGroups.forEach((group, groupIndex) => {
      const comment = (group?.comment ?? group?.Comment ?? '').toString().trim();
      if (!comment) {
        return;
      }

      const items = Array.isArray(group?.items) ? group.items : (Array.isArray(group?.Items) ? group.Items : []);
      const names = Array.from(new Set(
        items
          .map((item: any) => (item?.sparePartName ?? item?.SparePartName ?? '').toString().trim())
          .filter((name: string) => name !== '')
      ));
      const namesText = names.length > 0 ? ` - ${names.join(', ')}` : '';
      lines.push(`${groupIndex + 1}) "${comment}"${namesText}`);
    });

    return lines;
  }

  private showEmergentCommentModal(lines: string[], context: string): void {
    if (!Array.isArray(lines) || lines.length === 0) {
      return;
    }

    this.showAlert(`Comentario emergente (${context})`, lines.join('\n'), 'warning');
  }

  private showEmergentCommentAlerts(alertGroups: any[] | undefined, context: string): void {
    const lines = this.buildEmergentCommentLines(alertGroups);
    this.showEmergentCommentModal(lines, context);
  }

  /**
   * Limpiar mensaje de paquetes pendientes
   */
  private clearPendingPackageMessage(): void {
    this.pendingPackageMessage = '';
  }

  /**
   * Limpiar formulario de paquetes pendientes
   */
  private clearPendingPackageForm(): void {
    this.pendingPackageToScan = '';
    this.scannedPendingPackageInfo = null;
    this.clearPendingPackageMessage();
    this.remitoNumberForPendingPackage = '';
    this.transportTypeForPendingPackage = 'camion';
  }

  /**
   * Escanear paquete pendiente para enviado
   */
  async scanPendingPackageForEnviado(): Promise<void> {
    if (!this.pendingPackageToScan?.trim()) {
      this.showPendingPackageMessage('Por favor ingresa un número de paquete', 'error');
      return;
    }

    this.scanningPendingPackage = true;
    this.scannedPendingPackageInfo = null;
    this.clearPendingPackageMessage();

    try {
      // Verificar si el paquete existe y está disponible
      const response = await this.pendingPackagesService.checkPackageNumber(this.pendingPackageToScan.trim()).toPromise();

      if (response.isDeleted) {
        this.showPendingPackageMessage(
          `El paquete ${this.pendingPackageToScan} fue eliminado y no puede ser reutilizado. Razón: ${response.reason}`,
          'error'
        );
        return;
      }

      if (!response.exists) {
        this.showPendingPackageMessage(
          `El paquete ${this.pendingPackageToScan} no existe`,
          'error'
        );
        return;
      }

      if (response.status !== 'CREADO') {
        this.showPendingPackageMessage(
          `El paquete ${this.pendingPackageToScan} ya no está disponible para escanear (Estado: ${response.status})`,
          'error'
        );
        return;
      }

      // Obtener información del paquete pendiente
      const pendingPackagesResponse = await this.pendingPackagesService.searchPendingPackages({
        packageNumber: this.pendingPackageToScan.trim(),
        page: 1,
        pageSize: 1
      }).toPromise();

      if (pendingPackagesResponse.pendingPackages.length > 0) {
        const packageInfo = pendingPackagesResponse.pendingPackages[0];
        this.scannedPendingPackageInfo = {
          packageNumber: packageInfo.packageNumber,
          orderNumber: packageInfo.orderNumber,
          sparePartsCount: packageInfo.sparePartCount
        };
        this.showPendingPackageMessage(
          `Paquete ${this.pendingPackageToScan} encontrado. ${packageInfo.sparePartCount} repuestos disponibles.`,
          'success'
        );
      } else {
        this.showPendingPackageMessage(
          `No se pudo obtener información del paquete ${this.pendingPackageToScan}`,
          'error'
        );
      }
    } catch (error) {
      console.error('Error al escanear paquete pendiente para enviado:', error);
      this.showPendingPackageMessage(
        `Error al escanear paquete: ${error.error?.message || error.message || 'Error desconocido'}`,
        'error'
      );
    } finally {
      this.scanningPendingPackage = false;
    }
  }

  /**
   * Agregar paquete pendiente a la lista de enviado
   */
  async addPendingPackageToEnviadoList(): Promise<void> {
    if (!this.scannedPendingPackageInfo || !this.remitoNumberForPendingPackage?.trim()) {
      this.showPendingPackageMessage('Por favor completa todos los campos requeridos', 'error');
      return;
    }

    try {
      const request = {
        packageNumber: this.scannedPendingPackageInfo.packageNumber,
        listType: 'enviado',
        scannedBy: 'system',
        remitoNumber: this.remitoNumberForPendingPackage.trim()
      };

      const response = await this.pendingPackagesService.scanPendingPackage(request).toPromise();

      if (response.success) {
        this.showPendingPackageMessage(
          `Paquete ${this.scannedPendingPackageInfo.packageNumber} enviado exitosamente al taller`,
          'success'
        );

        // Limpiar formulario y recargar paquetes
        this.clearPendingPackageForm();
        this.loadExistingPackages();
      } else {
        this.showPendingPackageMessage(
          `Error al enviar paquete: ${response.message}`,
          'error'
        );
      }
    } catch (error) {
      console.error('Error al enviar paquete:', error);
      this.showPendingPackageMessage(
        `Error al enviar paquete: ${error.error?.message || error.message || 'Error desconocido'}`,
        'error'
      );
    }
  }

  /**
   * Escanear paquete pendiente para cargado
   */
  async scanPendingPackageForCargado(): Promise<void> {
    if (!this.pendingPackageToScan?.trim()) {
      this.showPendingPackageMessage('Por favor ingresa un número de paquete', 'error');
      return;
    }

    this.scanningPendingPackage = true;
    this.scannedPendingPackageInfo = null;
    this.clearPendingPackageMessage();

    try {
      // Verificar si el paquete existe y está disponible
      const response = await this.pendingPackagesService.checkPackageNumber(this.pendingPackageToScan.trim()).toPromise();

      if (response.isDeleted) {
        this.showPendingPackageMessage(
          `El paquete ${this.pendingPackageToScan} fue eliminado y no puede ser reutilizado. Razón: ${response.reason}`,
          'error'
        );
        return;
      }

      if (!response.exists) {
        this.showPendingPackageMessage(
          `El paquete ${this.pendingPackageToScan} no existe`,
          'error'
        );
        return;
      }

      if (response.status !== 'CREADO') {
        this.showPendingPackageMessage(
          `El paquete ${this.pendingPackageToScan} ya no está disponible para escanear (Estado: ${response.status})`,
          'error'
        );
        return;
      }

      // Obtener información del paquete pendiente
      const pendingPackagesResponse = await this.pendingPackagesService.searchPendingPackages({
        packageNumber: this.pendingPackageToScan.trim(),
        page: 1,
        pageSize: 1
      }).toPromise();

      if (pendingPackagesResponse.pendingPackages.length > 0) {
        const packageInfo = pendingPackagesResponse.pendingPackages[0];
        this.scannedPendingPackageInfo = {
          packageNumber: packageInfo.packageNumber,
          orderNumber: packageInfo.orderNumber,
          sparePartsCount: packageInfo.sparePartCount
        };
        this.showPendingPackageMessage(
          `Paquete ${this.pendingPackageToScan} encontrado. ${packageInfo.sparePartCount} repuestos disponibles.`,
          'success'
        );
      } else {
        this.showPendingPackageMessage(
          `No se pudo obtener información del paquete ${this.pendingPackageToScan}`,
          'error'
        );
      }
    } catch (error) {
      console.error('Error al escanear paquete pendiente para cargado:', error);
      this.showPendingPackageMessage(
        `Error al escanear paquete: ${error.error?.message || error.message || 'Error desconocido'}`,
        'error'
      );
    } finally {
      this.scanningPendingPackage = false;
    }
  }

  /**
   * Agregar paquete pendiente a la lista de cargado
   */
  async addPendingPackageToCargadoList(): Promise<void> {
    if (!this.scannedPendingPackageInfo || !this.transportTypeForPendingPackage) {
      this.showPendingPackageMessage('Por favor completa todos los campos requeridos', 'error');
      return;
    }

    try {
      const request = {
        packageNumber: this.scannedPendingPackageInfo.packageNumber,
        listType: 'cargado',
        scannedBy: 'system',
        transportType: this.transportTypeForPendingPackage
      };

      const response = await this.pendingPackagesService.scanPendingPackage(request).toPromise();

      if (response.success) {
        this.showPendingPackageMessage(
          `Paquete ${this.scannedPendingPackageInfo.packageNumber} cargado exitosamente al camión`,
          'success'
        );

        // Limpiar formulario y recargar paquetes
        this.clearPendingPackageForm();
        this.loadExistingPackages();
      } else {
        this.showPendingPackageMessage(
          `Error al cargar paquete: ${response.message}`,
          'error'
        );
      }
    } catch (error) {
      console.error('Error al cargar paquete:', error);
      this.showPendingPackageMessage(
        `Error al cargar paquete: ${error.error?.message || error.message || 'Error desconocido'}`,
        'error'
      );
    }
  }

  /**
   * Procesar la generación de etiquetas y empaquetado
   */
  private processLabelsAndPackages(order: OrderOutModel, selectedSpareParts: any[]): void {
    // Preparar los datos para el backend
    const request = {
      orderId: order.orderId,
      selectedSpareParts: selectedSpareParts,
      generatedBy: 'system'
    };

    // Mostrar indicador de carga
    this.showAlert('Info', 'Generando etiquetas y empaquetando orden...', 'info');

    // Llamar al servicio
    this.packageListsService.generateLabelsAndPackage(request).subscribe({
      next: (response) => {
        if (response.success) {
          this.showEmergentCommentAlerts(response.emergentCommentAlerts, 'Generación de paquetes');
          // Mostrar mensaje de éxito
          this.showAlert(
            'Éxito',
            `¡Orden empaquetada exitosamente!\n\n` +
            `• Lista de paquetes creada: ID ${response.packageListId}\n` +
            `• ${response.totalSparePartsProcessed} repuestos procesados\n` +
            `• ${response.labels.length} etiquetas generadas`,
            'success'
          );

          // Recargar la lista de paquetes empaquetados existentes
          if (this.currentView === 'empaquetado') {
            this.loadExistingPackagesFiltered('empaquetado');
          } else {
            this.loadExistingPackages();
          }
        } else {
          this.showAlert('Error', `Error al empaquetar la orden: ${response.message}`, 'error');
        }
      },
      error: (error) => {
        console.error('Error al generar etiquetas y empaquetar:', error);
        this.showAlert(
          'Error',
          `Error al generar etiquetas y empaquetar la orden:\n${error.error?.message || error.message || 'Error desconocido'}`,
          'error'
        );
      }
    });
  }













  // Método para finalizar la carga al camión
  finalizeTruckLoading() {
    if (this.scannedItemsForLoading.length === 0) {
      this.toastService.showWarning('No hay repuestos cargados para despachar');
      return;
    }

    // Para camión y camioneta, proceder directamente sin remito
    // El remito solo se solicita al enviar al taller (misma lógica que el camión)
    this.proceedWithFinalizeLoading();
  }

  // Solicitar remito para camioneta
  requestRemitoForCamioneta(): void {
    this.modalTitle = 'Número de Remito para Camioneta';
    this.modalMessage = `Ingrese el número de remito para la carga en camioneta:`;
    this.inputPlaceholder = 'Ej: REM-001';
    this.inputValue = '';

    // Forzar actualización del input
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);

    this.inputCallback = (remitoNumber: string) => {
      if (!remitoNumber || remitoNumber.trim() === '') {
        this.showAlert('Error', 'Debe ingresar un número de remito válido', 'error');
        return;
      }

      // Continuar con la finalización usando el remito
      this.proceedWithFinalizeLoading(remitoNumber.trim());
    };

    this.showInputModal = true;
  }

  // Proceder con la finalización de la carga
  proceedWithFinalizeLoading(remitoNumber?: string): void {
    // Preparar datos para enviar al backend
    const repuestosCargados = this.scannedItemsForLoading.map(item => ({
      sparePartId: item.sparePartId,
      productCode: item.productCode,
      packageNumber: item.packageNumber,
      orderNumber: item.orderNumber
    }));

    const repuestosNoCargados = this.empaquetadosForLoading.map(item => ({
      sparePartId: item.sparePartId,
      productCode: item.productCode,
      packageNumber: item.packageNumber,
      orderNumber: item.orderNumber
    }));

    // Mostrar advertencia si no todos los repuestos están cargados
    if (repuestosNoCargados.length > 0) {
      const mensajeNoCargados = this.transportType === 'camion'
        ? `Los repuestos no cargados pasarán al estado "EXPORTADO-NO-CARGADO".`
        : `Los repuestos no cargados mantendrán su estado actual.`;

      this.toastService.showWarning(
        `Advertencia: No todos los repuestos están escaneados. ` +
        `Repuestos cargados: ${repuestosCargados.length}, ` +
        `No cargados: ${repuestosNoCargados.length}. ` +
        mensajeNoCargados + ` ` +
        `Continuando con la finalización...`
      );
    }

    // Cerrar el modal inmediatamente después de la confirmación
    this.showMainLoadingModal = false;

    // Llamar al endpoint backend para finalizar la carga
    this.packageListsService.finalizarCargaCamion({
      tipoVehiculo: this.transportType,
      repuestosCargados: repuestosCargados,
      repuestosNoCargados: repuestosNoCargados,
      remitoNumber: remitoNumber || undefined // Incluir remito si se proporcionó
    }).subscribe({
      next: (response: any) => {
        if (response.success) {
          const mensajeExito = response.tipoVehiculo === 'camion'
            ? `Carga al camión finalizada exitosamente. ` +
            `Repuestos exportados: ${response.repuestosCargados}, ` +
            `No exportados: ${response.repuestosNoCargados}, ` +
            `Total: ${response.totalProcesados}`
            : `Carga a camioneta finalizada exitosamente. ` +
            `Repuestos cargados: ${response.repuestosCargados} (mantienen estado actual), ` +
            `Total: ${response.totalProcesados}`;

          this.toastService.showWarning(
            mensajeExito,
            'Éxito',
            true
          );

          // Crear lista de items exportados para mostrar en la vista de confirmación
          const nuevoEstado = response.tipoVehiculo === 'camion' ? 'EXPORTADO' : 'SIN_CAMBIOS';
          this.exportedItemsList = this.scannedItemsForLoading.map(item => ({
            ...item,
            newStatus: nuevoEstado
          }));

          // Limpiar las listas de carga
          this.scannedItemsForLoading = [];
          this.empaquetadosForLoading = [];
          this.clearTruckLoadingDraft();

          // Cerrar modales de carga
          this.showMainLoadingModal = false;
          this.showVehicleSelectionModal = false;

          // Mostrar vista de confirmación con items exportados
          this.showExportConfirmationModal = true;
        } else {
          this.toastService.showError(response.message || 'Error al finalizar la carga');
        }
      },
      error: (error) => {
        console.error('Error al finalizar la carga:', error);
        this.toastService.showError(
          `Error al finalizar la carga: ${error.error?.message || error.message || 'Error desconocido'}`
        );
      }
    });
  }

  // Cerrar modal de confirmación de exportación
  closeExportConfirmationModal(): void {
    this.showExportConfirmationModal = false;
    this.exportedItemsList = [];
    // Recargar paquetes para mostrar los cambios
    this.loadPaquetesEnCamion();
    this.syncAllViews();
  }

  // Método para cargar paquetes en camión
  loadPaquetesEnCamion(): void {
    this.loadingPaquetesEnCamion = true;

    // Usar la lista unificada para mostrar todos los paquetes cargados
    this.loadHistorialCargamentos();

    // También cargar repuestos individuales para compatibilidad
    this.packageListsService.getEmpaquetadosParaCargado().subscribe({
      next: (data) => {
        // Filtrar elementos que están cargados al camión (excluyendo EXPORTADO-NO-CARGADO)
        this.paquetesEnCamion = data.filter((item: any) =>
          item.productStatus === 'CARGADO' && item.productStatus !== 'EXPORTADO-NO-CARGADO'
        );
        this.loadingPaquetesEnCamion = false;
      },
      error: (error) => {
        console.error('❌ Error cargando repuestos cargados:', error);
        this.loadingPaquetesEnCamion = false;
        this.showAlert('Error', 'Error al cargar repuestos cargados', 'error');
      }
    });
  }

  // Método para filtrar paquetes por tipo de vehículo con búsqueda
  getPaquetesByVehicleType(vehicleType: 'camion' | 'camioneta'): any[] {
    if (!this.historialCargamentos) {
      return [];
    }

    let filtered = this.historialCargamentos.filter((paquete: any) => {
      // Filtrar por tipo de vehículo
      if (paquete.tipoVehiculo) {
        return paquete.tipoVehiculo === vehicleType;
      }

      // Si no tiene tipoVehiculo, usar heurística basada en el número de repuestos
      if (vehicleType === 'camion') {
        return paquete.totalRepuestos > 1;
      } else {
        return paquete.totalRepuestos <= 1;
      }
    });

    // Aplicar filtro de búsqueda si existe
    if (this.searchTermCargado && this.searchTermCargado.trim()) {
      const searchLower = this.searchTermCargado.toLowerCase().trim();
      filtered = filtered.filter((paquete: any) => {
        return (
          (paquete.packageNumber && paquete.packageNumber.toLowerCase().includes(searchLower)) ||
          (paquete.orden && paquete.orden.toLowerCase().includes(searchLower)) ||
          (paquete.packageName && paquete.packageName.toLowerCase().includes(searchLower))
        );
      });
    }

    return filtered;
  }

  // === MÉTODOS PARA CARGA AL CAMIÓN ===

  // Iniciar proceso de carga al camión
  startCargado(): void {
    this.currentView = 'cargado';
    this.clearLists();
    this.initializeViewData();

    // Recargar los paquetes en el camión para mostrar los más recientes
    this.loadPaquetesEnCamion();

    // Cargar paquetes cargados existentes
    this.loadExistingPackagesFiltered('cargado');
  }

  // Seleccionar tipo de vehículo y proceder
  selectVehicleType(vehicleType: 'camion' | 'camioneta'): void {
    this.transportType = vehicleType;
    this.loadingModalScanError = null;
    this.loadingModalMobileTab = 'pending';
    this.showVehicleSelectionModal = false;
    this.showMainLoadingModal = true;

    // Limpiar el historial durante el proceso de carga para evitar confusión
    this.historialCargamentos = [];

    const restoredDraft = this.tryRestoreTruckLoadingDraft(vehicleType);
    if (!restoredDraft) {
      this.loadEmpaquetadosForLoading();
    } else if (this.scannedItemsForLoading.length > 0) {
      this.loadingModalMobileTab = 'scanned';
    }
  }

  // Cargar repuestos empaquetados disponibles para carga
  loadEmpaquetadosForLoading(): void {
    this.loadingEmpaquetados = true;

    // Definir estados válidos según el tipo de vehículo
    const validStatesForCamion = [
      'EMPAQUETADO-EXPORTACION-ACTUAL-AUTOCOMERCIAL',
      'EMPAQUETADO-EXPORTACION-ACTUAL-SERIMPORT'
    ];

    // Primero obtener los paquetes únicos de la vista "Empaquetado"
    this.packageListsService.getUniquePackages('empaquetado').subscribe({
      next: (uniquePackages) => {
        if (uniquePackages.length === 0) {
          this.empaquetadosForLoading = [];
          this.loadingEmpaquetados = false;
          return;
        }

        // Obtener los números de paquete de la vista "Empaquetado"
        const packageNumbers = uniquePackages.map(pkg => pkg.packageNumber);

        // Ahora obtener todos los repuestos empaquetados
        this.packageListsService.getEmpaquetadosParaCargado().subscribe({
          next: (data) => {
            // Filtrar solo los repuestos que están en los paquetes de la vista "Empaquetado"
            // Y que tienen estados válidos para el tipo de vehículo seleccionado
            const filteredData = data.filter((item: any) => {
              const packageNumber = item.packageNumber || item.packagenumber;
              const productStatus = item.productStatus || item.productstatus || '';
              const packageNumberNormalized = (packageNumber || '').trim().toUpperCase();
              const isInViewPackage = packageNumbers.some(
                (pn: string) => (pn || '').trim().toUpperCase() === packageNumberNormalized
              );

              // Para camión: solo estados específicos
              // Para camioneta: cualquier estado que empiece con EMPAQUETADO- (ya que el backend excluye los que tienen TransportType)
              let hasValidStatus: boolean;
              if (this.transportType === 'camion') {
                hasValidStatus = validStatesForCamion.includes(productStatus);
              } else {
                // Camioneta: acepta cualquier estado EMPAQUETADO-*
                hasValidStatus = productStatus.startsWith('EMPAQUETADO-');
              }
              return isInViewPackage && hasValidStatus;
            });

            // Mapear los datos filtrados con fallbacks para evitar errores
            this.empaquetadosForLoading = filteredData.map((item: any) => ({
              sparePartId: item.sparePartId || item.sparepartId,
              sparePartName: item.sparePartName || item.sparepartname || item.sparePartNameEs || 'Sin nombre',
              productCode: item.productCode || item.productcode || 'N/A',
              packageNumber: item.packageNumber || item.packagenumber || 'N/A',
              orderNumber: item.orderNumber || item.ordernumber || 'N/A',
              exportNumber: item.exportNumber || item.exportnumber || 'N/A',
              amount: item.amount || 1,
              unitPrice: item.unitPrice || item.unitprice || 0,
              productStatus: item.productStatus || item.productstatus || 'EMPAQUETADO'
            }));
            this.saveTruckLoadingDraft();
            this.loadingEmpaquetados = false;
          },
          error: (error) => {
            console.error('❌ Error cargando elementos empaquetados:', error);
            this.toastService.showError('Error al cargar elementos empaquetados');
            this.loadingEmpaquetados = false;
            this.empaquetadosForLoading = [];
          }
        });
      },
      error: (error) => {
        console.error('❌ Error cargando paquetes únicos:', error);
        this.toastService.showError('Error al cargar paquetes empaquetados');
        this.loadingEmpaquetados = false;
        this.empaquetadosForLoading = [];
      }
    });
  }

  // Método auxiliar para mantener el foco en el input después de escanear
  private maintainInputFocus(): void {
    setTimeout(() => {
      if (this.scanningInput && this.scanningInput.nativeElement) {
        // Limpiar el input para poder escribir el siguiente código inmediatamente
        this.currentScanningCode = '';
        // Asegurar que el input esté habilitado (no bloqueado)
        this.scanningInput.nativeElement.disabled = false;
        // Enfocar el input para seguir escribiendo sin interrupciones
        this.scanningInput.nativeElement.focus();
        // Forzar la detección de cambios para actualizar el estado del input
        this.cdr.detectChanges();
      }
    }, 50);
  }

  // Método para quitar un paquete/repuesto de la lista de escaneados
  removeItemFromScanned(item: any, index: number): void {
    // Remover el item de la lista de escaneados
    this.scannedItemsForLoading = this.scannedItemsForLoading.filter((_, i) => i !== index);

    // Volver a agregar el item a la lista de disponibles
    // Remover campos temporales agregados durante el escaneo
    const itemToRestore: any = { ...item };
    delete itemToRestore.scannedAt;
    delete itemToRestore.scannedFromPackage;
    delete itemToRestore.scannedFromOrder;
    delete itemToRestore.packageScanned;
    delete itemToRestore.orderItemNumber;

    // Verificar si ya está en la lista de disponibles
    const alreadyInAvailable = this.empaquetadosForLoading.some(avail =>
      avail.sparePartId === item.sparePartId &&
      avail.packageNumber === item.packageNumber &&
      avail.productCode === item.productCode
    );

    if (!alreadyInAvailable) {
      this.empaquetadosForLoading.push(itemToRestore);
      // Forzar actualización
      this.empaquetadosForLoading = [...this.empaquetadosForLoading];
    } else {
    }

    // Forzar actualización de la lista de escaneados
    this.scannedItemsForLoading = [...this.scannedItemsForLoading];
    this.updateCounter++;
    this.lastUpdateTime = new Date().toLocaleTimeString();

    // Forzar detección de cambios
    this.cdr.detectChanges();

    // Mantener el foco en el input después de quitar un item
    this.maintainInputFocus();
    this.saveTruckLoadingDraft();
  }

  // Escanear repuesto individual
  // Función mejorada para detectar si es orden o repuesto individual
  scanIndividualSparePart(): void {
    if (!this.currentScanningCode.trim()) {
      return;
    }

    this.isScanningIndividual = true;
    // Normalizar el código escaneado antes de procesarlo (reemplazar comillas simples por guiones)
    let scanCode = this.normalizeScannedCode(this.currentScanningCode.trim());

    // Verificar si es un número de paquete (contiene "PAC")
    if (scanCode.includes('PAC') || scanCode.includes('-PAC')) {
      // Es un paquete - procesar todos sus repuestos para carga al camión
      this.scanPackageForLoading(scanCode);
      return;
    }

    // Verificar si es un número de orden (comienza con "ORD-")
    if (scanCode.startsWith('ORD-') || scanCode.includes('ORD')) {
      // Es una orden completa - procesar todos sus paquetes
      this.scanOrder(scanCode);
      return;
    }

    // Si no es orden, buscar por packageNumber o productCode (repuesto individual)
    const foundItem = this.empaquetadosForLoading.find(item =>
      item.packageNumber === scanCode || item.productCode === scanCode
    );

    if (foundItem) {
      this.loadingModalScanError = null;
      // Verificar si ya está escaneado
      const alreadyScanned = this.scannedItemsForLoading.some(scanned =>
        scanned.packageNumber === foundItem.packageNumber || scanned.productCode === foundItem.productCode
      );

      if (alreadyScanned) {
        this.isScanningIndividual = false;
        this.showAlert('Advertencia', 'Este repuesto ya ha sido escaneado', 'warning');
        // Mantener el foco en el input
        this.maintainInputFocus();
        return;
      }

      // Agregar a elementos escaneados
      this.scannedItemsForLoading.push({
        ...foundItem,
        scannedAt: new Date()
      } as any);

      // Crear nueva referencia para forzar detección de cambios
      this.scannedItemsForLoading = [...this.scannedItemsForLoading];
      this.updateCounter++;
      this.lastUpdateTime = new Date().toLocaleTimeString();

      // Remover de elementos disponibles
      this.empaquetadosForLoading = this.empaquetadosForLoading.filter(item =>
        item.packageNumber !== foundItem.packageNumber && item.productCode !== foundItem.productCode
      );

      // Crear nueva referencia para el array de disponibles también
      this.empaquetadosForLoading = [...this.empaquetadosForLoading];

      // NO limpiar el código, mantener el input enfocado para seguir escaneando
      // this.currentScanningCode = ''; // Comentado para mantener el foco

      // Mantener el foco en el input después de procesar
      this.maintainInputFocus();

      this.isScanningIndividual = false;

      // Forzar detección de cambios
      this.cdr.detectChanges();

      // Usar setTimeout para asegurar que la UI se actualice completamente
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 50);

      this.focusLoadingScannedTab();
      this.saveTruckLoadingDraft();
    } else {
      this.isScanningIndividual = false;
      const msg = 'El código escaneado no corresponde a un paquete/orden con repuestos asociados.';
      this.loadingModalScanError = msg;
      if (!this.showMainLoadingModal) {
        this.showAlert('Error', msg, 'error');
      }
      this.maintainInputFocus();
    }
  }

  // Función para obtener TODOS los repuestos de una orden específica
  private async getAllSparePartsForOrder(orderNumber: string): Promise<any[]> {
    try {

      // Llamar al servicio para obtener TODOS los repuestos de la orden desde el backend
      const sparePartsFromBackend = await this.packageListsService.getSparePartsByOrder(orderNumber).toPromise();

      if (!sparePartsFromBackend || sparePartsFromBackend.length === 0) {
        // Fallback: buscar en el array local como respaldo
        const sparePartsFromLocal = this.empaquetadosForLoading.filter(item =>
          item.orderNumber === orderNumber
        );

        // Agregar información adicional
        const sparePartsWithInfo = sparePartsFromLocal.map((sparePart, index) => ({
          ...sparePart,
          scannedAt: new Date(),
          scannedFromOrder: true,
          orderItemNumber: index + 1,
          sparePartId: sparePart.sparePartId || `temp-${Date.now()}-${index}`
        }));

        return sparePartsWithInfo;
      }

      // Agregar información adicional a los repuestos del backend
      const sparePartsWithInfo = sparePartsFromBackend.map(sparePart => ({
        ...sparePart,
        scannedAt: new Date(),
        scannedFromOrder: true,
        // orderItemNumber ya viene del backend
        sparePartId: sparePart.sparePartId || `backend-${Date.now()}-${sparePart.orderItemNumber}`
      }));

      return sparePartsWithInfo;

    } catch (error) {
      // Fallback final: buscar en array local
      const sparePartsFromLocal = this.empaquetadosForLoading.filter(item =>
        item.orderNumber === orderNumber
      );

      const sparePartsWithInfo = sparePartsFromLocal.map((sparePart, index) => ({
        ...sparePart,
        scannedAt: new Date(),
        scannedFromOrder: true,
        orderItemNumber: index + 1,
        sparePartId: sparePart.sparePartId || `fallback-${Date.now()}-${index}`
      }));

      return sparePartsWithInfo;
    }
  }

  // Nueva función para procesar paquetes completos para carga al camión (MUCHO MÁS SIMPLE)
  async scanPackageForLoading(packageNumber: string): Promise<void> {
    try {
      // Normalizar el código escaneado (reemplazar comillas simples por guiones)
      packageNumber = this.normalizeScannedCode(packageNumber);
      const packageNumberNormalized = (packageNumber || '').trim().toUpperCase();

      // Buscar TODOS los repuestos que pertenecen a este paquete (case-insensitive)
      let sparePartsInPackage = this.empaquetadosForLoading.filter(item =>
        (item.packageNumber || '').trim().toUpperCase() === packageNumberNormalized
      );

      if (sparePartsInPackage.length === 0) {
        const packageSpareParts = await this.packageListsService.getSparePartsByPackage(packageNumber).toPromise();
        let msg: string;
        if (packageSpareParts?.length && packageHasAnuladoSparePart(packageSpareParts)) {
          msg = getPackageBlockedByAnuladoMessage(packageNumber);
        } else if (packageSpareParts?.length) {
          msg = `El paquete ${packageNumber} tiene ${packageSpareParts.length} repuesto(s), pero no está disponible para este cargamento. Verifique que esté en Empaquetado y sin TransportType.`;
        } else {
          msg = `Este paquete no tiene repuestos asociados (${packageNumber}).`;
        }
        this.loadingModalScanError = msg;
        if (!this.showMainLoadingModal) {
          this.showAlert('Advertencia', msg, 'warning');
        }
        this.isScanningIndividual = false;
        return;
      }

      this.loadingModalScanError = null;
      // Agregar TODOS los repuestos del paquete a la lista de cargados
      sparePartsInPackage.forEach((sparePart) => {
        // Agregar marca de tiempo y información adicional
        const sparePartWithInfo = {
          ...sparePart,
          scannedAt: new Date(),
          scannedFromPackage: true,
          packageScanned: packageNumber
        };

        this.scannedItemsForLoading.push(sparePartWithInfo);
      });

      // Remover TODOS los repuestos del paquete de la lista de disponibles
      this.empaquetadosForLoading = this.empaquetadosForLoading.filter(item =>
        (item.packageNumber || '').trim().toUpperCase() !== packageNumberNormalized
      );

      // Forzar actualización de la UI
      this.scannedItemsForLoading = [...this.scannedItemsForLoading];
      this.updateCounter++;
      this.lastUpdateTime = new Date().toLocaleTimeString();

      this.cdr.detectChanges();

      // Mantener el foco en el input después de procesar (NO limpiar el input)
      this.maintainInputFocus();

      this.focusLoadingScannedTab();
      this.saveTruckLoadingDraft();

    } catch (error) {
      console.error('❌ Error procesando paquete:', error);
      this.showAlert('Error', `Error al procesar el paquete: ${error.message || 'Error desconocido'}`, 'error');
    }

    this.isScanningIndividual = false;
  }

  // Nueva función para procesar órdenes completas
  async scanOrder(orderNumber: string): Promise<void> {
    try {

      // BUSCAR DIRECTAMENTE TODOS LOS REPUESTOS DE LA ORDEN
      // No necesitamos buscar paquetes primero, vamos directo a los repuestos
      const allSparePartsForOrder = await this.getAllSparePartsForOrder(orderNumber);
      const loadableSpareParts = allSparePartsForOrder.filter(sparePart =>
        this.empaquetadosForLoading.some(available =>
          available.sparePartId === sparePart.sparePartId ||
          (
            available.productCode === sparePart.productCode &&
            available.packageNumber === sparePart.packageNumber
          )
        )
      );

      if (!loadableSpareParts || loadableSpareParts.length === 0) {
        const msg = allSparePartsForOrder.length > 0
          ? `La orden ${orderNumber} no tiene repuestos disponibles para cargar. Verifique estados del paquete o repuestos anulados.`
          : `Esta orden no tiene repuestos asociados (${orderNumber}).`;
        this.loadingModalScanError = msg;
        if (!this.showMainLoadingModal) {
          this.showAlert('Error', msg, 'error');
        }
        this.isScanningIndividual = false;
        return;
      }

      if (loadableSpareParts.length < allSparePartsForOrder.length) {
        this.toastService.showWarning(
          `Se omitieron ${allSparePartsForOrder.length - loadableSpareParts.length} repuesto(s) de la orden por no estar disponibles para carga.`
        );
      }

      this.loadingModalScanError = null;
      // Agregar los repuestos disponibles de la orden a la lista de escaneados

      loadableSpareParts.forEach((sparePart) => {
        this.scannedItemsForLoading.push(sparePart);
      });

      // Forzar detección de cambios INMEDIATA después de agregar elementos
      this.cdr.detectChanges();

      // Crear nueva referencia para forzar detección de cambios
      this.scannedItemsForLoading = [...this.scannedItemsForLoading];
      this.updateCounter++;
      this.lastUpdateTime = new Date().toLocaleTimeString();

      // Remover los repuestos cargados de la lista de disponibles
      const loadedSparePartIds = new Set(loadableSpareParts.map(sp => sp.sparePartId));
      this.empaquetadosForLoading = this.empaquetadosForLoading.filter(item =>
        !loadedSparePartIds.has(item.sparePartId)
      );

      // Crear nueva referencia para el array de disponibles también
      this.empaquetadosForLoading = [...this.empaquetadosForLoading];

      // NO limpiar el código, mantener el input enfocado para seguir escaneando
      // this.currentScanningCode = ''; // Comentado para mantener el foco

      // Mantener el foco en el input después de procesar
      this.maintainInputFocus();

      // Forzar detección de cambios múltiples veces para asegurar la actualización
      this.cdr.detectChanges();

      // Usar setTimeout para asegurar que la UI se actualice completamente
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 50);

      this.isScanningIndividual = false;

      this.focusLoadingScannedTab();
      this.saveTruckLoadingDraft();

    } catch (error) {
      this.showAlert('Error', `Error al procesar la orden: ${error.error?.message || error.message || 'Error desconocido'}`, 'error');
      this.isScanningIndividual = false;
    }

  }

  // Método para probar el escaneo de un paquete conocido
  testScanPackage(): void {
    // Buscar un paquete que sepamos que existe en los datos actuales
    const availablePackages = [...new Set(this.empaquetadosForLoading.map(item => item.packageNumber).filter(p => p))];

    if (availablePackages.length === 0) {
      this.showAlert('Advertencia', 'No hay paquetes disponibles para probar', 'warning');
      return;
    }

    // Tomar el primer paquete disponible para la prueba
    const testPackage = availablePackages[0];
    const itemsInTestPackage = this.empaquetadosForLoading.filter(item => item.packageNumber === testPackage);

    // Mostrar notificación al usuario
    this.showAlert('Info', `Probando paquete: ${testPackage} (${itemsInTestPackage.length} repuestos)`, 'info');

    // Simular el escaneo del paquete directamente
    this.scanPackageForLoading(testPackage);
  }

  // Método de diagnóstico para verificar arrays
  diagnoseOrderData(orderNumber?: string): void {
    // Función de diagnóstico vacía para compatibilidad futura
  }

  /** Cierre seguro del modal de carga con confirmación y borrador */
  closeMainLoadingModal(): void {
    this.requestCancelTruckLoading();
  }

  // Volver a vista previa
  volverAVistaPrevia(): void {
    this.requestCancelTruckLoading();
  }

  private requestCancelTruckLoading(): void {
    const hasProgress = this.scannedItemsForLoading.length > 0 || this.currentScanningCode.trim().length > 0;
    const confirmationMessage = hasProgress
      ? 'Hay una carga en curso. Si sale ahora, se guardará un borrador para retomarlo luego. ¿Desea salir?'
      : '¿Desea cancelar y volver a la vista previa?';

    if (!window.confirm(confirmationMessage)) {
      return;
    }

    if (hasProgress) {
      this.saveTruckLoadingDraft();
    } else {
      this.clearTruckLoadingDraft();
    }

    this.resetTruckLoadingState();
  }

  private focusLoadingScannedTab(): void {
    this.loadingModalMobileTab = 'scanned';
  }

  private resetTruckLoadingState(): void {
    this.showMainLoadingModal = false;
    this.loadingModalScanError = null;
    this.loadingModalMobileTab = 'pending';
    this.showVehicleSelectionModal = false;
    this.scannedItemsForLoading = [];
    this.empaquetadosForLoading = [];
    this.transportType = 'camion';
    this.currentScanningCode = '';
    this.isScanningIndividual = false;

    // Sincronizar todas las vistas para asegurar que estén actualizadas
    this.syncAllViews();
  }

  private tryRestoreTruckLoadingDraft(vehicleType: 'camion' | 'camioneta'): boolean {
    const draft = this.getTruckLoadingDraft();
    if (!draft || draft.transportType !== vehicleType) {
      return false;
    }

    if (!draft.scannedItemsForLoading?.length && !draft.currentScanningCode?.trim()) {
      return false;
    }

    const shouldRestore = window.confirm(
      `Se encontró un borrador de carga (${draft.scannedItemsForLoading.length} repuestos). ¿Desea recuperarlo?`
    );

    if (!shouldRestore) {
      this.clearTruckLoadingDraft();
      return false;
    }

    this.scannedItemsForLoading = (draft.scannedItemsForLoading || []).map((item: any) => ({
      ...item,
      scannedAt: item.scannedAt ? new Date(item.scannedAt) : new Date()
    }));
    this.empaquetadosForLoading = draft.empaquetadosForLoading || [];
    this.currentScanningCode = draft.currentScanningCode || '';
    this.cdr.detectChanges();
    return true;
  }

  private saveTruckLoadingDraft(): void {
    if (!this.transportType || !this.showMainLoadingModal) {
      return;
    }

    const draft: TruckLoadingDraft = {
      transportType: this.transportType,
      scannedItemsForLoading: this.scannedItemsForLoading || [],
      empaquetadosForLoading: this.empaquetadosForLoading || [],
      currentScanningCode: this.currentScanningCode || '',
      savedAt: new Date().toISOString()
    };

    localStorage.setItem(this.truckLoadingDraftKey, JSON.stringify(draft));
  }

  private getTruckLoadingDraft(): TruckLoadingDraft | null {
    try {
      const rawDraft = localStorage.getItem(this.truckLoadingDraftKey);
      if (!rawDraft) {
        return null;
      }
      return JSON.parse(rawDraft);
    } catch (error) {
      console.error('Error parsing truck loading draft:', error);
      localStorage.removeItem(this.truckLoadingDraftKey);
      return null;
    }
  }

  private clearTruckLoadingDraft(): void {
    localStorage.removeItem(this.truckLoadingDraftKey);
  }

  /**
   * Cargar todos los paquetes cargados al camión (unificado)
   */
  loadHistorialCargamentos() {
    this.loadingHistorial = true;

    // Obtener repuestos que están cargados al camión usando el endpoint específico
    this.packageListsService.getRepuestosCargadosAlCamion().subscribe({
      next: (repuestos) => {
        // Ya no necesitamos filtrar, el endpoint devuelve solo los cargados
        const repuestosCargados = repuestos;

        if (repuestosCargados.length === 0) {
          this.historialCargamentos = [];
          this.loadingHistorial = false;
          return;
        }

        // Agrupar repuestos por PackageNumber
        const repuestosPorPaquete = new Map();

        repuestosCargados.forEach(repuesto => {
          const packageNumber = repuesto.packageNumber;
          if (!repuestosPorPaquete.has(packageNumber)) {
            repuestosPorPaquete.set(packageNumber, []);
          }
          repuestosPorPaquete.get(packageNumber).push(repuesto);
        });

        // Crear lista unificada de paquetes cargados con formato consistente
        this.historialCargamentos = [];

        repuestosPorPaquete.forEach((repuestosDelPaquete, packageNumber) => {
          // Obtener información del primer repuesto para datos comunes
          const primerRepuesto = repuestosDelPaquete[0];

          // Determinar tipo de vehículo basándose en el transportType del primer repuesto
          // Si todos los repuestos tienen el mismo transportType válido, usarlo
          const transportTypes = repuestosDelPaquete.map(rep => (rep as any).transportType).filter(t => t && t !== null && t !== '');
          let tipoVehiculo: string | null = null;

          if (transportTypes.length > 0 && transportTypes.every(t => t === transportTypes[0])) {
            // Todos los repuestos tienen el mismo TransportType válido
            tipoVehiculo = transportTypes[0];
          } else if (transportTypes.length === 0) {
            // Ningún repuesto tiene TransportType establecido - no mostrar en historial
            return; // Saltar este paquete
          } else {
            // TransportType inconsistente - usar el más común o el primero
            tipoVehiculo = transportTypes[0];
          }

          // Obtener la fecha real de carga (la más antigua - primera vez que se cargó)
          const fechasCarga = repuestosDelPaquete
            .map(rep => (rep as any).loadingDate)
            .filter(fecha => fecha != null && fecha !== undefined)
            .map(fecha => new Date(fecha))
            .sort((a, b) => a.getTime() - b.getTime()); // Ordenar de más antigua a más reciente

          // Usar la fecha más antigua (primera carga). Sin fecha guardada queda null:
          // usar "ahora" mostraba paquetes viejos como cargados en el momento de abrir la página.
          const fechaReal: Date | null = fechasCarga.length > 0 ? fechasCarga[0] : null;

          const historialItem = {
            packageNumber: packageNumber,
            packageName: `Paquete ${packageNumber}`,
            fecha: fechaReal, // Usar fecha real de carga desde el backend
            tipoVehiculo: tipoVehiculo!,
            estado: 'completado',
            orden: primerRepuesto.orderNumber || '',
            repuestos: repuestosDelPaquete.map(rep => ({
              sparePartId: rep.sparePartId,
              sparePartName: rep.sparePartName || 'Sin nombre',
              productCode: rep.productCode || 'Sin código',
              amount: rep.amount || 1,
              unitPrice: rep.unitPrice || 0,
              orderNumber: rep.orderNumber || '',
              exportNumber: rep.exportNumber || '',
              productStatus: rep.productStatus || 'EXPORTADO'
            })),
            totalRepuestos: repuestosDelPaquete.length,
            repuestosCargados: repuestosDelPaquete.length,
            repuestosNoCargados: 0
          };
          this.historialCargamentos.push(historialItem);
        });

        // Ordenar por fecha descendente (más recientes primero)
        // Más recientes primero; los paquetes sin fecha van al final
        this.historialCargamentos.sort((a, b) => (b.fecha?.getTime() ?? 0) - (a.fecha?.getTime() ?? 0));

        this.loadingHistorial = false;
      },
      error: (error) => {
        console.error('❌ Error cargando paquetes cargados al camión:', error);
        console.error('❌ Detalles del error:', {
          status: error.status,
          statusText: error.statusText,
          message: error.message,
          url: error.url
        });
        this.loadingHistorial = false;

        // Mostrar mensaje de error al usuario
        this.showAlert('Error', 'Error al cargar los paquetes cargados al camión. Verifique la conexión con el servidor.', 'error');
      }
    });
  }

  /**
   * Agregar nuevo cargamento al historial agrupado por paquetes
   * NOTA: Este método ya no se usa, se reemplazó por loadHistorialCargamentos()
   * que obtiene todos los paquetes cargados directamente de la base de datos
   */
  // agregarCargamentoAlHistorial(datos: any) {
  //   // Método deshabilitado - usar loadHistorialCargamentos() en su lugar
  // }

  /**
   * Mostrar detalles de un paquete del historial
   */
  viewPackageHistoryDetails(paquete: any) {
    this.selectedPackageForHistory = paquete;
    this.showPackageHistoryModal = true;
  }

  /**
   * Cerrar modal de detalles del paquete
   */
  closePackageHistoryModal() {
    this.showPackageHistoryModal = false;
    this.selectedPackageForHistory = null;
  }

  /**
   * Sincronizar todas las vistas después de una carga al camión
   */
  syncAllViews() {
    // LIMPIAR cachés antes de recargar para forzar datos frescos del backend
    this.uniquePackages = [];
    this.scannedItemsForLoading = [];
    this.empaquetadosForLoading = [];

    // Recargar la vista actual si es relevante
    if (this.currentView === 'empaquetado') {
      this.loadUniquePackages('empaquetado');
    }

    // Recargar los paquetes cargados al camión
    this.loadHistorialCargamentos();

    // Si estamos en la vista de cargado, también recargar
    if (this.currentView === 'cargado') {
      this.loadPaquetesEnCamion();
    }

    // Si estamos en la vista de enviado, también recargar
    if (this.currentView === 'enviado') {
      this.loadUniquePackages('enviado');
    }

    // SIEMPRE recargar la vista de enviado cuando se cargan paquetes al camión
    // porque ahora hay paquetes disponibles para enviar al taller
    this.loadUniquePackages('enviado');
  }

  // Iniciar nueva carga
  iniciarNuevaCarga(): void {
    this.showVehicleSelectionModal = true;
  }

  /**
   * Método interno para agregar un repuesto específico a un paquete
   */
  private addSparePartToPackageInternal(packageNumber: string, sparePartId: number): void {
    // Buscar el repuesto en los datos disponibles (paquete normal o gestión existente)
    let sparePart = this.availableSparePartsForPackage.find(sp => sp.sparePartId === sparePartId);

    // Si no se encuentra en la lista normal, buscar en la lista de gestión existente
    if (!sparePart && this.selectedPackageForManagement) {
      sparePart = this.availableSparePartsForOrder.find(sp => sp.sparePartId === sparePartId);
    }

    if (!sparePart) {
      console.error('❌ Repuesto no encontrado en las listas disponibles');
      this.showAlert('Error', 'Repuesto no encontrado en la lista disponible', 'error');
      return;
    }

    // Verificar si ya existe una lista activa para este paquete
    if (!this.currentPackageList) {
      const listName = `Paquete ${packageNumber} - ${new Date().toLocaleDateString()}`;
      const listType = this.currentView === 'empaquetado' ? 'empaquetado' : 'enviado';

      this.packageListsService.createListForComponent(listName, listType, 'camion').subscribe({
        next: (response) => {
          this.currentPackageList = response;
          this.addSparePartToPackageList(sparePart, packageNumber);
        },
        error: (error) => {
          console.error('❌ Error al crear lista:', error);
          this.showAlert('Error', 'Error al crear la lista de paquetes', 'error');
        }
      });
    } else {
      this.addSparePartToPackageList(sparePart, packageNumber);
    }
  }

  /**
   * Agregar repuesto a la lista de paquetes
   */
  private addSparePartToPackageList(sparePart: any, packageNumber: string): void {
    if (!this.currentPackageList) {
      console.error('❌ No hay lista activa para agregar el repuesto');
      return;
    }

    // Crear el item para agregar
    const itemToAdd = {
      sparePartId: sparePart.sparePartId,
      sparePartName: sparePart.sparePartName,
      sparePartNameEs: sparePart.sparePartNameEs || sparePart.sparePartName,
      sparePartNamePt: sparePart.sparePartNamePt || sparePart.sparePartName,
      productCode: sparePart.productCode || '',
      packageNumber: packageNumber,
      scannedAt: new Date(),
      orderNumber: sparePart.orderNumber || 'N/A'
    };

    // Guardar en la base de datos
    this.packageListsService.addItemForComponent(this.currentPackageList.packageListId, itemToAdd).subscribe({
      next: (savedItem) => {
        // Actualizar la lista local
        this.currentPackageList!.items.push({
          packageListItemId: savedItem.packageListItemId,
          packageListId: savedItem.packageListId,
          sparePartId: savedItem.sparePartId,
          sparePartName: savedItem.sparePartName,
          sparePartNameEs: savedItem.sparePartNameEs,
          sparePartNamePt: savedItem.sparePartNamePt,
          productCode: savedItem.productCode,
          packageNumber: savedItem.packageNumber,
          scannedAt: savedItem.scannedAt,
          orderNumber: savedItem.orderNumber,
          itemStatus: savedItem.itemStatus || 'active',
          position: savedItem.position || this.currentPackageList!.items.length + 1
        });

        this.currentPackageList!.totalItems++;

        this.showAlert('Éxito', `Repuesto "${sparePart.sparePartName}" agregado al paquete ${packageNumber}`, 'success');

        // Cerrar el modal si está abierto
        this.closeAddSparePartToPackageModal();
      },
      error: (error) => {
        console.error('❌ Error al agregar repuesto:', error);
        this.showAlert('Error', 'Error al agregar el repuesto al paquete', 'error');
      }
    });
  }

  /**
   * Reimprimir solo la etiqueta del paquete desde la vista de paquetes empaquetados
   */
  async reprintPackageLabelOnly(packageData: UniquePackageModel): Promise<void> {
    if (!packageData || !packageData.packageNumber) {
      alert('No se puede reimprimir la etiqueta: datos del paquete inválidos');
      return;
    }

    try {
      this.loading = true;

      // Obtener la orden completa usando el número de orden
      const orderResponse = await this.ordersService.getAllFilteredOrders(packageData.orderNumber).toPromise();
      const order = orderResponse?.find((o: any) => o.orderNumber === packageData.orderNumber);

      if (!order) {
        alert('No se pudo encontrar la orden para generar la etiqueta del paquete');
        this.loading = false;
        return;
      }

      if (this.isSendYesOrNoNo(this.getOrderSendYesOrNo(order))) {
        this.showAlert(
          'Error',
          `No se puede imprimir la etiqueta del paquete: la orden ${packageData.orderNumber} tiene ENVIAR = NO.`,
          'error'
        );
        this.loading = false;
        return;
      }

      // Usar el workshopId de la orden si está disponible, sino usar el del primer repuesto
      let finalWorkshopId = order?.workshopId;

      // Si la orden no tiene workshopId, obtener el del primer repuesto del paquete
      if (!finalWorkshopId) {
        try {
          const sparePartsResponse = await this.packageListsService.getSparePartsByPackage(packageData.packageNumber).toPromise();
          if (sparePartsResponse && sparePartsResponse.length > 0) {
            finalWorkshopId = sparePartsResponse[0].workshopId;
          } else {
          }
        } catch (error) {
          console.error('🎯 reprintPackageLabelOnly - Error obteniendo repuestos del paquete:', error);
        }
      }

      // Obtener datos correctos del taller
      const workshopData = this.getWorkshopDataById(finalWorkshopId);

      // Crear solo la etiqueta del paquete (sin etiquetas individuales de repuestos)
      const packageLabel: SparePartLabelInfo = {
        sparePartId: 0, // ID especial para etiqueta del paquete
        sparePartName: 'ETIQUETA DEL PAQUETE',
        productCode: packageData.packageNumber,
        packageNumber: packageData.packageNumber,
        orderNumber: packageData.orderNumber,
        siniestro: packageData.siniestro || order.siniestro || 'N/A',  // ✅ Priorizar siniestro del packageData
        clienteUY: order.clienteUY || 'AUTOCOMERCIAL',
        positionInPackage: 1,
        totalInPackage: packageData.sparePartsCount,
        // Información del taller correcta
        workshopName: workshopData.name,
        workshopAddress: workshopData.address,
        workshopPhone: workshopData.phone,
        workshopCity: workshopData.city
      };

      const labelsForPrint: SparePartLabelInfo[] = [packageLabel];

      // Crear orden con información correcta del taller
      const orderWithCorrectWorkshop = {
        ...order,
        workshopId: finalWorkshopId,
        workshopName: workshopData.name,
        workshopAddress: workshopData.address,
        workshopPhone: workshopData.phone,
        workshopCity: workshopData.city
      };

      // Imprimir solo la etiqueta del paquete
      this.labelsService.printLabelsWindow(orderWithCorrectWorkshop, labelsForPrint, true, true, finalWorkshopId);

      alert(`Etiqueta del paquete ${packageData.packageNumber} reimpresa exitosamente`);

    } catch (error) {
      console.error('Error al reimprimir etiqueta del paquete:', error);
      alert('Error al reimprimir la etiqueta del paquete');
    } finally {
      this.loading = false;
    }
  }

  /**
   * Método para imprimir solo la etiqueta del paquete
   */
  private printPackageLabelOnly(order: any, packageLabelInfo: SparePartLabelInfo, totalPieces: number): void {
    // Generar el HTML de la etiqueta del paquete
    const packageLabelHtml = this.generatePackageLabelHtml(order, packageLabelInfo, totalPieces);

    // Crear ventana de impresión
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Etiqueta del Paquete - ${packageLabelInfo.packageNumber}</title>
          <style>
            ${this.getPrintStyles()}
          </style>
        </head>
        <body>
          <div class="print-container">
            ${packageLabelHtml}
          </div>
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();

      // Esperar un momento antes de imprimir
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    }
  }

  /**
   * Generar el HTML de la etiqueta del paquete
   */
  private generatePackageLabelHtml(order: any, packageLabelInfo: SparePartLabelInfo, totalPieces: number): string {
    const logoSrc = this.getCompanyLogoPath(order?.clienteUY || 'AUTOCOMERCIAL');


    return `
      <div class="label package-label">
        <div class="label-header">
          <img class="label-logo" src="${logoSrc}" onerror="this.style.display='none'" />
          <div class="contact-info">
            <div class="contact-address">Dirección Empresa</div>
            <div class="provider-phone"><img src="/assets/images/whatsapp-svgrepo-com.svg" style="width: 12px; height: 12px; margin-right: 3px;" alt="WhatsApp" /> +598 95 765 648</div>
          </div>
        </div>

        <div class="package-title">ETIQUETA DE PAQUETE</div>

        <div class="package-main-info">
          <div class="package-row">
            <div><strong>Nro Orden:</strong> ${packageLabelInfo.orderNumber}</div>
            <div><strong>Nro Siniestro:</strong> ${packageLabelInfo.siniestro}</div>
          </div>
          <div class="package-row">
            <div><strong>Cant Piezas:</strong> ${totalPieces}</div>
            <div><strong>Nro Paquete:</strong> ${packageLabelInfo.packageNumber}</div>
          </div>
        </div>

        <!-- Datos del taller asignado -->
        <div class="workshop-info">
          <div class="workshop-title"><strong>Datos de taller</strong></div>
          ${order.workshopId && (order.workshopName || packageLabelInfo.workshopName) ? `
          <div class="package-row">
            <div><strong>Nombre:</strong> ${order.workshopName || packageLabelInfo.workshopName || this.getWorkshopDataById(order.workshopId).name}</div>
            <div><strong>Tel:</strong> ${order.workshopPhone || packageLabelInfo.workshopPhone || this.getWorkshopDataById(order.workshopId).phone}</div>
          </div>
          <div class="package-row">
            <div><strong>Dirección:</strong> ${order.workshopAddress || packageLabelInfo.workshopAddress || this.getWorkshopDataById(order.workshopId).address}</div>
          </div>
          <div class="package-row">
            <div><strong>Ciudad:</strong> ${order.workshopCity || packageLabelInfo.workshopCity || this.getWorkshopDataById(order.workshopId).city}</div>
          </div>
          <div class="package-row">
            <div><strong>Departamento:</strong> ${order.workshopDepartment || packageLabelInfo.workshopDepartment || this.getWorkshopDataById(order.workshopId).department}</div>
          </div>
          ` : `
          <div class="package-row">
            <div><strong>Sin taller asignado</strong></div>
            <div><strong>Fecha:</strong> ${new Date().toLocaleDateString('es-ES')}</div>
          </div>
          `}
        </div>

        <div class="barcode-container">
          ${this.generateBarcodeSvg(packageLabelInfo.packageNumber)}
        </div>

        <div class="recipient-info">
          <div class="client-header">
            <img class="client-logo" src="${logoSrc}" onerror="this.style.display='none'" alt="Logo Cliente" />
            <span class="client-company">${order?.clienteUY || 'AUTOCOMERCIAL'}</span>
          </div>
          <div><strong>Nombre del cliente:</strong> ${order.clientName || 'N/A'}</div>
        </div>
      </div>
    `;
  }


  /**
   * Generar código de barras SVG
   */
  private generateBarcodeSvg(packageCode: string): string {
    try {
      // Crear un elemento canvas temporal
      const canvas = document.createElement('canvas');
      canvas.width = 200;
      canvas.height = 60;

      // Generar el código de barras
      if (typeof (window as any).JsBarcode !== 'undefined') {
        (window as any).JsBarcode(canvas, packageCode, {
          format: 'CODE128',
          width: 2,
          height: 40,
          displayValue: true,
          fontSize: 12,
          margin: 5
        });

        // Convertir canvas a imagen
        const ctx = canvas.getContext('2d');
        if (ctx) {
          const svgData = canvas.toDataURL('image/png');
          return `<img src="${svgData}" class="barcode-image" alt="Código: ${packageCode}" style="max-width: 100%; max-height: 40px;" />`;
        }
      }

      // Fallback si JsBarcode no está disponible
      return `<div class="barcode-fallback" style="font-family: monospace; font-size: 12px; text-align: center; padding: 10px; border: 1px solid #ccc;">${packageCode}</div>`;
    } catch (error) {
      console.error('Error generando código de barras:', error);
      return `<div class="barcode-fallback">${packageCode}</div>`;
    }
  }

  /**
   * Obtener estilos CSS para impresión
   */
  private getPrintStyles(): string {
    return `
      body {
        margin: 0;
        padding: 20px;
        font-family: Arial, sans-serif;
      }
      .print-container {
        display: flex;
        justify-content: center;
        align-items: flex-start;
        min-height: 100vh;
      }
      .label {
        width: 7cm;
        height: 5cm;
        display: flex;
        flex-direction: column;
        background-color: white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        box-sizing: border-box;
        overflow: hidden;
        margin: 10px;
      }
      .label-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 2px;
        height: 55px;
      }
      .label-logo {
        max-height: 50px;
        max-width: 160px;
        object-fit: contain;
      }
      .contact-info {
        font-size: 8px;
        text-align: right;
        line-height: 1.2;
        position: relative;
      }
      .provider-phone {
        position: absolute !important;
        right: -50px !important;
        top: 85% !important;
        transform: translateY(-50%) !important;
        font-size: 8px !important;
        margin: 0 !important;
        padding: 0 !important;
        display: flex !important;
        align-items: center !important;
        white-space: nowrap !important;
      }
      .package-label {
        background-color: #f8f9fa;
        border: 3px solid #000;
      }
      .package-title {
        font-size: 11px;
        font-weight: bold;
        text-align: center;
        margin: 2px 0;
        color: #000;
        background-color: #e9ecef;
        padding: 2px;
        border-radius: 2px;
      }
      .package-main-info {
        font-size: 8px;
        line-height: 1.1;
        margin: 3px 0;
      }
      .package-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 2px 8px;
        margin-bottom: 2px;
      }
      .workshop-info {
        font-size: 8px;
        line-height: 1.1;
        margin: 3px 0;
      }
      .workshop-title {
        font-size: 9px;
        font-weight: bold;
        color: #2563eb;
        text-align: left;
        margin-bottom: 4px;
      }
      .barcode-container {
        text-align: center;
        margin: 5px 0;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .barcode {
        max-width: 100%;
        max-height: 35px;
      }
      .recipient-info {
        font-size: 8px;
        line-height: 1.1;
        margin: 3px 0;
      }
      .recipient-info div {
        margin-bottom: 1px;
      }
      .client-header {
        display: flex;
        align-items: center;
        gap: 3px;
        margin-bottom: 2px;
        padding-bottom: 2px;
        border-bottom: 1px solid #ddd;
      }
      .client-logo {
        max-height: 20px;
        max-width: 60px;
        object-fit: contain;
        margin-right: 5px;
      }
      .client-company {
        font-weight: bold;
        font-size: 8px;
        color: #333;
        text-transform: uppercase;
      }
      @media print {
        body {
          margin: 0;
          padding: 0;
        }
        .print-container {
          margin: 0;
          padding: 0;
          min-height: auto;
        }
        .label {
          margin: 0;
          box-shadow: none;
          page-break-inside: avoid;
        }
      }
    `;
  }

  /**
   * Determina si un paquete puede ser eliminado basado en su estado
   */
  canDeletePackage(packageStatus: string): boolean {
    // Solo se pueden eliminar paquetes con estado 'active'
    return packageStatus === 'active';
  }

  /**
   * Determina si un repuesto puede ser eliminado de un paquete basado en su estado
   */
  canDeleteSparePart(itemStatus: string): boolean {
    // Solo se pueden eliminar repuestos con estado 'active'
    return itemStatus === 'active';
  }

  /**
   * Mapea el ID del taller a los datos correctos del taller
   */
  private getWorkshopDataById(workshopId: number | null | undefined): { name: string; address: string; phone: string; city: string; department: string } {
    if (!workshopId) {
      return {
        name: 'Sin taller asignado',
        address: '',
        phone: '',
        city: '',
        department: ''
      };
    }

    const workshop = this.workshops.find(w => w.workshopId === workshopId);

    if (workshop) {
      const result = {
        name: workshop.name,
        address: workshop.address || 'Dirección no disponible',
        phone: workshop.phoneNumber || 'Teléfono no disponible',
        city: workshop.city || 'Ciudad no disponible',
        department: workshop.department || 'Departamento no disponible'
      };
      return result;
    }

    // Fallback a mapeo hardcodeado si no se encuentra en la lista cargada
    const workshopMap: { [key: number]: { name: string; address: string; phone: string; city: string; department: string } } = {
      1: {
        name: 'Taller Central',
        address: 'Av. 18 de Julio 1234',
        phone: '+598 2901 2345',
        city: 'Montevideo',
        department: 'Montevideo'
      },
      2: {
        name: 'AutoService Norte',
        address: 'Ruta 8 Km 12',
        phone: '+598 2602 3456',
        city: 'Las Piedras',
        department: 'Canelones'
      },
      3: {
        name: 'Mecanica Especializada',
        address: 'Bulevar Artigas 567',
        phone: '+598 2400 4567',
        city: 'Montevideo',
        department: 'Montevideo'
      },
      4: {
        name: 'Taller Express',
        address: 'Av. Italia 890',
        phone: '+598 2487 5678',
        city: 'Montevideo',
        department: 'Montevideo'
      },
      5: {
        name: 'Garage Premium',
        address: 'Constituyente 1234',
        phone: '+598 2203 6789',
        city: 'Montevideo',
        department: 'Montevideo'
      }
    };

    const result = workshopMap[workshopId] || {
      name: `Taller ID: ${workshopId}`,
      address: 'Dirección no disponible',
      phone: 'Teléfono no disponible',
      city: 'Ciudad no disponible',
      department: 'Departamento no disponible'
    };
    return result;
  }

  getPrinterInfoText(): string {
    if (this.printerType === 'thermal') {
      const dimensions = this.getDimensionsText(this.thermalPrinterWidth);
      return `Ticketera térmica ${dimensions} (optimizada)`;
    } else {
      return 'Formato A4 estándar';
    }
  }

  getReprintPrinterInfoText(): string {
    if (this.reprintPrinterType === 'thermal') {
      const dimensions = this.getDimensionsText(this.reprintThermalPrinterWidth);
      return `Ticketera térmica ${dimensions} (optimizada)`;
    } else {
      return 'Formato A4 estándar';
    }
  }

  private getDimensionsText(width: string): string {
    switch (width) {
      case '58mm': return '58mm x 100mm';
      case '80mm': return '80mm x 100mm';
      case '100mm': return '100mm x 150mm';
      default: return width;
    }
  }

  /**
   * Volver atrás a un estado anterior si el paquete fue enviado
   */
  /**
   * Verifica si el paquete seleccionado tiene repuestos ENVIADOS
   * Nota: PackageListItemOutModel no tiene shippingStatus, necesitamos verificar por remitoNumber
   * o usar el tipo de lista para determinar si está enviado
   */
  hasEnviadoSpareParts(): boolean {
    if (!this.selectedPackageForView?.items || this.selectedPackageForView.items.length === 0) {
      return false;
    }
    // Si la lista es de tipo "enviado", entonces los repuestos están enviados
    if (this.selectedPackageForView.type === 'enviado') {
      return true;
    }
    // También verificar si hay remitoNumber, que indica que fue enviado
    return this.selectedPackageForView.items.some(item => item.remitoNumber && item.remitoNumber.trim() !== '');
  }

  /**
   * Volver atrás a un estado anterior si el paquete fue enviado
   * Permite revertir manualmente el estado de ENVIADO a NO-ENVIADO
   */
  revertPackageToPreviousState(packageNumber: string): void {
    if (!packageNumber) {
      this.showAlert('Error', 'Número de paquete no válido', 'error');
      return;
    }

    if (!confirm(`¿Está seguro de que desea volver atrás el paquete ${packageNumber} a su estado anterior?\n\nEsto cambiará el estado de ENVIADO a NO-ENVIADO y limpiará el remito.`)) {
      return;
    }

    // Mostrar indicador de carga
    this.isScanning = true;

    // Obtener los repuestos del paquete
    this.packageListsService.getSparePartsByPackage(packageNumber).subscribe({
      next: async (spareParts) => {
        if (spareParts.length === 0) {
          this.isScanning = false;
          this.showAlert('Error', 'No se encontraron repuestos para el paquete', 'error');
          return;
        }

        let successCount = 0;
        let errorCount = 0;
        const errors: string[] = [];

        // Revertir cada repuesto a su estado anterior
        for (const sparePart of spareParts as any[]) {
          try {
            // Si está ENVIADO, volver a NO-ENVIADO
            if (sparePart.shippingStatus === 'ENVIADO') {
              // Actualizar ShippingStatus a NO-ENVIADO
              await this.sparePartService.updateSparePartShippingStatus(sparePart.sparePartId, 'NO-ENVIADO').toPromise();

              // Limpiar el remito
              await this.sparePartService.updateSparePartShippingReceipt(sparePart.sparePartId, '').toPromise();
              successCount++;
            } else {
            }
          } catch (error: any) {
            console.error(`❌ Error revirtiendo repuesto ${sparePart.sparePartId}:`, error);
            errorCount++;
            const errorMsg = error?.error?.message || error?.message || 'Error desconocido';
            errors.push(`Repuesto ${sparePart.sparePartId}: ${errorMsg}`);
          }
        }

        this.isScanning = false;

        // Mostrar resultado
        if (successCount > 0) {
          let message = `✅ ${successCount} repuesto${successCount > 1 ? 's' : ''} revertido${successCount > 1 ? 's' : ''} correctamente`;
          if (errorCount > 0) {
            message += `\n\n⚠️ ${errorCount} repuesto${errorCount > 1 ? 's' : ''} con error${errorCount > 1 ? 'es' : ''}:`;
            message += `\n${errors.join('\n')}`;
          }
          this.showAlert('Éxito', message, 'success');

          // Recargar datos
          this.syncAllViews();

          // Cerrar modal si está abierto
          if (this.showPackageDetailsModal) {
            this.closePackageDetailsModal();
          }

          // Recargar la vista actual
          if (this.currentView === 'enviado') {
            this.loadUniquePackages('enviado');
          } else if (this.currentView === 'cargado') {
            this.loadUniquePackages('cargado');
          }
        } else {
          let errorMessage = 'No se pudo revertir ningún repuesto';
          if (errorCount > 0) {
            errorMessage += `:\n${errors.join('\n')}`;
          } else {
            errorMessage += '. No hay repuestos en estado ENVIADO para revertir.';
          }
          this.showAlert('Error', errorMessage, 'error');
        }
      },
      error: (error) => {
        this.isScanning = false;
        console.error('❌ Error obteniendo repuestos del paquete:', error);
        const errorMsg = error?.error?.message || error?.message || 'Error desconocido';
        this.showAlert('Error', `Error al obtener los repuestos del paquete: ${errorMsg}`, 'error');
      }
    });
  }
}
